import { KERB_LAT, LAT_CENTER, MAX_PLAYERS, SCREEN_H, SCREEN_W, SPEED_QUANT, TICK_FRAMES, UNIT_PER_TICK_FACTOR } from "../shared/constants";
import { TRACK_PACK } from "../shared/trackpack";
import { SURFACE_KINDS, type SurfaceKind, type SurfaceSeg } from "../shared/track";
import { BUILTIN_COUNT, POINTS, SEASON, TRACKS, TrackDef, normalizeTrack, registerTrack, setSeason, validateTrack } from "../shared/track";
import { DemoDriver } from "./demo";
import { Car, MACHINES, NO_INPUT, Race, formatTime, SKILLS, VCLASSES, vclassOf, PHYS_REV, physOf } from "../shared/physics";
import { CHAT_DEFAULTS, CHAT_MSGS, PlayerInfo, ServerMsg, STATE_HZ } from "../shared/protocol";
import { MIN_RANKED_SKILL, RANKED_AI, dailyOf, inputOf, maskOf, rle, utcDay } from "../shared/replay";
import { minimapPoints } from "../shared/track";
import { WireCar } from "../shared/protocol";
import { drawThemeEffect, drawThemeLogo, resolveTheme } from "./seasons";
import { TEAM_COLORS } from "../shared/teamcolors";
import { PAL, accents, cardSky, cardTint, drawCarSprite, renderCountdown, renderIntroBanner, renderRace, skidTrace, spriteWidth, applySprites } from "./render";
import type { SpriteSpec } from "../shared/sprites";
import { drawText, drawTextC, textWidth, tr } from "./font";
import { Sfx } from "./audio";
import { PODIUM_FRAMES, drawPodium } from "./podium";
import { bindInput, capturePadButton, clearZones, padCapturing, padConnected, padInfo, pollGamepad, readInput, resetSource, rumble, runSource, sampleSource } from "./input";
import { Net } from "./net";
import { PALETTES, BTN_SIZES, BTN_ALPHAS, SPEEDO_MODES, setBtnSize, setBtnAlpha, setSpeedo, setSkids, RUMBLE_LEVELS, colorsActive, setColors, TOUCH_MODES, VOLUME_STEPS, applyPalette, options, padButtonName, padMap, setPadButton, setPalette, setRumble, setTouch, setVolume, type VolumeKind } from "./options";
import { VERSION } from "../shared/buildinfo";
import { currentLang, initLang, setLang } from "./i18n";
import { initInAppBanner } from "./inapp";
import { SERVER, api } from "./serverUrl";
import { BADGES } from "../shared/badges";
import { formatPilotKey, normPilotKey, normPilotName, pilotNameError } from "../shared/pilot";

type Screen = "title" | "licence" | "options" | "spectate" | "records" | "name" | "room" | "select" | "lobby" | "race" | "result" | "standings" | "challenge" | "circuit" | "popular" | "solo" | "qual" | "team" | "teaminvite" | "teamcode" | "stream" | "streamrace" | "streamend" | "review" | "padmap";
const canvas = document.getElementById("screen") as HTMLCanvasElement;
canvas.width = SCREEN_W;
canvas.height = SCREEN_H;
const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
const sfx = new Sfx();
(window as any).__sfx = sfx;

let screen: Screen = "title";
let menuIdx = 6; // DAILY CHALLENGE is first in the menu and selected by default
/** Logical menu ids: 0 season, 1 quick race, 2 online, 3 driver, 4 records, 5 time trial, 6 daily, 7 community Discord (only when the server has an invite link). Shown in this order. */
// 8 = options, 9 = weekly challenge (once the server has told us this week's)
// home menu: the featured modes first, the solo modes behind SOLO (11), the Discord link is in OPTIONS
const menuOrder = () => [6, ...(weeklyInfo ? [9] : []), ...(qualInfo ? [12] : []), 2, ...(popularOn ? [10] : []), 4, 13, 11, 3, 8];
const SOLO_IDS = [0, 1, 5]; // season, quick race, time trial (a fourth row, RALLY, plays the stages)
const soloRows = () => (stageList().length ? 4 : 3);
let soloRow = 0;
/** Back on the home menu the cursor must sit on a line that exists (a solo mode stands for SOLO). */
const fixMenuIdx = () => {
  if (!menuOrder().includes(menuIdx)) menuIdx = menuIdx === 5 && activeQual && qualInfo ? 12 : SOLO_IDS.includes(menuIdx) ? 11 : 6; // a tournament time trial stands for TOURNAMENT, not SOLO
};
interface WeeklyInfo { week: string; track: number; seed: number; skill: number; vclass: number; ai: number; nojet: boolean; machine: number; title: string; trackName: string; endsAt: number }
interface QualInfo { id: string; name: string; phase: "upcoming" | "open" | "closed"; track: number; trackName: string; machine: number; openAt: number; closeAt: number; size: number; entries: number }
let qualInfo: QualInfo | null = null; // the tournament's qualifier (open, coming, or closed this week), from the server
let freeChatOn = false; // the admin allows free-text chat in the lobby
let macroWarnOn = false; // the admin switched on the "macro detected" message
/** Physics revision of the races started now: the admin can switch off the lapped-cars behaviour (revisions 2 and 3: they stay visible and move aside) and go back to revision 1. */
let lappedCarsOn = true;
const livePhys = () => (lappedCarsOn ? PHYS_REV : 1);
let popularOn = false; // the admin switched on the list of popular player circuits
let weeklyInfo: WeeklyInfo | null = null; // this week's challenge, from the server
let selRow = 1; // select screen cursor: 0 level, 1 machine, 2 circuit (quick race)
let skillIdx = (() => {
  try {
    const v = Number(localStorage.getItem("f1skill") ?? 1);
    return v >= 0 && v < SKILLS.length ? v : 1;
  } catch {
    return 1;
  }
})();
let raceSkill = 1; // level of the race being driven (fixed for a whole season)
let trackIdx = 0;
/** Admin editor "test" link (?testtrack=<base64url json>): plays a circuit that is not published yet; nothing is submitted. */
let testDef: TrackDef | null = null;
try {
  const raw = new URLSearchParams(location.search).get("testtrack");
  if (raw) {
    const d = JSON.parse(decodeURIComponent(escape(atob(raw.replace(/-/g, "+").replace(/_/g, "/")))));
    if (!validateTrack(d)) testDef = { ...(normalizeTrack(d) as TrackDef), id: 99 };
  }
} catch {
  /* bad link: ignore */
}
/** Prototype link (?surfacetest=1, or snow / ice / water / gravel): the LAPONIE stage with road surfaces, solo and unranked. */
const surfaceTest = new URLSearchParams(location.search).get("surfacetest");
if (surfaceTest) {
  const base = TRACK_PACK.find((t) => t.name === "LAPONIE");
  if (base) {
    const kind = (SURFACE_KINDS as readonly string[]).includes(surfaceTest) ? (surfaceTest as SurfaceKind) : null;
    const surfaces: SurfaceSeg[] = kind
      ? [{ s0: 0.12, s1: 0.3, kind }, { s0: 0.42, s1: 0.6, kind }, { s0: 0.72, s1: 0.9, kind }]
      : [{ s0: 0.1, s1: 0.22, kind: "snow" }, { s0: 0.28, s1: 0.36, kind: "gravel" }, { s0: 0.46, s1: 0.56, kind: "ice" }, { s0: 0.64, s1: 0.74, kind: "water" }, { s0: 0.8, s1: 0.9, kind: "snow" }];
    testDef = { ...(normalizeTrack({ ...base, surfaces, id: 0 } as TrackDef, true) as TrackDef), id: 99 };
  }
}
/** Admin test link (?testvclass=1..3&track=N&machine=0|1): solo race on any circuit with a non-Formula vehicle class; nothing is submitted. */
const testParams = new URLSearchParams(location.search);
const testVClass = VCLASSES[Number(testParams.get("testvclass"))] && Number(testParams.get("testvclass")) > 0 ? Number(testParams.get("testvclass")) : surfaceTest ? 1 : 0;
let round = 0;
let seasonPts: number[] = [];
let onlineStandings: { id: number; pts: number }[] = [];
let lobbyTourn: { name: string; rounds: number } | null = null; // the room is playing a tournament
let resultTourn: { name: string; round: number; rounds: number } | null = null;
let tournFinal: { title: string; top: { name: string; time: string; pts: number; team?: string }[] } | null = null; // the tournament's own podium, after the last round
let showFinal = false;
let lobbyRound = 0;
let rematchIds: number[] = []; // players who voted to replay the last circuit
let rematchTrack = -1; // that circuit (-1: nothing raced yet in this room)
let friendlyResult = false; // the race just finished was a rematch: no points
let lobbyTrack = 0;
let lobbyPick = -1; // circuit chosen by the host (-1: season order)
let lobbyHost = -1;
let resultTrack = 0;
let machine = 0;
let online = false;
let race: Race | null = null;
let myId = 0;
let net: Net | null = null;
let players: PlayerInfo[] = [];
let ready = false;
initLang(); // page texts: French or English (device language, or the saved choice)
initInAppBanner(); // inside Facebook / Messenger: how to open the real browser
let netErr = "";
// ---- maintenance mode (set from the admin panel): a scrolling bar on the title screen, a pop-up box elsewhere ----
let maint = { on: false, message: "" };
let maintToastAt = -1e9; // performance.now() of the last pop-up trigger
const MAINT_TOAST_MS = 9000;
function setMaint(on: boolean, message: string, toast: boolean) {
  const changed = on !== maint.on || message !== maint.message;
  maint = { on, message };
  if (on && (toast || changed)) maintToastAt = performance.now();
}
/** Greedy word wrap for the 4px-per-char font. */
function wrapText(s: string, max: number): string[] {
  const out: string[] = [];
  let line = "";
  for (const w of s.split(" ")) {
    if (w.length > max) {
      if (line) out.push(line), (line = "");
      for (let i = 0; i < w.length; i += max) out.push(w.slice(i, i + max));
    } else if (line && line.length + 1 + w.length > max) out.push(line), (line = w);
    else line = line ? line + " " + w : w;
  }
  if (line) out.push(line);
  return out;
}
function drawMaintToast() {
  if (!maint.on || performance.now() - maintToastAt > MAINT_TOAST_MS) return;
  const lines = wrapText(maint.message, 33).slice(0, 7);
  const h = 13 + lines.length * 8 + 4;
  const y0 = Math.round((SCREEN_H - h) / 2) - 6;
  ctx.fillStyle = PAL[3];
  ctx.fillRect(8, y0 - 1, 144, h + 2);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(10, y0 + 1, 140, h - 2);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(10, y0 + 1, 140, 9);
  drawTextC(ctx, "MAINTENANCE", 80, y0 + 3, PAL[0]);
  lines.forEach((l, i) => drawTextC(ctx, l, 80, y0 + 14 + i * 8, PAL[3]));
}
let resultOrder: { id: number; frame: number }[] = [];
let tick = 0;
let lastSend = 0;
const cleanRoom = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
const urlRoom = cleanRoom(new URLSearchParams(location.search).get("room") ?? "");
// ?stream=ROOM[&follow=NAME]: a clean broadcast view for OBS (watches the room, never takes a seat); the camera follows the leader
const streamParam = new URLSearchParams(location.search).get("stream");
const streamMode = streamParam !== null;
const streamToken = streamMode ? (new URLSearchParams(location.search).get("token") ?? "") : ""; // admin token: watches without a pilot licence
if (streamToken) history.replaceState(null, "", location.pathname + "?stream=" + encodeURIComponent(streamParam ?? "")); // the token does not stay in the address bar
const streamFollow = (new URLSearchParams(location.search).get("follow") ?? "").toUpperCase().slice(0, 8);
// admin: replay of a stored run (?review=BOARD&pilot=NAME&token=ADMIN_TOKEN)
const reviewParam = new URLSearchParams(location.search).get("review");
const reviewMode = reviewParam !== null;
const reviewPilot = reviewMode ? (new URLSearchParams(location.search).get("pilot") ?? "") : "";
const reviewToken = reviewMode ? (new URLSearchParams(location.search).get("token") ?? "") : "";
if (reviewMode) history.replaceState(null, "", location.pathname); // the token does not stay in the address bar
let room = (streamParam ? cleanRoom(streamParam) : "") || urlRoom || "PUBLIC";
const newCode = () => {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no look-alikes (I, O, 0, 1)
  let c = "";
  for (let i = 0; i < 4; i++) c += a[Math.floor(Math.random() * a.length)];
  return c;
};
let roomRow = 0; // 0 public, 1 new private room, 2 join with a code
let roomCode = newCode();
let joinDraft = "";
let copied = 0; // frames left of the "LINK COPIED" notice
// install as an app: Chrome/Edge/Android hand us a prompt to fire from a button; iOS Safari has none, so the button explains the gesture
let installEvent: { prompt(): Promise<unknown> } | null = null;
let installHelp = 0; // frames left of the iOS explanation
const isStandalone = () => matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
const isIosSafari = () => /iP(hone|ad|od)/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
const installAvail = () => !isStandalone() && (installEvent !== null || isIosSafari());
addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  installEvent = e as unknown as { prompt(): Promise<unknown> };
});
addEventListener("appinstalled", () => (installEvent = null));
// a one-time hint in the menu ticker (until the player has seen it for a while, tried the button, or installed the game)
let installTipFrames = 0;
const tipSeen = () => {
  try {
    return localStorage.getItem("f1installtip") === "1";
  } catch {
    return false;
  }
};
const tipDone = () => {
  try {
    localStorage.setItem("f1installtip", "1");
  } catch {
    /* ignore */
  }
};
addEventListener("appinstalled", tipDone);
function installApp() {
  tipDone();
  if (installEvent) {
    const ev = installEvent;
    installEvent = null; // a prompt can be used once; the browser offers a new one if the player declines
    void ev.prompt();
  } else if (isIosSafari()) installHelp = 330;
}
// itch.io build: the game runs in an iframe whose address is useless to a friend, so the link opens the full game on the server
const inviteLink = () => (SERVER ? SERVER + "/" : location.origin + (location.pathname.startsWith("/c/") ? "/" : location.pathname)) + "?room=" + room;
async function shareInvite() {
  const url = inviteLink();
  try {
    if (navigator.share && matchMedia("(pointer:coarse)").matches) await navigator.share({ title: "Formule Race", text: "Join my Formule Race room " + room, url });
    else await navigator.clipboard.writeText(url);
    copied = 120;
  } catch {
    /* cancelled or unavailable: the code on screen can still be read out */
  }
}
const cleanName = (v: string) => v.toUpperCase().replace(/[^A-Z0-9 \-]/g, "").slice(0, 8);
/** The driver name is kept in localStorage, with a cookie as a second copy for browsers that wipe or block site storage. */
function saveName(n: string) {
  try {
    localStorage.setItem("f1name", n);
  } catch {
    /* storage unavailable */
  }
  try {
    document.cookie = "f1name=" + encodeURIComponent(n) + "; max-age=31536000; path=/; SameSite=Lax";
  } catch {
    /* cookies unavailable */
  }
}
function loadName(): string | null {
  try {
    const v = localStorage.getItem("f1name");
    if (v) return v;
  } catch {
    /* storage unavailable */
  }
  const m = /(?:^|; )f1name=([^;]*)/.exec(document.cookie || "");
  return m ? decodeURIComponent(m[1]) : null;
}
let playerName = (() => {
  let n = new URLSearchParams(location.search).get("name");
  if (n) saveName(cleanName(n));
  else n = loadName();
  return cleanName(n ?? "");
})();

// ---- pilot licence: the name belongs to a secret code made by the game and kept in this browser (no email, no password) ----
function readStore(k: string): string | null {
  try {
    const v = localStorage.getItem(k);
    if (v) return v;
  } catch {
    /* storage unavailable */
  }
  const m = new RegExp("(?:^|; )" + k + "=([^;]*)").exec(document.cookie || "");
  return m ? decodeURIComponent(m[1]) : null;
}
function writeStore(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage unavailable */
  }
  try {
    document.cookie = k + "=" + encodeURIComponent(v) + "; max-age=31536000; path=/; SameSite=Lax";
  } catch {
    /* cookies unavailable */
  }
}
let pilotKey = normPilotKey(readStore("f1key") ?? "");
if (pilotKey.length !== 32) pilotKey = "";
let pilotName: string | null = pilotKey ? readStore("f1reg") : null; // the registered name (confirmed by the server at start-up)
let pilotChecked = false; // the server has answered about this licence
function ensureKey() {
  if (!pilotKey) {
    pilotKey = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    writeStore("f1key", pilotKey);
  }
  return pilotKey;
}
function setPilot(name: string | null) {
  pilotName = name;
  writeStore("f1reg", name ?? "");
  if (name) (playerName = name), saveName(name);
}
const nameOf = () => pilotName || playerName.trim() || "PLAYER";
async function postJson(path: string, body: unknown): Promise<any> {
  const r = await fetch(api(path), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  return r.json();
}
// someone took my daily record: the server tells the game (at start-up, every two minutes, and as soon as the page is looked at again).
// The server keeps the notice until the pop-up has really been on screen (9 s with the page visible and focused), so it is never lost.
type Notice = { id?: number; by: string; frames: number; track: string; weekly?: boolean };
let notice: Notice | null = null;
let shownMs = 0; // time the pop-up has been on screen
let lastShown = performance.now();
let ackId = -1; // the notice to confirm to the server (sent with the next check)
let doneId = -1; // last notice already shown: not shown twice
const NOTICE_MS = 9000;
function gotNotice(n: Notice | null | undefined) {
  if (!n || (n.id !== undefined && (n.id === doneId || n.id === notice?.id))) return;
  notice = n;
  shownMs = 0;
  lastShown = performance.now();
  noticeAt = -1;
}
let noticeAt = -1; // performance.now() when the pop-up first showed
let lastInputAt = 0;
for (const ev of ["pointerdown", "keydown", "touchstart"]) addEventListener(ev, () => (lastInputAt = performance.now()), { passive: true });
let lastCheck = -1e9;
const checkNotice = () => {
  if (!pilotName || document.visibilityState !== "visible" || screen === "race" || performance.now() - lastCheck < 15_000) return;
  lastCheck = performance.now();
  const ack = ackId;
  postJson("/api/pilot/me", { key: pilotKey, keep: 1, ack: ack >= 0 ? ack : undefined }).then((d) => {
    if (ack >= 0 && ackId === ack) ackId = -1;
    if (d.ok) gotNotice(d.notice);
  }, () => {});
};
setInterval(checkNotice, 120_000);
document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && setTimeout(checkNotice, 500));
window.addEventListener("focus", () => setTimeout(checkNotice, 500));
// ---- badge unlocked: the server computes the badges from the pilot's numbers, so the game compares the list with the one it saw last time ----
let badgeToasts: { text: string; sub: string }[] = [];
let badgeToastAt = -1e9;
let badgeDirty = false; // a race was played since the last check
let badgeChecked = -1e9;
async function checkBadges() {
  if (!pilotKey || !pilotName) return;
  badgeChecked = performance.now();
  badgeDirty = false;
  try {
    const d = await postJson("/api/pilot/stats", { key: pilotKey });
    if (!d.ok || !Array.isArray(d.badges)) return;
    const k = "f1badges:" + pilotName;
    let seen: string[] | null = null;
    try {
      const raw = localStorage.getItem(k);
      seen = raw ? JSON.parse(raw) : null;
    } catch {
      /* storage unavailable: nothing is announced */
    }
    if (seen) for (const id of d.badges as string[]) if (!seen.includes(id)) {
      const b = BADGES.find((x) => x.id === id);
      if (b) badgeToasts.push({ text: b.en, sub: "LICENCE > BADGE" });
    }
    try {
      localStorage.setItem(k, JSON.stringify(d.badges)); // a first visit only records the list: nothing is announced for badges earned before this feature
    } catch {
      /* ignore */
    }
  } catch {
    /* offline */
  }
}
function drawBadgeToast() {
  if (badgeToasts.length === 0) return;
  const now = performance.now();
  if (badgeToastAt < 0) (badgeToastAt = now), sfx.jingle();
  if (now - badgeToastAt > 5000) {
    badgeToasts.shift();
    badgeToastAt = -1;
    return;
  }
  const t = badgeToasts[0];
  const slide = Math.min(1, (now - badgeToastAt) / 250);
  const y0 = Math.round(-26 + slide * 28);
  const blink = Math.floor((now - badgeToastAt) / 250) % 2 === 0 && now - badgeToastAt < 2000;
  ctx.fillStyle = PAL[3];
  ctx.fillRect(10, y0, 140, 24);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(12, y0 + 2, 136, 20);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(12, y0 + 2, 136, 9);
  drawTextC(ctx, "BADGE UNLOCKED!", 80, y0 + 4, PAL[0]);
  if (blink) {
    ctx.fillStyle = PAL[0];
    ctx.fillRect(14, y0 + 4, 3, 3);
    ctx.fillRect(143, y0 + 4, 3, 3);
  }
  drawTextC(ctx, t.text, 80, y0 + 13, PAL[3]);
  void t.sub;
}
function drawNoticeToast() {
  if (!notice) return;
  const now = performance.now();
  if (noticeAt < 0) (noticeAt = now), sfx.jingle();
  if (document.visibilityState === "visible" && (document.hasFocus() || now - lastInputAt < 30_000)) shownMs += Math.min(100, now - lastShown); // only time spent looking counts
  lastShown = now;
  if (shownMs > NOTICE_MS) {
    if (notice.id !== undefined) (doneId = ackId = notice.id), checkNotice();
    return void (notice = null);
  }
  const lines = [...wrapText(`${notice.by} BEAT YOUR ${notice.weekly ? "WEEKLY" : "DAILY"} RECORD`, 30), formatTime(notice.frames), notice.track.slice(0, 30)];
  const h = 13 + lines.length * 8 + 4;
  const y0 = 18;
  ctx.fillStyle = PAL[3];
  ctx.fillRect(8, y0 - 1, 144, h + 2);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(10, y0 + 1, 140, h - 2);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(10, y0 + 1, 140, 9);
  drawTextC(ctx, "RECORD BEATEN!", 80, y0 + 3, PAL[0]);
  lines.forEach((l, i) => drawTextC(ctx, l, 80, y0 + 14 + i * 8, i === lines.length - 2 ? PAL[3] : PAL[2]));
}
async function checkPilot() {
  try {
    const d = await postJson("/api/pilot/me", { key: pilotKey, keep: 1 });
    pilotChecked = true;
    gotNotice(d.notice);
    if (d.ok) (setPilot(d.name), (pilotEmail = d.email ?? ""), void checkBadges());
    else if (pilotName) setPilot(null); // released by the admin: pick a new name
    if (!pilotName && screen === "title" && !urlChallenge && !urlShared) startClaim(); // a friend's challenge link plays first, the name comes later
  } catch {
    /* offline: the saved name keeps working for solo play */
  }
}
let emailOn = false; // the server can send sign-in codes by email
let pilotEmail = ""; // masked address linked to this licence
let nameMode: "name" | "code" | "mail" | "link" = "name"; // choose a name / restore a licence with its code / sign in by email / link an email to this licence
let mailStep = false; // email modes: false = typing the address, true = typing the 6 digit code
let mailAddr = "";
let nameStage: "type" | "confirm" | "busy" = "type";
let nameMsg = "";
let licCopied = 0;
function startClaim(msg = "") {
  nameMode = "name";
  mailStep = false;
  nameStage = "type";
  nameMsg = msg;
  nameDraft = pilotNameError(playerName) ? "" : cleanName(playerName); // a name used before is offered again
  nameField.value = nameDraft;
  nameField.maxLength = 8;
  screen = "name";
  nameField.focus({ preventScroll: true });
}
function setNameMode(m: typeof nameMode) {
  nameMode = m;
  fieldOverlay = false;
  mailStep = false;
  nameMsg = "";
  nameDraft = "";
  nameField.value = "";
  nameField.maxLength = m === "code" ? 40 : m === "name" ? 8 : 60;
  nameField.focus({ preventScroll: true });
}
function toggleNameMode() {
  if (nameStage !== "type" || nameMode === "link") return;
  setNameMode(nameMode === "name" ? "code" : nameMode === "code" && emailOn ? "mail" : "name");
}
/** Licence page: link an email so the licence can be restored on another device with a code sent there. */
function startLink() {
  if (!emailOn || !pilotName) return;
  nameStage = "type";
  screen = "name";
  setNameMode("link");
}
const cleanMail = (v: string) => v.toLowerCase().replace(/[^a-z0-9._%+@-]/g, "").slice(0, 60);
async function mailSend() {
  const email = cleanMail(nameDraft);
  if (!/^[a-z0-9._%+-]{1,64}@[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(email)) return void (nameMsg = "NOT AN EMAIL");
  nameStage = "busy";
  try {
    const d = await postJson(nameMode === "link" ? "/api/pilot/email/send" : "/api/pilot/login/send", { key: pilotKey, email, next: readStore("f1invite") || undefined });
    if (d.ok) {
      mailAddr = email;
      mailStep = true;
      nameDraft = "";
      nameField.value = "";
      nameField.maxLength = 6;
      nameMsg = "";
    } else nameMsg = d.error === "used" ? "EMAIL USED BY ANOTHER" : d.error === "wait" ? "TRY AGAIN LATER" : d.error === "off" ? "NOT AVAILABLE" : "ERROR";
  } catch {
    nameMsg = "NO SERVER";
  }
  nameStage = "type";
}
async function mailVerify() {
  if (nameDraft.length !== 6) return void (nameMsg = "6 DIGITS");
  nameStage = "busy";
  try {
    const link = nameMode === "link";
    const key = link ? pilotKey : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join(""); // a new device gets a code of its own
    const d = await postJson(link ? "/api/pilot/email/verify" : "/api/pilot/login/verify", { key, email: mailAddr, code: nameDraft });
    if (d.ok) {
      pilotEmail = d.email ?? "";
      if (!link) {
        pilotKey = key;
        writeStore("f1key", key);
        setPilot(d.name);
      }
      nameField.blur();
      if (link) (openLicence(), (licCopied = 0), (mailMsgAt = 180));
      else afterSignIn();
    } else nameMsg = d.error === "bad code" ? "WRONG OR EXPIRED CODE" : "ERROR";
  } catch {
    nameMsg = "NO SERVER";
  }
  nameStage = "type";
}
let mailMsgAt = 0;
let licRemove = false; // licence page: asking to unlink the email
/** Down / tap on the email line: link an email, or (when one is linked) ask to remove it. */
function emailAction() {
  if (!emailOn || !pilotName) return;
  if (pilotEmail) licRemove = true;
  else startLink();
}
async function removeEmail() {
  licRemove = false;
  try {
    const d = await postJson("/api/pilot/email/remove", { key: pilotKey });
    if (d.ok) (pilotEmail = ""), (mailMsgAt = 0);
  } catch {
    /* offline: still linked */
  }
}
/** A licence was just obtained on this device: back to the team invitation that was being answered, else the licence page. */
function afterSignIn() {
  const code = (readStore("f1invite") ?? "").toUpperCase();
  if (/^[A-Z0-9]{6}$/.test(code)) return void loadTeamInvite(code);
  openLicence();
}
async function claimName() {
  nameStage = "busy";
  try {
    const d = await postJson("/api/pilot/claim", { key: ensureKey(), name: normPilotName(nameDraft) });
    if (d.ok || d.error === "locked") {
      setPilot(d.name);
      afterSignIn();
      nameField.blur();
    } else nameMsg = d.error === "taken" ? "NAME TAKEN" : d.error === "generic" ? "CHOOSE ANOTHER NAME" : d.error === "short" ? "TOO SHORT" : "ERROR";
  } catch {
    nameMsg = "NO SERVER";
  }
  nameStage = "type";
}
async function restoreLicence() {
  const k = normPilotKey(nameDraft);
  if (k.length !== 32) return void (nameMsg = "CODE IS 32 CHARACTERS");
  nameStage = "busy";
  try {
    const d = await postJson("/api/pilot/me", { key: k });
    if (d.ok) {
      pilotKey = k;
      writeStore("f1key", k);
      setPilot(d.name);
      afterSignIn();
      nameField.blur();
    } else nameMsg = "UNKNOWN CODE";
  } catch {
    nameMsg = "NO SERVER";
  }
  nameStage = "type";
}
// licence page 2: the pilot badge (stats and badges earned), which can be shared on Discord
let licPage = 0;
let badgeData: { profile: Record<string, number | string>; badges: string[]; total: number; share: boolean } | null = null;
let badgeMsg = "";
let badgeMsgAt = -1e9;
function openLicence() {
  licPage = 0;
  licRemove = false;
  screen = "licence";
}
function licenceSwitch() {
  licPage = 1 - licPage;
  if (licPage === 1) {
    badgeData = null;
    postJson("/api/pilot/stats", { key: pilotKey }).then((d) => d.ok && (badgeData = d), () => {});
  }
}
async function shareBadge() {
  if (!badgeData?.share) return;
  try {
    const d = await postJson("/api/pilot/announce", { key: pilotKey });
    badgeMsg = d.ok ? "SHARED ON DISCORD!" : d.error === "wait" ? `WAIT ${d.minutes} MIN` : "NOT AVAILABLE";
  } catch {
    badgeMsg = "NO SERVER";
  }
  badgeMsgAt = performance.now();
}
async function copyLicence() {
  try {
    await navigator.clipboard.writeText(formatPilotKey(pilotKey));
    licCopied = 120;
  } catch {
    /* clipboard unavailable: the code stays on screen */
  }
}

// Real <input> behind the canvas: gives paste, IME and the mobile keyboard for free. The canvas mirrors its text.
let nameDraft = "";
const nameField = document.createElement("input");
nameField.type = "text";
nameField.maxLength = 8;
nameField.autocomplete = "off";
nameField.autocapitalize = "none";
nameField.spellcheck = false;
nameField.setAttribute("aria-label", "Driver name");
nameField.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;border:0;padding:0";
document.body.appendChild(nameField);
const filterDraft = (v: string) => (nameMode === "code" ? normPilotKey(v).slice(0, 32).toUpperCase() : nameMode === "name" ? cleanName(v) : mailStep ? v.replace(/\D/g, "").slice(0, 6) : cleanMail(v));
nameField.addEventListener("input", () => {
  nameDraft = filterDraft(nameField.value);
  nameField.value = nameDraft;
});
/** Phones: the field is invisible and has no paste menu, so a tap on the box reads the clipboard (the browser may ask to allow it). */
async function pasteDraft() {
  try {
    const t = await Promise.race([navigator.clipboard.readText(), new Promise<string>((_, no) => setTimeout(() => no(new Error("timeout")), 1500))]); // a permission prompt nobody answers must not leave the box dead
    nameDraft = filterDraft(t);
    nameField.value = nameDraft;
    nameMsg = nameDraft ? "" : "NOTHING TO PASTE";
  } catch {
    // the browser refuses to hand over the clipboard (iPhone, in-app browsers...): the real field is laid over the code box, invisible, so a long press on it shows the phone's own Paste menu
    fieldOverlay = true;
    nameMsg = "HOLD THE BOX: PASTE";
    nameField.focus({ preventScroll: true });
  }
}
let fieldOverlay = false;
let fieldCss = "";
const FIELD_BASE = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;border:0;padding:0";
function placeField() {
  const want = fieldOverlay && screen === "name" && (nameMode === "code" || (nameMode !== "name" && !mailStep));
  if (!want) {
    if (fieldCss) (nameField.style.cssText = FIELD_BASE), (fieldCss = "");
    if (screen !== "name") fieldOverlay = false;
    return;
  }
  const r = canvas.getBoundingClientRect();
  const css = `position:fixed;left:${r.left}px;top:${r.top + (r.height * 34) / SCREEN_H}px;width:${r.width}px;height:${(r.height * 40) / SCREEN_H}px;opacity:0.01;border:0;padding:0;font-size:16px;z-index:5`;
  if (css !== fieldCss) nameField.style.cssText = fieldCss = css; // only when it changes: rewriting it every frame would cancel a long press
}
const coarse = () => matchMedia("(pointer:coarse)").matches;
// coming back from another window (the email with the code): the field must be ready to type in again, whatever was clicked before
const refocusName = () => {
  if (screen === "name" && nameStage !== "confirm" && document.activeElement !== nameField) nameField.focus({ preventScroll: true });
};
addEventListener("focus", refocusName);
document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && refocusName());
addEventListener("keydown", (e) => {
  if (screen === "name" && document.activeElement !== nameField && !e.altKey && e.key !== "Escape" && e.key !== "Enter" && (e.key.length === 1 || e.key === "Backspace")) refocusName(); // the keystroke then lands in the field (Ctrl+V pastes too)
}, true);
nameField.addEventListener("keydown", (e) => {
  e.stopPropagation();
  if (e.key === "Enter") commitName();
  else if (e.key === "Escape") nameBack();
  else if (e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") (e.preventDefault(), toggleNameMode());
});
const roomField = document.createElement("input");
roomField.type = "text";
roomField.maxLength = 6;
roomField.autocomplete = "off";
roomField.autocapitalize = "characters";
roomField.spellcheck = false;
roomField.setAttribute("aria-label", "Room code");
roomField.style.cssText = nameField.style.cssText;
document.body.appendChild(roomField);
roomField.addEventListener("input", () => {
  joinDraft = cleanRoom(roomField.value);
  roomField.value = joinDraft;
});
roomField.addEventListener("keydown", (e) => {
  e.stopPropagation();
  if (e.key === "Enter") commitJoin();
  else if (e.key === "Escape") roomField.blur();
});
const teamField = document.createElement("input");
teamField.type = "text";
teamField.maxLength = 6;
teamField.autocomplete = "off";
teamField.autocapitalize = "characters";
teamField.spellcheck = false;
teamField.setAttribute("aria-label", "Team code");
teamField.style.cssText = nameField.style.cssText;
document.body.appendChild(teamField);
teamField.addEventListener("input", () => {
  teamField.value = cleanRoom(teamField.value);
});
teamField.addEventListener("keydown", (e) => {
  e.stopPropagation();
  if (e.key === "Enter") void submitTeamCode();
  else if (e.key === "Escape") closeTeamCode();
});
function commitJoin() {
  if (joinDraft.length < 3) return;
  room = joinDraft;
  roomField.blur();
  screen = "select";
  selRow = 1;
}
function enterOnline() {
  if (!pilotName) return void startClaim("ONLINE NEEDS A NAME");
  if (maint.on) {
    maintToastAt = performance.now(); // online play is closed: show why instead of opening the lobby
    return;
  }
  if (urlRoom) {
    room = urlRoom; // opened from an invitation link
    (screen = "select"), (selRow = 1);
    return;
  }
  roomCode = newCode();
  roomRow = 0;
  screen = "room";
}
function enterName() {
  if (pilotName) openLicence();
  else startClaim();
}
/** Enter on the name screen: check the name, ask to confirm (it cannot be changed afterwards), then register it. */
function commitName() {
  if (nameStage === "busy") return;
  if (nameMode === "code") return void restoreLicence();
  if (nameMode === "mail" || nameMode === "link") return void (mailStep ? mailVerify() : mailSend());
  if (nameStage === "confirm") return void claimName();
  const bad = pilotNameError(nameDraft);
  if (bad) return void (nameMsg = bad === "short" ? "TOO SHORT" : "CHOOSE ANOTHER NAME");
  nameMsg = "";
  nameStage = "confirm";
  nameField.blur();
}
function nameBack() {
  if (nameStage === "confirm") return void ((nameStage = "type"), nameField.focus({ preventScroll: true }));
  if (mailStep) return void ((mailStep = false), (nameDraft = ""), (nameField.value = ""), (nameField.maxLength = 60), (nameMsg = ""));
  if (nameMode === "link") return void (nameField.blur(), openLicence());
  screen = "title";
  nameField.blur();
}
canvas.addEventListener("pointerdown", (e) => {
  if (screen === "name" && nameStage !== "confirm") nameField.focus({ preventScroll: true });
  if (screen === "name") {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    if (y > 108 && y < 124) toggleNameMode();
    else if (y > 34 && y < 74 && nameStage === "type" && (nameMode === "code" || (nameMode !== "name" && !mailStep))) void pasteDraft();
  }
  if (screen === "team") {
    // phones have no C or L key: tap the invite-link line, or the bottom line to leave
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    if (teamLoaded === "ok" && myTeam && !myTeam.team) {
      if (y > 48 && y < 66) ((teamSel = 0), teamCreateGo());
      else if (y > 66 && y < 86) ((teamSel = 1), openTeamCode());
    } else if (y >= SW_Y && y < SW_Y + 10 && myTeam?.team?.owner === myTeam?.name) {
      const x = ((e.clientX - r.left) / r.width) * SCREEN_W;
      const i = Math.floor((x - SW_X0) / SW_W);
      if (i >= 0 && i < TEAM_COLORS.length) setTeamColor(i);
    } else if (y > 110 && y < 123) copyTeamInvite();
    else if (y > 133 && myTeam?.team) leaveTeam();
  }
  if (screen === "teamcode") {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    if (y > 36 && y < 66) teamField.focus({ preventScroll: true }); // a field can only be focused from a tap
  }
  if (screen === "licence" && licPage === 0 && emailOn) {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    if (y > 115 && y < 127) licRemove ? void removeEmail() : emailAction();
  }
  if (screen === "result" && !online && runMode === "tt") {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    if (y > 6 && y < 17 && canVote()) void voteShared((((e.clientX - r.left) / r.width) * SCREEN_W) < 80 ? 1 : -1);
    if (y > 122 && y < 131 && !activeQual && (activeShared || sent.state === "done")) void (activeShared ? shareCircuit() : challengeFriend()); // tap the "share / challenge a friend" line (phones have no C key)
  }
  if (screen === "select" && tapMachine(e, 17, 94)) selRow = 1;
  else if (screen === "lobby" && room !== "PUBLIC") void shareInvite();
  if (screen === "options") {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    const row = Math.floor((y - OPT_Y0) / optDy());
    if (row >= 0 && row < optRows()) {
      optRow = row;
      if (row >= 8 || row === 6) handlePress("Enter");
      else optStep(row, 1, true); // a tap on a setting goes to its next value (a volume wraps from 100% to 0)
    }
  }
  if (screen === "room") {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    const i = Math.floor((y - 40) / 24);
    if (i >= 0 && i < 3) {
      roomRow = i;
      handlePress("Enter");
    }
  }
  if (screen === "title") {
    // tap a menu line to select and open it
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    const row = Math.floor((y - 90) / 9);
    if (row >= 0 && row < 5) {
      // same 5-row window as drawTitle
      const first = Math.max(0, Math.min(menuOrder().length - 5, menuOrder().indexOf(menuIdx) - 2));
      menuIdx = menuOrder()[first + row];
      handlePress("Enter");
    }
  }
  if (screen === "solo") {
    const r = canvas.getBoundingClientRect();
    const row = Math.floor((((e.clientY - r.top) / r.height) * SCREEN_H - 30) / 25);
    if (row >= 0 && row < soloRows()) (soloRow = row), handlePress("Enter");
  }
  if (screen === "popular") {
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
    const x = ((e.clientX - r.left) / r.width) * SCREEN_W;
    if (y >= 11 && y < 22) {
      const t = Math.floor((x - 3) / 39);
      if (t >= 0 && t < 4 && t !== comSort) (comSort = t), (popRow = 0), loadCommunity();
    } else {
      const k = Math.floor((y - 23) / 10);
      if (k === 0) (popRow = 0), handlePress("Enter");
      else if (k > 0 && Array.isArray(popular) && comFirst() + k - 1 < popular.length) (popRow = comFirst() + k), handlePress("Enter");
    }
  }
});
const targets = new Map<number, { pos: number; speed: number; lat: number; jet: boolean; at: number }>();

// ---- records: personal bests (local) and the shared server leaderboard ----
interface Pb { lap?: number; race?: number }
const loadPbs = (): Record<string, Pb> => {
  try {
    return JSON.parse(localStorage.getItem("f1pb") ?? "{}");
  } catch {
    return {};
  }
};
let pbs = loadPbs();
const savePbs = () => {
  try {
    localStorage.setItem("f1pb", JSON.stringify(pbs));
  } catch {
    /* storage unavailable */
  }
};
// ---- circuits: the 8 built-in ones plus custom ones made by the admin; the server decides which are in the season ----
let seasonClass = 0; // vehicle category of the solo season, set by the admin
function applyTracks(m: { custom: TrackDef[]; season: number[]; seasonClass?: number; sprites?: Record<string, SpriteSpec> }) {
  applySprites(m.sprites);
  seasonClass = VCLASSES[m.seasonClass ?? 0] ? (m.seasonClass ?? 0) : 0;
  m.custom.forEach((d, i) => registerTrack(BUILTIN_COUNT + i, d));
  setSeason(m.season);
  // a custom circuit that was removed from the rotation must not stay selected
  if (!SEASON.includes(trackIdx) && !(rally && TRACKS[trackIdx]?.stage)) trackIdx = SEASON[0];
  if (!SEASON.includes(recTrack) && !TRACKS[recTrack]?.stage) recTrack = SEASON[0];
}
/** The stages (rally style, A to B): they are not in the season; the RALLY entry of the SOLO menu plays them. */
const stageList = () => TRACKS.map((t, i) => (t.stage ? i : -1)).filter((i) => i >= 0);
let rally = false; // the solo time-trial screen is showing the stages
const cycleIn = (list: number[], cur: number, left: boolean) => {
  const i = list.indexOf(cur);
  return list[(Math.max(i, 0) + (left ? list.length - 1 : 1)) % list.length];
};
const cycleSeason = (cur: number, left: boolean) => {
  const i = SEASON.indexOf(cur);
  return SEASON[(Math.max(i, 0) + (left ? SEASON.length - 1 : 1)) % SEASON.length];
};
function loadTracks(): Promise<void> {
  return fetch(api("/api/tracks"), { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((m) => {
      applyTracks(m);
      try {
        localStorage.setItem("f1tracks", JSON.stringify(m));
      } catch {
        /* storage unavailable */
      }
    })
    .catch(() => {
      try {
        const m = JSON.parse(localStorage.getItem("f1tracks") ?? "null"); // offline: last known rotation
        if (m && Array.isArray(m.custom) && Array.isArray(m.season)) applyTracks(m);
      } catch {
        /* nothing cached */
      }
    });
}
interface Row { name: string; frames: number; machine: number; dev?: number }
interface BoardData { lap: Row[]; race: Row[] }
const boards = new Map<string, BoardData | "loading" | "offline">();
/** Board cache keys: "<circuit>" for races with CPU, "tt<circuit>" for time trials, "daily" for today's challenge. */
function loadBoard(key: string) {
  boards.set(key, "loading");
  const url = key === "daily" ? "/api/daily" : key === "weekly" ? "/api/weekly" : key.startsWith("tt") ? `/api/scores?mode=tt&track=${key.slice(2)}` : `/api/scores?track=${key}`;
  fetch(api(url), { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((d: BoardData & { day?: string; track?: number; seed?: number; skill?: number; vclass?: number } & Partial<WeeklyInfo>) => {
      if (key === "weekly" && (d as any).enabled === false) {
        weeklyInfo = null; // switched off by the admin: no menu entry, no tab
        if (recCat === 4) recCat = 3;
        if (menuIdx === 9) menuIdx = 6;
      } else if (key === "weekly" && d.week) {
        if (!TRACKS[d.track!]) return void loadTracks().then(() => loadBoard(key));
        weeklyInfo = { week: d.week, track: d.track!, seed: d.seed!, skill: d.skill!, vclass: VCLASSES[d.vclass ?? 0] ? (d.vclass ?? 0) : 0, ai: d.ai ?? 9, nojet: !!d.nojet, machine: d.machine ?? -1, title: String(d.title ?? "WEEKLY").slice(0, 13), trackName: TRACKS[d.track!].name, endsAt: d.endsAt ?? 0 };
      }
      if (key === "daily" && d.day) {
        if (!TRACKS[d.track!]) return void loadTracks().then(() => loadBoard(key)); // a circuit we have not heard of yet
        dailyInfo = { day: d.day, track: d.track!, seed: d.seed!, skill: d.skill!, vclass: VCLASSES[d.vclass ?? 0] ? (d.vclass ?? 0) : 0, ai: d.ai === 0 ? 0 : RANKED_AI };
        loadDailyGhost();
      }
      boards.set(key, d);
    })
    .catch(() => boards.set(key, "offline"));
}
void loadTracks().then(() => (loadBoard("daily"), loadBoard("weekly"))); // learn the circuits, then today's challenge from the server (its clock decides the day)
let recTrack = 0;
let recCat = 0; // 0 world lap, 1 world race, 2 time trial, 3 daily, 4 weekly, 5 daily championship, 6 mine, 7 teams
const REC_TABS = ["LAP", "RACE", "TT", "DAY", "WEEK", "CHMP", "MINE", "TEAM"];
const REC_TABS_FR = ["TOUR", "COUR", "ESS", "DEFI", "SEM", "CHMP", "MOI", "ECUR"]; // 4 letters at most: eight tabs of 19 px
/** Tabs shown: WEEK disappears when the weekly challenge is switched off. */
const recTabs = () => REC_TABS.map((_, i) => i).filter((i) => i !== 4 || weeklyInfo);
// ---- daily championship: points for every daily challenge of the month (or all time), from the server ----
interface ChampRow { rank: number; name: string; points: number; wins: number; played: number; streak: number }
interface ChampData { label: string; provisional: boolean; standings: ChampRow[] }
const champs = new Map<string, ChampData | "loading" | "offline">();
let champPeriod: "month" | "all" = "month";
function loadChamp(period: "month" | "all") {
  champs.set(period, "loading");
  fetch(api("/api/daily/standings?period=" + period), { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((d: ChampData) => champs.set(period, d))
    .catch(() => champs.set(period, "offline"));
}
// ---- the écuries' ranking (same numbers as the /ecuries page) ----
interface TeamRecRow { rank: number; name: string; tag: string; points: number; members: number; active: number }
interface TeamRecData { label: string; teams: TeamRecRow[] }
const teamRecs = new Map<string, TeamRecData | "loading" | "offline">();
let teamPeriod: "month" | "all" = "month";
function loadTeamRec(period: "month" | "all") {
  teamRecs.set(period, "loading");
  fetch(api("/api/teams/standings?period=" + period), { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((d: TeamRecData) => teamRecs.set(period, d))
    .catch(() => teamRecs.set(period, "offline"));
}
const recKey = () => (recCat === 2 ? "tt" + recTrack : recCat === 3 ? "daily" : recCat === 4 ? "weekly" : String(recTrack));
const loadRecBoard = () => {
  if (recCat < 5) loadBoard(recKey());
  else if (recCat === 5) loadChamp(champPeriod);
  else if (recCat === 7) loadTeamRec(teamPeriod);
};

// ---- game modes: season / quick race (vs CPU), time trial (alone, with a ghost), daily challenge (same race for everyone) ----
type RunMode = "season" | "quick" | "tt" | "daily" | "weekly";
let runMode: RunMode = "quick";
let dailyInfo = { ...dailyOf(utcDay()), ai: RANKED_AI }; // refreshed from the server (its clock decides the day)
interface GhostData { name: string; frames: number; machine: number; phys?: number; rle: [number, number][] }
interface GhostRun { race: Race; masks: Uint8Array; idx: number; posAt: number[]; name: string; frames: number }
let ghost: GhostRun | null = null;
let ghostSrc = 0; // 0 my best run, 1 world record, 2 off
const GHOST_SRC = ["MY BEST", "WORLD", "OFF"];
const loadLocalGhost = (t: number): GhostData | null => {
  try {
    return JSON.parse(localStorage.getItem("f1ghost" + t) ?? "null");
  } catch {
    return null;
  }
};
const validGhost = (g: any): g is GhostData =>
  !!g && Array.isArray(g.rle) && g.rle.length > 0 && g.rle.every((p: any) => Array.isArray(p) && Number.isInteger(p[0]) && Number.isInteger(p[1]) && p[1] > 0) && (g.machine === 0 || g.machine === 1);
function makeGhost(t: number, g: GhostData, daily?: { seed: number; skill: number; vclass: number; ai: number }, def?: TrackDef) {
  const tr = def ?? TRACKS[t];
  const total = g.rle.reduce((a, p) => a + p[1], 0);
  const masks = new Uint8Array(total);
  let i = 0;
  for (const [m, n] of g.rle) for (let k = 0; k < n; k++) masks[i++] = m;
  // a daily ghost is the champion's whole race (same seed, same traffic): replaying it in its own copy of the race reproduces it exactly
  const race = daily
    ? new Race({ track: tr, machines: [g.machine], aiCount: daily.ai, seed: daily.seed, skill: daily.skill, vclass: daily.vclass, phys: physOf(g.phys) })
    : new Race({ track: tr, machines: [g.machine], aiCount: 0, seed: 0, phys: physOf(g.phys) });
  ghost = { race, masks, idx: 0, posAt: [], name: g.name, frames: g.frames };
}
// ---- the daily champion's ghost: today's best verified run, fetched from the server ----
let dailyGhost: (GhostData & { day: string }) | null = null;
let dailyGhostOn = (() => {
  try {
    return localStorage.getItem("f1dghost") !== "0";
  } catch {
    return true;
  }
})();
function loadDailyGhost() {
  fetch(api("/api/daily/ghost"), { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : null))
    .then((g) => void (dailyGhost = validGhost(g) && typeof (g as { day?: unknown }).day === "string" ? (g as GhostData & { day: string }) : null))
    .catch(() => void (dailyGhost = null));
}
const posPerFrame = (v: number) => (UNIT_PER_TICK_FACTOR * Math.floor(v / SPEED_QUANT) * SPEED_QUANT) / TICK_FRAMES;
/** Seconds between me and the ghost: positive when the ghost is ahead. */
function ghostGap(me: Car): number | undefined {
  if (!ghost) return undefined;
  const gp = ghost.race.cars[0].pos;
  const cur = ghost.posAt.length;
  if (gp > me.pos) {
    // the ghost has already been where I am now: find when
    let lo = 0, hi = cur - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (ghost.posAt[mid] >= me.pos) hi = mid;
      else lo = mid + 1;
    }
    return (cur - lo) / 59.7275;
  }
  return -(me.pos - gp) / Math.max(40, posPerFrame(Math.max(me.speed, 60))) / 59.7275;
}

let inputLog: number[] = [];
let runSeed = 0;
let runAi = 9;
let runDay = "";
let runClass = 0; // vehicle category of the race being driven
type Sent = { state: "idle" } | { state: "sending" } | { state: "offline" } | { state: "unranked" } | { state: "done"; lapRank: number; raceRank: number; challenge?: boolean };
let sent: Sent = { state: "idle" };
let newLap = false, newRace = false;

function submitRun() {
  if (testDef || testVClass || !pilotName) return void (sent = { state: "unranked" }); // records are filed under a registered pilot name
  const me = race!.cars[mySlot];
  const best = Math.min(...me.lapFrames);
  const mode = runMode === "tt" ? "tt" : runMode === "daily" ? "daily" : runMode === "weekly" ? "weekly" : "race";
  if (mode === "race" && (runAi !== RANKED_AI || raceSkill < MIN_RANKED_SKILL || runClass !== 0)) return void (sent = { state: "unranked" }); // only the formula car is ranked outside the daily challenge
  const sc = mode === "tt" && trackIdx === SHARED_IDX ? activeShared : null; // a circuit shared by a player: its own board
  const qc = mode === "tt" && !sc && activeQual && trackIdx === activeQual.track ? activeQual : null; // a qualifier run: its own board
  const key = qc ? "q" + qc.id : sc ? "s" + sc.code : mode === "tt" ? "tt" + trackIdx : mode === "daily" ? "d" + runDay + (runClass ? "v" + runClass : "") : mode === "weekly" ? "w" + runDay : String(trackIdx);
  const pb = (pbs[key] ??= {});
  newLap = pb.lap === undefined || best < pb.lap;
  newRace = pb.race === undefined || me.finishFrame < pb.race;
  if (newLap) pb.lap = best;
  if (newRace) pb.race = me.finishFrame;
  if (mode === "tt" && newRace && !sc) {
    try {
      localStorage.setItem("f1ghost" + trackIdx, JSON.stringify({ name: nameOf(), frames: me.finishFrame, machine, phys: physOf(me.phys), rle: rle(inputLog) }));
    } catch {
      /* storage unavailable */
    }
  }
  savePbs();
  sent = { state: "sending" };
  fetch(api("/api/runs"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key: pilotKey, name: nameOf(), track: trackIdx, circuit: sc?.code, qual: qc?.id, machine, phys: physOf(me.phys), seed: runSeed, ai: runAi, skill: raceSkill, vclass: (mode === "daily" || mode === "weekly") && runClass ? runClass : undefined, mode: mode === "race" ? undefined : mode, day: mode === "daily" || mode === "weekly" ? runDay : undefined, rle: rle(inputLog), dev: runSource() }),
  })
    .then((r) => r.json())
    .then((d) => {
      if (d.ok) {
        sent = { state: "done", lapRank: d.lap.rank, raceRank: d.race.rank, challenge: !!d.challenge };
        void checkBadges();
        if (sc) void refreshShared(sc.code); // the new board and ghost for the next try
        boards.delete(mode === "tt" ? "tt" + trackIdx : mode === "daily" ? "daily" : mode === "weekly" ? "weekly" : String(trackIdx));
        if (mode === "daily") loadChamp("month"); // my new championship points
        if (mode === "daily") loadDailyGhost(); // if I am the new champion, the ghost is mine
      } else sent = { state: "unranked" };
    })
    .catch(() => (sent = { state: "offline" }));
}

function startSolo(t: number) {
  activeQual = null; // acceptQual sets it again once the race exists
  activeChallenge = null; // acceptChallenge sets it again once the race exists
  linkMsg = "";
  online = false;
  seasonScored = false;
  ghost = null;
  trackIdx = t;
  runClass = runMode === "season" ? seasonClass : 0;
  if (runMode === "tt") {
    runAi = 0; // alone on the track: the replay is then exact and the times comparable
    runSeed = 0;
    raceSkill = 1;
  } else if (runMode === "weekly" && weeklyInfo) {
    trackIdx = t = weeklyInfo.track;
    runAi = weeklyInfo.ai;
    runSeed = weeklyInfo.seed;
    raceSkill = weeklyInfo.skill;
    runDay = weeklyInfo.week;
    runClass = weeklyInfo.vclass;
    if (weeklyInfo.machine >= 0) machine = weeklyInfo.machine;
  } else if (runMode === "daily") {
    trackIdx = t = dailyInfo.track;
    runAi = dailyInfo.ai;
    runSeed = dailyInfo.seed;
    raceSkill = dailyInfo.skill;
    runDay = dailyInfo.day;
    runClass = dailyInfo.vclass;
  } else {
    raceSkill = skillIdx;
    runAi = Math.max(0, Math.min(9, Number(new URLSearchParams(location.search).get("ai") ?? 9)));
    runSeed = (Math.random() * 2 ** 32) >>> 0;
  }
  if (testVClass && runMode === "quick") runClass = testVClass;
  inputLog = [];
  resetSource();
  sent = { state: "idle" };
  newLap = newRace = false;
  if (t !== SHARED_IDX) activeShared = null; // only the shared-circuit screen starts a race on one
  race = new Race({ track: testDef ?? (t === SHARED_IDX && activeShared ? activeShared.def : undefined) ?? TRACKS[t], machines: [machine], aiCount: runAi, seed: runSeed, skill: raceSkill, vclass: runClass, phys: livePhys() });
  myId = 0;
  mySlot = 0;
  screen = "race";
}

// ---- circuits shared by players: a link /t/CODE opens the game on "NAME's circuit": race it against the creator's ghost, with its own board ----
const SHARED_IDX = 99;
interface SharedData { code: string; by: string; up: number; down: number; def: TrackDef; board: { name: string; frames: number }[]; ghost: GhostData | null }
const urlShared = (location.pathname.match(/^\/t\/([A-Za-z0-9]{6})$/)?.[1] ?? "").toUpperCase();
let sharedC: SharedData | null = null; // the one on the circuit screen
let sharedMsg = "";
let activeShared: SharedData | null = null; // the race being driven is on this circuit
async function fetchShared(code: string): Promise<SharedData> {
  const r = await fetch(api("/api/circuit/" + code), { cache: "no-store" });
  const d: any = await r.json();
  if (!r.ok || validateTrack(d.def)) throw new Error("expired");
  return {
    code,
    by: String(d.by).slice(0, 8),
    up: Number(d.up) || 0,
    down: Number(d.down) || 0,
    def: { ...(normalizeTrack(d.def) as TrackDef), id: SHARED_IDX },
    board: Array.isArray(d.board) ? d.board.slice(0, 3).map((e: any) => ({ name: String(e.name).slice(0, 8), frames: Number(e.frames) })) : [],
    ghost: validGhost(d.ghost) ? { name: String(d.ghost.name).slice(0, 8), frames: d.ghost.frames, machine: d.ghost.machine, rle: d.ghost.rle } : null,
  };
}
async function loadShared(code: string) {
  try {
    sharedC = await fetchShared(code);
  } catch {
    sharedC = null;
    sharedMsg = "CIRCUIT EXPIRED";
  }
  screen = "circuit";
  try {
    history.replaceState(null, "", "/");
  } catch {
    /* ignore */
  }
}
async function refreshShared(code: string) {
  try {
    const d = await fetchShared(code);
    if (activeShared?.code === code) activeShared = d;
    if (sharedC?.code === code) sharedC = d;
  } catch {
    /* offline: keep what we have */
  }
}
function acceptShared() {
  const c = sharedC;
  if (!c) return void (screen = "title");
  runMode = "tt";
  menuIdx = 5;
  trackIdx = SHARED_IDX;
  activeShared = c;
  voteMsg = "";
  startSolo(SHARED_IDX);
  if (c.ghost) makeGhost(SHARED_IDX, c.ghost, undefined, c.def);
  activeChallenge = null;
  linkMsg = "";
}
// ---- community circuits (admin switch): enter a code, or browse the popular / newest / most played player circuits ----
type ComRow = { code: string; name: string; by: string; pilots: number; pct: number };
let popular: ComRow[] | "loading" | "offline" = "loading";
let popRow = 0; // 0 = ENTER A CODE, then the circuits
let comSort = 0; // 0 popular this week, 1 newest, 2 most played
const COM_TABS = ["POPULAR", "NEWEST", "PLAYED", "RATED"];
const ratePct = (up: number, down: number) => (up + down ? Math.round((100 * up) / (up + down)) : 0);
let codeFor: "team" | "circuit" = "team";
function openPopular() {
  screen = "popular";
  popRow = 0;
  comSort = 1; // opens on the newest circuits: the freshest creations are what players come to try
  loadCommunity();
}
function loadCommunity() {
  popular = "loading";
  fetch(api("/api/community?sort=" + ["pop", "new", "played", "rated"][comSort]), { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((d) => {
      popularOn = d.enabled === true;
      popular = Array.isArray(d.circuits) ? d.circuits.slice(0, 40).map((x: any) => ({ code: String(x.code).slice(0, 6), name: String(x.name).slice(0, 13), by: String(x.by).slice(0, 8), pilots: Number(comSort === 0 ? x.week : x.total) || 0, pct: comSort === 3 ? ratePct(Number(x.up) || 0, Number(x.down) || 0) : -1 })) : [];
    })
    .catch(() => (popular = "offline"));
}
function openCircuitCode() {
  codeFor = "circuit";
  teamCodeMsg = "";
  teamField.value = "";
  screen = "teamcode";
  teamField.focus({ preventScroll: true });
}
const COM_VIS = 7;
const comFirst = () => Math.max(0, Math.min(Math.max(0, popRow - 1 - 3), (Array.isArray(popular) ? popular.length : 0) - COM_VIS));
function drawPopular() {
  drawTextC(ctx, "COMMUNITY CIRCUITS", 80, 3, PAL[3], 1);
  COM_TABS.forEach((t, i) => {
    const x = 5 + i * 39;
    if (i === comSort) (ctx.fillStyle = PAL[3]), ctx.fillRect(x - 2, 12, 38, 9);
    drawText(ctx, t, x, 14, i === comSort ? PAL[0] : PAL[2]);
  });
  const rowY = (k: number) => 24 + k * 10;
  const code = popRow === 0;
  if (code) (ctx.fillStyle = PAL[3]), ctx.fillRect(4, rowY(0) - 1, 152, 10);
  drawText(ctx, "> ENTER A CODE", 7, rowY(0) + 1, code ? PAL[0] : PAL[3]);
  if (popular === "loading") return void drawTextC(ctx, "LOADING...", 80, 80, PAL[2]);
  if (popular === "offline") return void drawTextC(ctx, "SERVER OFFLINE", 80, 80, PAL[3]);
  if (!popular.length) {
    drawTextC(ctx, "NOTHING YET", 80, 70, PAL[3]);
    drawTextC(ctx, "DESIGN ONE AT /CIRCUIT", 80, 82, PAL[2]);
  }
  const f = comFirst();
  popular.slice(f, f + COM_VIS).forEach((c, k) => {
    const i = f + k;
    const y = rowY(k + 1);
    const on = popRow === i + 1;
    if (on) (ctx.fillStyle = PAL[3]), ctx.fillRect(4, y - 1, 152, 10);
    const col = on ? PAL[0] : PAL[3];
    drawText(ctx, "\u0001" + `${i + 1}`.padStart(2) + " " + c.name, 7, y + 1, col); // a player's circuit name is never translated
    drawText(ctx, "\u0001" + c.by, 92, y + 1, on ? PAL[0] : PAL[2]);
    if (c.pct >= 0 || c.pilots > 0) drawText(ctx, c.pct >= 0 ? `${c.pct}%`.padStart(4) : `${c.pilots}P`.padStart(3), c.pct >= 0 ? 134 : 140, y + 1, col);
  });
  drawTextC(ctx, "LEFT/RIGHT: LIST - START: PLAY", 80, 128, PAL[2]);
  drawTextC(ctx, "ESC: MENU", 80, 136, PAL[2]);
}
// ---- tournament qualifier: a time trial open for some days, the fastest pilots qualify for the final ----
const validQual = (q: any): q is QualInfo => !!q && typeof q.id === "string" && typeof q.name === "string" && ["upcoming", "open", "closed"].includes(q.phase) && Number.isInteger(q.track) && !!TRACKS[q.track] && Number.isFinite(q.openAt) && Number.isFinite(q.closeAt) && Number.isInteger(q.size);
interface QualData { info: QualInfo; top: { name: string; frames: number; dev?: number }[]; skew: number }
let qualD: QualData | null | "loading" | "offline" = "loading";
let activeQual: QualInfo | null = null; // the time trial being driven counts for this qualifier
function openQual() {
  screen = "qual";
  qualD = "loading";
  void refreshQual();
}
async function refreshQual() {
  try {
    const r = await fetch(api("/api/qualifier"), { cache: "no-store" });
    const d: any = (await r.json()).qualifier;
    if (!r.ok) throw new Error("bad");
    if (!validQual(d)) return void ((qualInfo = null), (qualD = null));
    const info = d as QualInfo;
    qualInfo = info;
    qualD = { info, top: Array.isArray((d as any).top) ? (d as any).top.slice(0, 10).map((e: any) => ({ name: String(e.name).slice(0, 8), frames: Number(e.frames), dev: e.dev === 0 || e.dev === 1 || e.dev === 2 ? e.dev : undefined })) : [], skew: Number((d as any).now) - Date.now() };
  } catch {
    if (qualD === "loading") qualD = "offline";
  }
}
function acceptQual() {
  if (!qualD || typeof qualD !== "object" || qualD.info.phase !== "open") return;
  const q = qualD.info;
  if (q.machine >= 0) machine = q.machine;
  runMode = "tt";
  menuIdx = 5;
  startSolo(q.track);
  activeQual = q;
  activeChallenge = null;
  linkMsg = "";
}
const span = (ms: number) => {
  const m = Math.max(0, Math.floor(ms / 60_000));
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);
  return d ? `${d}D ${h}H` : h ? `${h}H ${m % 60}M` : `${m}M`;
};
function drawQual() {
  drawTextC(ctx, "TOURNAMENT", 80, 3, PAL[3], 1);
  if (qualD === "loading") return void drawTextC(ctx, "LOADING...", 80, 70, PAL[2]);
  if (qualD === "offline") return void (drawTextC(ctx, "SERVER OFFLINE", 80, 64, PAL[3]), drawTextC(ctx, "ESC: MENU", 80, 134, PAL[2]));
  if (!qualD) return void (drawTextC(ctx, "NO QUALIFIER NOW", 80, 64, PAL[3]), drawTextC(ctx, "ESC: MENU", 80, 134, PAL[2]));
  const data = qualD;
  const q = data.info;
  const now = Date.now() + data.skew;
  drawTextC(ctx, `${q.name} - ${q.trackName}`.slice(0, 38), 80, 12, PAL[3]);
  const state = q.phase === "open" ? `QUALIFYING: ${span(q.closeAt - now)} LEFT` : q.phase === "upcoming" ? `OPENS IN ${span(q.openAt - now)}` : "QUALIFYING CLOSED";
  drawTextC(ctx, state, 80, 21, q.phase === "open" ? PAL[3] : PAL[2]);
  const me = nameOf();
  if (!data.top.length) drawTextC(ctx, q.phase === "upcoming" ? "NO TIMES YET" : "NO TIME YET: BE THE FIRST!", 80, 62, PAL[2]);
  data.top.forEach((r, i) => {
    const y = 31 + i * 8;
    const mine = r.name === me;
    if (mine) (ctx.fillStyle = PAL[3]), ctx.fillRect(6, y - 1, 148, 8);
    const c = mine ? PAL[0] : PAL[3];
    drawText(ctx, String(i + 1).padStart(2), 8, y, c);
    drawText(ctx, r.name, 24, y, c);
    if (r.dev !== undefined) drawDevice(ctx, r.dev, 63, y, c);
    drawText(ctx, formatTime(r.frames), 84, y, c);
    if (i === q.size - 1 && data.top.length > i + 1) (ctx.fillStyle = PAL[2]), ctx.fillRect(6, y + 7, 148, 1); // the qualifying line
  });
  drawTextC(ctx, `TOP ${q.size} QUALIFY${q.machine >= 0 ? " - MACHINE " + MACHINES[q.machine].name : ""}`, 80, 114, PAL[2]);
  if (q.phase === "open") {
    if (q.machine < 0) drawTextC(ctx, `YOUR MACHINE: < ${MACHINES[machine].name} >`, 80, 123, PAL[3]);
    drawTextC(ctx, pilotName ? "PRESS START - ESC: MENU" : "NO LICENCE: UNRANKED - ESC: MENU", 80, 134, PAL[2]);
  } else drawTextC(ctx, "ESC: MENU", 80, 134, PAL[2]);
}
/** Colours of the cars for the "Couleurs" option: online, each player's team livery; solo, only the player's own team. `cam` = car index whose view is drawn (stream). */
function setAccents(cam = -1) {
  accents.on = colorsActive();
  accents.carColors.clear();
  if (!accents.on) return;
  if (online || streamMode) {
    players.forEach((p, i) => p.tc !== undefined && accents.carColors.set(i, p.tc));
    const mine = streamMode ? players[cam]?.tc : players.find((p) => p.id === myId)?.tc;
    if (mine !== undefined) accents.carColors.set(-1, mine);
  } else if (myTeam?.team) accents.carColors.set(-1, myTeam.team.color);
}
let myTeamAsked = false;
/** Loads the player's team once, as soon as the menu shows (its name is in the menu label, the livery in the colours). */
function ensureMyTeam() {
  if (myTeamAsked || !pilotKey || myTeam) return;
  myTeamAsked = true;
  teamCall("me").then((v) => v?.ok && (myTeam = v as TeamView)).catch(() => {});
}
// ---- teams (écuries): the tag of a pilot's team shows on the podiums; management is on the web page /team ----
interface TeamView { name: string; points: number; minPoints: number; canCreate: boolean; waitDays: number; max: number; team?: { name: string; tag: string; color: number; owner: string; members: string[]; code?: string } | null; score?: { points: number; rank: number; of: number } | null }
let myTeam: TeamView | null = null;
let teamMsg = "";
let teamLoaded: "loading" | "ok" | "offline" | "nokey" = "loading";
let teamLeaveAsk = false;
async function teamCall(act: string, body: object = {}): Promise<any> {
  const r = await fetch(api("/api/team/" + act), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key: pilotKey, ...body }) });
  try {
    return await r.json();
  } catch {
    return { ok: false, error: "http " + r.status }; // the server answered, but not with data (a proxy page, a restart...)
  }
}
/** What went wrong, in a few words: the real reason, not a blanket "offline". */
function teamErrText(v: any): string {
  const e = String(v?.error ?? "");
  const map: Record<string, string> = { "already in a team": "YOU ARE ALREADY IN A TEAM", "team full": "THIS TEAM IS FULL", "unknown invitation": "UNKNOWN CODE", "no pilot": "LICENCE NOT RECOGNISED", "not enough points": "NOT ENOUGH POINTS", "slow down": "TOO MANY TRIES: WAIT A MINUTE" };
  if (e === "wait") return `WAIT ${v?.wait ?? "A FEW"} DAYS`;
  if (map[e]) return map[e];
  if (e.startsWith("http ")) return "SERVER ERROR " + e.slice(5);
  return e ? e.toUpperCase().slice(0, 26) : "COULD NOT JOIN";
}
let teamSel = 0; // no team yet: 0 create, 1 enter a code
let teamCodeMsg = "";
let inviteBack: Screen = "title";
function openTeamCode() {
  if (!pilotName || !pilotKey) return void startClaim("GET A LICENCE FIRST");
  codeFor = "team";
  teamCodeMsg = "";
  teamField.value = "";
  screen = "teamcode";
  teamField.focus({ preventScroll: true });
}
function closeTeamCode() {
  teamField.blur();
  screen = codeFor === "circuit" ? "popular" : "team";
}
async function submitTeamCode() {
  const code = cleanRoom(teamField.value);
  if (code.length < 6) return void (teamCodeMsg = "THE CODE HAS 6 CHARACTERS");
  if (codeFor === "circuit") {
    teamField.blur();
    return void loadShared(code);
  }
  teamCodeMsg = "CHECKING...";
  try {
    const r = await fetch(api("/api/team/invite/" + code), { cache: "no-store" });
    if (r.status === 404) return void (teamCodeMsg = "UNKNOWN CODE");
    if (!r.ok) return void (teamCodeMsg = "SERVER ERROR " + r.status);
    const d: any = await r.json();
    invite = { code, name: String(d.name).slice(0, 14), tag: String(d.tag).slice(0, 4), owner: String(d.owner).slice(0, 8), members: Number(d.members) || 0, max: Number(d.max) || 0 };
    inviteMsg = "";
    inviteBack = "team";
    teamField.blur();
    screen = "teaminvite";
  } catch {
    teamCodeMsg = "SERVER UNREACHABLE";
  }
}
function openTeam() {
  screen = "team";
  teamMsg = "";
  teamLeaveAsk = false;
  if (!pilotName || !pilotKey) return void (teamLoaded = "nokey");
  teamLoaded = myTeam ? "ok" : "loading";
  teamCall("me")
    .then((v) => {
      if (v?.ok) (myTeam = v as TeamView), (teamLoaded = "ok");
      else (teamLoaded = "offline"), (teamMsg = teamErrText(v));
    })
    .catch(() => ((teamLoaded = "offline"), (teamMsg = "SERVER UNREACHABLE")));
}
const teamPageUrl = () => (SERVER || location.origin) + "/team" + (SERVER && pilotKey ? "#k=" + pilotKey : ""); // the key rides in the #: it never reaches a server
function teamCreateGo() {
  const v = myTeam;
  if (!v) return;
  if (v.canCreate && !v.waitDays) openTeamPage();
  else teamMsg = v.canCreate ? `WAIT ${v.waitDays} DAYS` : `${v.minPoints} POINTS NEEDED`;
}
function openTeamPage() {
  if (teamLoaded !== "ok") return;
  const w = window.open(teamPageUrl(), "_blank", "noopener");
  if (w) return;
  teamMsg = "LINK COPIED";
  try {
    void navigator.clipboard?.writeText(teamPageUrl());
  } catch {
    /* ignore */
  }
}
function copyTeamInvite() {
  const t = myTeam?.team;
  if (!t?.code || t.owner !== myTeam?.name) return;
  const url = (SERVER || location.origin) + "/e/" + t.code;
  try {
    const text = `Join my team ${t.name} - Formule Race. In TEAM, choose ENTER A CODE: ${t.code}`;
    if (navigator.share && matchMedia("(pointer:coarse)").matches) void navigator.share({ title: "Formule Race", text, url });
    else void navigator.clipboard?.writeText(`${text}\n${url}`);
    teamMsg = "INVITE COPIED";
  } catch {
    teamMsg = "COPY FAILED";
  }
}
function leaveTeam() {
  if (!myTeam?.team) return;
  if (!teamLeaveAsk) return void ((teamLeaveAsk = true), (teamMsg = coarse() ? "TAP AGAIN TO LEAVE" : "PRESS L AGAIN TO LEAVE"));
  teamLeaveAsk = false;
  teamCall("leave")
    .then((v) => {
      if (v?.ok) (myTeam = v as TeamView), (teamMsg = "YOU LEFT THE TEAM");
      else teamMsg = String(v?.error ?? "ERROR").toUpperCase().slice(0, 26);
    })
    .catch(() => (teamMsg = "SERVER OFFLINE"));
}
/** Row of livery swatches for the owner (shown whatever the Colors option says: it is the owner's choice for everybody). */
const SW_X0 = 20, SW_W = 10, SW_Y = 123;
function drawTeamSwatches(sel: number) {
  TEAM_COLORS.forEach((c, i) => {
    const x = SW_X0 + i * SW_W;
    ctx.fillStyle = i === sel ? PAL[3] : PAL[1];
    ctx.fillRect(x, SW_Y, SW_W - 1, 9);
    ctx.fillStyle = c.light;
    ctx.fillRect(x + 1, SW_Y + 1, SW_W - 3, 3);
    ctx.fillStyle = c.dark;
    ctx.fillRect(x + 1, SW_Y + 4, SW_W - 3, 4);
  });
}
function setTeamColor(i: number) {
  const t = myTeam?.team;
  if (!t || t.owner !== myTeam?.name) return;
  const c = ((i % TEAM_COLORS.length) + TEAM_COLORS.length) % TEAM_COLORS.length;
  teamCall("color", { color: c })
    .then((v) => {
      if (v?.ok) (myTeam = v as TeamView), (teamMsg = "COLOR: " + TEAM_COLORS[c].name);
      else teamMsg = String(v?.error ?? "ERROR").toUpperCase().slice(0, 26);
    })
    .catch(() => (teamMsg = "SERVER OFFLINE"));
}
function drawTeam() {
  drawTextC(ctx, "TEAM", 80, 4, PAL[3], 2);
  if (teamLoaded === "nokey") return void (drawTextC(ctx, "GET A LICENCE FIRST", 80, 56, PAL[3]), drawTextC(ctx, "MENU: DRIVER", 80, 68, PAL[2]), drawTextC(ctx, "ESC: MENU", 80, 134, PAL[2]));
  if (teamLoaded === "loading") return void drawTextC(ctx, "LOADING...", 80, 70, PAL[2]);
  if (teamLoaded === "offline" || !myTeam) return void (drawTextC(ctx, teamMsg === "SERVER UNREACHABLE" || !teamMsg ? "SERVER OFFLINE" : teamMsg, 80, 64, PAL[3]), drawTextC(ctx, "ESC: MENU", 80, 134, PAL[2]));
  const v = myTeam;
  if (!v.team) {
    drawTextC(ctx, "NO TEAM YET", 80, 24, PAL[3]);
    drawTextC(ctx, `YOUR POINTS: ${v.points}`, 80, 36, PAL[2]);
    const canMake = v.canCreate && !v.waitDays;
    ["CREATE A TEAM", "ENTER A CODE"].forEach((t, i) => {
      const y = 54 + i * 18;
      const on = teamSel === i;
      if (on) (ctx.fillStyle = PAL[3]), ctx.fillRect(24, y - 3, 112, 13);
      drawTextC(ctx, t, 80, y, on ? PAL[0] : i === 0 && !canMake ? PAL[2] : PAL[3]);
    });
    if (teamSel === 0) {
      if (canMake) drawTextC(ctx, "OPENS THE TEAM PAGE", 80, 100, PAL[2]);
      else if (v.canCreate) drawTextC(ctx, `WAIT ${v.waitDays} DAY${v.waitDays > 1 ? "S" : ""} TO CHANGE TEAM`, 80, 100, PAL[2]);
      else drawTextC(ctx, `${v.minPoints} POINTS NEEDED (CHAMPIONSHIP)`, 80, 100, PAL[2]);
    } else drawTextC(ctx, "THE CODE COMES FROM THE OWNER", 80, 100, PAL[2]);
    drawTextC(ctx, "UP/DOWN: CHOOSE - START: OK", 80, 118, PAL[2]);
  } else {
    const t = v.team;
    drawTextC(ctx, `\u0001${t.name} [${t.tag}]`, 80, 16, PAL[3]); // a team name is never translated
    if (options.colors && colorsActive()) {
      const w = textWidth(`${t.name} [${t.tag}]`), c = TEAM_COLORS[t.color] ?? TEAM_COLORS[0];
      ctx.fillStyle = PAL[3];
      ctx.fillRect(Math.round(80 - w / 2) - 14, 15, 11, 8);
      ctx.fillStyle = c.light;
      ctx.fillRect(Math.round(80 - w / 2) - 13, 16, 9, 3);
      ctx.fillStyle = c.dark;
      ctx.fillRect(Math.round(80 - w / 2) - 13, 19, 9, 3);
    }
    if (t.code && t.owner === v.name) drawTextC(ctx, `CODE: ${t.code}`, 80, 25, PAL[3]); // the owner hands this code to the pilots to invite
    t.members.slice(0, 8).forEach((m, i) => drawText(ctx, "\u0001" + (m === t.owner ? "* " : "  ") + m, 36, 32 + i * 8, m === v.name ? PAL[3] : PAL[2]));
    drawTextC(ctx, `${t.members.length}/${v.max} PILOTS`, 80, 95, PAL[2]);
    if (v.score) drawTextC(ctx, `MONTH: ${v.score.points} PTS - RANK ${v.score.rank}/${v.score.of}`, 80, 106, PAL[3]);
    drawTextC(ctx, t.owner === v.name ? (coarse() ? "TAP: SHARE CODE  START: MANAGE" : "C: SHARE CODE  START: MANAGE") : "START: TEAM PAGE", 80, 116, PAL[3]);
    if (t.owner === v.name) drawTeamSwatches(t.color);
  }
  drawTextC(ctx, teamMsg || (v.team ? (coarse() ? "TAP: LEAVE - ESC: MENU" : "L: LEAVE - ESC: MENU") : "ESC: MENU"), 80, 134, teamMsg ? PAL[3] : PAL[2]);
}
// an invitation link /e/CODE opens the game on "join NAME [TAG]"
const urlTeam = (location.pathname.match(/^\/e\/([A-Za-z0-9]{6})$/)?.[1] ?? "").toUpperCase();
let invite: { code: string; name: string; tag: string; owner: string; members: number; max: number } | null = null;
let inviteMsg = "";
async function loadTeamInvite(code: string) {
  try {
    const r = await fetch(api("/api/team/invite/" + code), { cache: "no-store" });
    if (r.status === 404) throw new Error("unknown");
    if (!r.ok) throw new Error("http " + r.status);
    const d: any = await r.json();
    writeStore("f1invite", code); // kept until it is answered: a pilot without a licence signs in first and comes back to it
    inviteBack = "title";
    inviteMsg = "";
    invite = { code, name: String(d.name).slice(0, 14), tag: String(d.tag).slice(0, 4), owner: String(d.owner).slice(0, 8), members: Number(d.members) || 0, max: Number(d.max) || 0 };
  } catch (err) {
    invite = null;
    const m = err instanceof Error ? err.message : "";
    inviteMsg = m === "unknown" ? "UNKNOWN CODE" : m.startsWith("http ") ? "SERVER ERROR " + m.slice(5) : "SERVER UNREACHABLE";
    if (m === "unknown") writeStore("f1invite", ""); // a real dead code is forgotten; a network hiccup keeps it for the next try
  }
  screen = "teaminvite";
  nameField.blur(); // a name field focused earlier (first visit) would swallow the START key
  try {
    history.replaceState(null, "", "/");
  } catch {
    /* ignore */
  }
}
async function acceptTeamInvite() {
  if (!invite) return void (screen = "title");
  if (!pilotName || !pilotKey) {
    // no licence on this device (the installed app and the browser keep separate ones): sign in by email, or pick a name, then come back here
    startClaim();
    if (emailOn) setNameMode("mail");
    return;
  }
  try {
    const v = await teamCall("join", { code: invite.code });
    if (v?.ok) {
      myTeam = v as TeamView;
      writeStore("f1invite", "");
      inviteMsg = "WELCOME TO " + invite.name.slice(0, 14);
      invite = null;
      teamLoaded = "ok";
      setTimeout(() => screen === "teaminvite" && (screen = "team"), 1800);
    } else inviteMsg = teamErrText(v);
  } catch {
    inviteMsg = "SERVER UNREACHABLE";
  }
}
function drawTeamCode() {
  drawTextC(ctx, codeFor === "circuit" ? "CIRCUIT CODE" : "ENTER TEAM CODE", 80, 6, PAL[3], 2);
  const v = teamField.value;
  drawTextC(ctx, (v + "------").slice(0, 6).split("").join(" "), 80, 48, PAL[3], 2);
  if (teamCodeMsg) drawTextC(ctx, teamCodeMsg, 80, 80, PAL[3]);
  else drawTextC(ctx, coarse() ? "TAP THE CODE TO TYPE" : "TYPE THE 6 CHARACTERS", 80, 80, PAL[2]);
  drawTextC(ctx, "START: OK - ESC: BACK", 80, 134, PAL[2]);
}
function drawTeamInvite() {
  drawTextC(ctx, "TEAM INVITATION", 80, 6, PAL[3], 2);
  if (!invite) return void (drawTextC(ctx, inviteMsg || "NOT FOUND", 80, 60, PAL[3]), drawTextC(ctx, "ESC: MENU", 80, 134, PAL[2]));
  drawTextC(ctx, "\u0001" + invite.name, 80, 34, PAL[3], 2);
  drawTextC(ctx, `[${invite.tag}]`, 80, 52, PAL[3]);
  drawTextC(ctx, `BY ${invite.owner} - ${invite.members}/${invite.max} PILOTS`, 80, 66, PAL[2]);
  if (inviteMsg) drawTextC(ctx, inviteMsg, 80, 96, PAL[3]);
  else drawTextC(ctx, pilotName ? "THE TAG SHOWS BESIDE YOUR NAME" : "SIGN IN TO JOIN", 80, 96, PAL[2]);
  drawTextC(ctx, pilotName ? "START: JOIN - ESC: MENU" : "START: SIGN IN - ESC: MENU", 80, 134, PAL[2]);
}
function drawSolo() {
  drawTextC(ctx, "SOLO", 80, 8, PAL[3], 2);
  const labels = ["SEASON" + (seasonClass ? " - " + vclassOf(seasonClass).name : ""), "QUICK RACE", "TIME TRIAL", "RALLY"].slice(0, soloRows());
  const hints = ["THE FULL CHAMPIONSHIP", "ONE RACE ON ANY TRACK", "BEAT YOUR OWN LAPS", "POINT TO POINT STAGES"];
  labels.forEach((l, i) => {
    const y = 30 + i * 25, sel = i === soloRow;
    ctx.fillStyle = sel ? PAL[3] : PAL[1];
    ctx.fillRect(10, y, 140, 22);
    drawText(ctx, (sel ? "> " : "  ") + l, 14, y + 4, sel ? PAL[0] : PAL[3]);
    drawText(ctx, hints[i], 22, y + 13, sel ? PAL[0] : PAL[2]);
  });
  drawTextC(ctx, "START: PLAY - ESC: MENU", 80, 134, PAL[2]);
}
async function shareCircuit() {
  const c = activeShared;
  if (!c || linkMsg === "...") return;
  linkMsg = "...";
  try {
    const url = (SERVER || location.origin) + "/t/" + c.code;
    const text = `Try my circuit ${c.def.name} - Formule Race`;
    if (navigator.share && matchMedia("(pointer:coarse)").matches) await navigator.share({ title: "Formule Race", text, url });
    else await navigator.clipboard.writeText(`${text}\n${url}`);
    linkMsg = "LINK COPIED";
  } catch (e) {
    linkMsg = (e as Error)?.name === "AbortError" ? "" : "NO LINK, RETRY";
  }
}
function drawCircuit() {
  drawTextC(ctx, "SHARED CIRCUIT", 80, 6, PAL[3], 2);
  const c = sharedC;
  if (!c) return void (drawTextC(ctx, sharedMsg || "NOT FOUND", 80, 60, PAL[3]), drawTextC(ctx, "PRESS START", 80, 134, PAL[2]));
  drawTextC(ctx, "\u0001" + c.def.name, 80, 28, PAL[3], 2);
  drawTextC(ctx, `BY ${c.by} - ${c.def.stage ? "STAGE" : c.def.laps + " LAP" + (c.def.laps > 1 ? "S" : "")}`, 80, 44, PAL[2]);
  if (c.board.length) {
    drawTextC(ctx, "BEST TIMES", 80, 58, PAL[2]);
    c.board.forEach((e, i) => drawTextC(ctx, `${i + 1} ${e.name} ${formatTime(e.frames)}`, 80, 68 + i * 9, i ? PAL[2] : PAL[3]));
  } else drawTextC(ctx, "NO TIME YET: BE THE FIRST!", 80, 70, PAL[3]);
  drawTextC(ctx, `YOUR MACHINE: < ${MACHINES[machine].name} >`, 80, 102, PAL[3]);
  if (c.ghost) drawTextC(ctx, `GHOST: ${c.ghost.name}`, 80, 112, PAL[2]);
  if (c.up + c.down) drawTextC(ctx, `LIKED BY ${ratePct(c.up, c.down)}% (${c.up + c.down} VOTE${c.up + c.down > 1 ? "S" : ""})`, 80, 122, PAL[2]);
  drawTextC(ctx, pilotName ? "PRESS START - ESC: MENU" : "NO LICENCE: UNRANKED - ESC: MENU", 80, 134, PAL[2]);
}
// ---- friend challenges: a link /c/CODE opens the game on "NAME challenges you": race their ghost on their circuit ----
interface ChallengeData extends GhostData { code: string; by: string; track: number; trackName: string }
const urlChallenge = (location.pathname.match(/^\/c\/([A-Za-z0-9]{6})$/)?.[1] ?? new URLSearchParams(location.search).get("c") ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
let challenge: ChallengeData | null = null; // the one on the challenge screen
let challengeMsg = ""; // why there is none (expired, offline…)
let activeChallenge: ChallengeData | null = null; // the race being played is against this one
let voteMsg = ""; // feedback of the like / dislike on the result screen
let voted: Record<string, 1 | -1> = {};
const canVote = () => !online && runMode === "tt" && !!activeShared && !!pilotName && !!pilotKey && sent.state === "done" && activeShared.by !== pilotName;
async function voteShared(v: 1 | -1) {
  const c = activeShared;
  if (!c || !canVote()) return;
  try {
    const r = await fetch(api("/api/circuit/vote"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key: pilotKey, code: c.code, v }) });
    const d: any = await r.json();
    if (d.ok) {
      voted[c.code] = v;
      c.up = Number(d.up) || 0;
      c.down = Number(d.down) || 0;
      voteMsg = v === 1 ? "THANKS: YOU LIKED IT" : "THANKS: YOU DISLIKED IT";
    } else voteMsg = d.error === "norun" ? "FINISH IT FIRST" : d.error === "own" ? "YOUR OWN CIRCUIT" : "VOTE REFUSED";
  } catch {
    voteMsg = "SERVER UNREACHABLE";
  }
}
let linkMsg = ""; // feedback of "challenge a friend" on the result screen
async function loadChallenge(code: string) {
  try {
    const r = await fetch(api("/api/challenge/" + code), { cache: "no-store" });
    const d: any = await r.json();
    if (!r.ok || !validGhost({ ...d }) || !TRACKS[d.track]) throw new Error("expired");
    const by = String(d.by).slice(0, 8);
    challenge = { code, by, name: by, track: d.track, trackName: String(d.trackName ?? TRACKS[d.track].name), machine: d.machine, frames: d.frames, rle: d.rle };
  } catch {
    challenge = null;
    challengeMsg = "CHALLENGE EXPIRED";
  }
  screen = "challenge";
  try {
    history.replaceState(null, "", "/"); // a reload opens the normal menu
  } catch {
    /* ignore */
  }
}
function acceptChallenge() {
  const c = challenge;
  if (!c) return void (screen = "title");
  runMode = "tt";
  menuIdx = 5;
  trackIdx = c.track;
  startSolo(c.track);
  makeGhost(c.track, c);
  activeChallenge = c;
  linkMsg = "";
}
/** Turns my last time trial into a link and shares it (the server builds it from the run it verified). */
async function challengeFriend() {
  if (sent.state !== "done" || !sent.challenge || linkMsg === "...") return;
  linkMsg = "...";
  try {
    const d = await postJson("/api/challenge", { key: pilotKey });
    if (!d.ok) throw new Error(d.error);
    const url = (SERVER || location.origin) + "/c/" + d.code;
    const text = `Beat my time on ${d.track}: ${formatTime(d.frames)} - Formule Race`;
    if (navigator.share && matchMedia("(pointer:coarse)").matches) await navigator.share({ title: "Formule Race", text, url });
    else await navigator.clipboard.writeText(`${text}\n${url}`);
    linkMsg = "LINK COPIED";
  } catch (e) {
    linkMsg = (e as Error)?.name === "AbortError" ? "" : "NO LINK, RETRY";
  }
}
function drawChallenge() {
  drawTextC(ctx, "CHALLENGE", 80, 6, PAL[3], 2);
  const c = challenge;
  if (!c) return void (drawTextC(ctx, challengeMsg || "NOT FOUND", 80, 60, PAL[3]), drawTextC(ctx, "PRESS START", 80, 134, PAL[2]));
  drawTextC(ctx, `${c.by} CHALLENGES YOU`, 80, 30, PAL[3]);
  drawTextC(ctx, c.trackName.slice(0, 20), 80, 44, PAL[2]);
  drawTextC(ctx, formatTime(c.frames), 80, 56, PAL[3], 2);
  drawTextC(ctx, "THEIR MACHINE: " + MACHINES[c.machine].name, 80, 78, PAL[2]);
  drawTextC(ctx, `YOUR MACHINE: < ${MACHINES[machine].name} >`, 80, 90, PAL[3]);
  drawTextC(ctx, "BEAT THE GHOST!", 80, 108, PAL[3]);
  drawTextC(ctx, pilotName ? "PRESS START - ESC: MENU" : "NO LICENCE: UNRANKED - ESC: MENU", 80, 134, PAL[2]);
}

async function startTrial() {
  runMode = "tt";
  let g: GhostData | null = null;
  if (ghostSrc === 0) g = loadLocalGhost(trackIdx);
  else if (ghostSrc === 1) {
    try {
      const r = await fetch(api(`/api/ghost?track=${trackIdx}`), { cache: "no-store" });
      if (r.ok) g = await r.json();
    } catch {
      /* offline: race without a ghost */
    }
  }
  startSolo(trackIdx);
  if (validGhost(g)) makeGhost(trackIdx, g);
}

/** After a dropped connection the lobby screens rejoin the same room by themselves (3 tries); a race in progress does not. */
const RECONNECT_SCREENS: Screen[] = ["lobby", "result", "standings"];
async function goOnline(retry = 0) {
  online = true;
  if (!retry) netErr = "";
  const resuming = retry > 0 && screen === "race"; // the connection dropped during a race: the race goes on locally while we knock again
  const n = (net = new Net());
  let tries = retry; // reset once connected, so each later drop gets its own 3 tries
  n.onmsg = onNet;
  n.onclose = () => {
    if (net !== n) return;
    if (streamMode && !n.closing) { // a broadcast never gives up: it keeps knocking until the server is back
      setTimeout(() => {
        if (net === n && online) void goOnline(Math.min(tries + 1, 6));
      }, Math.min(10_000, 1500 * (tries + 1)));
      return;
    }
    if (!n.closing && screen === "race" && race && race.phase !== "finished" && tries < 25) { // keep racing and rejoin the seat (the server holds it for ~45 s)
      setTimeout(() => {
        if (net === n && online) void goOnline(tries + 1);
      }, Math.min(3000, 800 * (tries + 1)));
      return;
    }
    if (!n.closing && tries < 3 && RECONNECT_SCREENS.includes(screen)) {
      setTimeout(() => {
        if (net === n && online) void goOnline(tries + 1);
      }, 1500 * (tries + 1));
      return;
    }
    if (screen !== "title") netErr = "DISCONNECTED";
    screen = "title";
    race = null;
  };
  try {
    await n.connect(room, pilotKey, machine, streamMode, streamToken);
    tries = 0;
    if (!resuming) {
      ready = false;
      screen = streamMode ? "stream" : "lobby";
    }
  } catch {
    if (retry) return; // the close that follows schedules the next try
    if (streamMode) return void setTimeout(() => void goOnline(1), 3000);
    netErr = "NO SERVER";
    screen = "title";
  }
}

// ---- spectating a race that is already running ----
let specTrack = 0;
let specRound = 0;
let specStart = 0; // performance.now() timestamp of the race's start
let specLastStates = 0;
const specCars = new Map<number, WireCar>();
let specLocked = false; // the room is a final reserved for the qualified pilots
function enterSpectate(track: number, list: PlayerInfo[], sinceMs: number, round: number) {
  players = list;
  specTrack = TRACKS[track] ? track : SEASON[0];
  specRound = round;
  specStart = performance.now() - sinceMs;
  specLastStates = 0;
  specCars.clear();
  race = null;
  screen = "spectate";
}
// ---- quick chat: preset messages only (a number travels on the wire), shown as short-lived bubbles ----
const chatLog: { name: string; c: number; at: number; text?: string }[] = [];
const CHAT_ICONS = [
  ["01010", "01010", "00000", "10001", "01110"], // GG: smile
  ["00100", "11111", "01110", "01010", "10001"], // nice: star
  ["00100", "00100", "00100", "00000", "00100"], // oops: !
  ["01110", "10000", "10010", "10001", "01110"], // rematch: loop
  ["11100", "11110", "10000", "10000", "10000"], // ready: flag
  ["00100", "00010", "11111", "00010", "00100"], // go: arrow
  ["01010", "11111", "11111", "01110", "00100"], // thanks: heart
  ["01010", "01010", "00000", "01110", "10001"], // sorry: sad
];
const chatScreens: Screen[] = ["lobby", "race", "result", "standings", "spectate"];
const chatOn = () => online && !!net && chatScreens.includes(screen);
let lastChatSent = 0;
function sendChat(c: number) {
  if (!chatOn() || c < 0 || c >= CHAT_MSGS.length || performance.now() - lastChatSent < 1200) return;
  lastChatSent = performance.now();
  net?.send({ t: "chat", c });
  closeChat();
}
function closeChat() {
  document.body.classList.remove("chat-open");
}
/** Connection bars (and the ping, or LAG when the server went quiet) in the corner of the screen. */
function drawNetStatus(x: number, y: number, withMs: boolean) {
  if (!net) return;
  const q = net.quality;
  for (let i = 0; i < 3; i++) {
    const h = 2 + i * 2;
    ctx.fillStyle = i < q ? PAL[3] : PAL[1];
    ctx.fillRect(x + i * 3, y + 6 - h, 2, h);
  }
  if (q === 0) drawText(ctx, "LAG", x + 10, y + 1, PAL[3]);
  else if (withMs) drawText(ctx, String(Math.round(net.rttAvg)), x + 10, y + 1, PAL[2]);
}
function drawChat() {
  if (!chatOn()) return;
  const now = performance.now();
  const live = chatLog.filter((m) => now - m.at < (m.text ? 8000 : 5000)).slice(-3);
  // keep clear of each screen's own text: sky area while racing, under the player list in the lobby, above the footer on results
  const y0 = screen === "lobby" ? 96 : screen === "result" || screen === "standings" ? 100 : 2;
  live.forEach((m, i) => {
    const free = m.text !== undefined;
    const text = free ? (m.name ? `\u0001${m.name}: ${m.text}` : m.text!) : `\u0001${m.name}: ${chatWord(m.c)}`; // free text is drawn as is; a note from the server (no name) is translated
    const w = tr(text).length * 4 + (free ? 6 : 12);
    const y = y0 + i * 9;
    ctx.fillStyle = PAL[3];
    ctx.fillRect(2, y, Math.min(w, 156), 8);
    if (!free) CHAT_ICONS[m.c].forEach((row, ry) => [...row].forEach((px, rx) => px === "1" && ((ctx.fillStyle = PAL[0]), ctx.fillRect(4 + rx, y + 1 + ry, 1, 1))));
    drawText(ctx, text, free ? 5 : 11, y + 1, PAL[0]);
  });
  if (sayOpen) {
    // the line being typed
    const cur = Math.floor(now / 400) % 2 === 0 ? "_" : " ";
    ctx.fillStyle = PAL[3];
    ctx.fillRect(2, 98, 156, 11);
    ctx.fillStyle = PAL[0];
    ctx.fillRect(3, 99, 154, 9);
    drawText(ctx, "\u0001" + sayField.value.slice(-34) + cur, 5, 101, PAL[3]);
    drawText(ctx, coarse() ? "KEYBOARD: SEND   CLOSE: CANCEL" : "ENTER SEND   ESC CANCEL", 5, 111, PAL[2]);
  }
}
// ---- free-text chat (lobby, results): T opens a line, Enter sends it. The server decides who may write and what is refused ----
const sayField = document.createElement("input");
sayField.type = "text";
sayField.maxLength = 40;
sayField.enterKeyHint = "send";
sayField.autocomplete = "off";
sayField.autocapitalize = "characters";
sayField.spellcheck = false;
sayField.setAttribute("aria-label", "Chat message");
sayField.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px"; // 16 px: phones do not zoom in when it gets the focus
document.body.appendChild(sayField);
let sayOpen = false;
let lastSayAt = 0;
const sayScreens: Screen[] = ["lobby", "result", "standings"];
const sayAllowed = () => freeChatOn && chatOn() && sayScreens.includes(screen) && !!net;
function openSay() {
  sayOpen = true;
  sayField.value = "";
  sayField.focus({ preventScroll: true });
}
function closeSay() {
  sayOpen = false;
  sayField.value = "";
  sayField.blur();
}
addEventListener("keydown", (e) => {
  if (e.code === "KeyT" && !e.ctrlKey && !e.metaKey && !e.altKey && sayAllowed() && !sayOpen) (e.preventDefault(), e.stopImmediatePropagation(), openSay()); // preventDefault: the T itself must not be typed into the line
}, true);
sayField.addEventListener("input", () => {
  sayField.value = sayField.value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9:\-/.!?'%+ ]/g, "").replace(/ {2,}/g, " ").slice(0, 40);
});
sayField.addEventListener("keydown", (e) => {
  e.stopPropagation();
  if (e.key === "Enter") {
    const text = sayField.value.trim();
    if (text && performance.now() - lastSayAt > 800) (lastSayAt = performance.now()), net?.send({ t: "say", text });
    closeSay();
  } else if (e.key === "Escape") closeSay();
});
sayField.addEventListener("blur", () => setTimeout(() => sayOpen && document.activeElement !== sayField && closeSay(), 150)); // the keyboard was put away: the line closes
let chatLists: { fr: string[]; en: string[] } = CHAT_DEFAULTS;
/** The quick-chat word for a slot, in the player's language, drawn as is (the admin writes it already adapted). */
const chatWord = (i: number) => (chatLists[currentLang()][i] ?? CHAT_MSGS[i]);
const chatPanel = document.getElementById("chat");
const labelChat = () => chatPanel?.querySelectorAll<HTMLButtonElement>("button[data-chat]").forEach((b) => (b.textContent = chatLists[currentLang()][+b.dataset.chat!] ?? CHAT_MSGS[+b.dataset.chat!]));
if (chatPanel) {
  CHAT_MSGS.forEach((_t, i) => {
    const b = document.createElement("button");
    b.dataset.chat = String(i);
    b.addEventListener("click", () => sendChat(i));
    chatPanel.appendChild(b);
  });
  labelChat();
  const w = document.createElement("button"); // phones: the keyboard comes up from a tap (a field can only be focused from one)
  w.id = "saybtn";
  w.style.gridColumn = "1 / -1";
  w.style.display = "none";
  w.addEventListener("click", () => {
    if (!sayAllowed()) return;
    closeChat();
    openSay();
  });
  chatPanel.appendChild(w);
  const x = document.createElement("button");
  x.textContent = "CLOSE";
  x.style.gridColumn = "1 / -1";
  x.addEventListener("click", closeChat);
  chatPanel.appendChild(x);
}
const imIn = () => players.some((p) => p.id === myId);

function onNet(m: ServerMsg) {
  if (m.t === "chat") {
    chatLog.push({ name: m.name, c: m.c, at: performance.now() });
    if (chatLog.length > 20) chatLog.shift();
    return;
  }
  if (m.t === "say") {
    chatLog.push({ name: m.name, c: -1, text: m.text, at: performance.now() });
    if (chatLog.length > 20) chatLog.shift();
    return;
  }
  if (m.t === "saynote") {
    const note = { off: "CHAT IS OFF", runs: "NEED 5 RACES TO CHAT", muted: "YOU ARE MUTED", wait: "WAIT A FEW SECONDS", refused: "MESSAGE REFUSED", link: "NO LINKS PLEASE", empty: "EMPTY MESSAGE" }[m.why];
    chatLog.push({ name: "", c: -1, text: note, at: performance.now() });
    return;
  }
  if (m.t === "maintenance") return void setMaint(m.on, m.msg, true);
  if (m.t === "welcome") myId = m.id;
  else if (m.t === "error") {
    net?.close();
    if (m.msg === "no pilot") (setPilot(null), startClaim("PICK YOUR PILOT NAME"));
    else (netErr = m.msg.toUpperCase()), (screen = "title");
  }
  else if (m.t === "tracks") applyTracks(m);
  else if (m.t === "spectate" && streamMode) players = m.players; // a broadcast waits for the next start (it needs the seed)
  else if (m.t === "spectate") (specLocked = !!m.locked), enterSpectate(m.track, m.players, m.sinceStartMs, m.round);
  else if (m.t === "lobby") {
    players = m.players;
    lobbyRound = m.round ?? 0;
    lobbyTrack = m.track ?? SEASON[lobbyRound % SEASON.length];
    rematchIds = m.rematch ?? [];
    rematchTrack = m.last ?? -1;
    lobbyTourn = m.tourn ?? null;
    lobbyPick = m.pick ?? -1;
    lobbyHost = m.host;
    if (streamMode) {
      if (screen === "stream") streamPlayers = m.players;
    } else if (screen === "spectate" && imIn() && !m.inRace) (screen = "lobby"), (ready = false); // a seat opened up
  } else if (m.t === "start" && streamMode) {
    startStreamRace(m);
  } else if (m.t === "start" && !m.players.some((p) => p.id === myId)) {
    enterSpectate(m.track, m.players, 0, Math.max(0, SEASON.indexOf(m.track))); // seat-less: watch the next race
  }
  else if (m.t === "start") {
    players = m.players;
    race = new Race({ track: TRACKS[m.track] ?? TRACKS[0], machines: m.players.map((p) => p.machine), aiCount: m.ai, seed: m.seed, phys: livePhys() });
    // remap ids: human cars keep slot index = position in players; find mine
    const idx = m.players.findIndex((p) => p.id === myId);
    race.cars.forEach((c, i) => i < m.players.length && (c.id = i));
    mySlot = idx;
    slotOfId = new Map(m.players.map((p, i) => [p.id, i]));
    targets.clear();
    screen = "race";
  } else if (m.t === "states" && streamMode) {
    for (const w of m.cars) {
      const slot = slotOfId.get(w.id);
      if (slot === undefined || !race) continue;
      targets.set(slot, { pos: w.pos, speed: w.speed, lat: w.lat, jet: w.jet, at: performance.now() });
      const car = race.cars[slot];
      if (w.fin && !car.finished) (car.finished = true), (car.finishFrame = w.fin);
    }
  } else if (m.t === "states" && screen === "spectate") {
    for (const w of m.cars) specCars.set(w.id, w);
    specLastStates = performance.now();
  } else if (m.t === "states" && race) {
    for (const w of m.cars) {
      const slot = slotOfId.get(w.id);
      if (slot === undefined || slot === mySlot) continue;
      targets.set(slot, { pos: w.pos, speed: w.speed, lat: w.lat, jet: w.jet, at: performance.now() });
      const car = race.cars[slot];
      if (w.fin && !car.finished) (car.finished = true), (car.finishFrame = w.fin);
    }
  } else if (m.t === "result") {
    resultOrder = m.order;
    onlineStandings = m.standings ?? [];
    resultTrack = m.track ?? 0;
    friendlyResult = !!m.friendly;
    resultTourn = m.tourn ?? null;
    showFinal = false;
    tournFinal = m.tourn?.done ? { title: m.tourn.name.slice(0, 10) + " FINAL", top: (m.tourn.top ?? []).map((r) => ({ name: r.name, time: r.pts + " PTS", pts: 0, team: r.team })) } : null;
    rematchIds = [];
    podiumAt = tick;
    screen = "result";
    ready = false;
    specCars.clear();
    if (streamMode) (screen = "streamend"), (streamEndAt = tick), (streamPlayers = players);
  }
}
// ---- broadcast view (?stream=ROOM) ----
let streamPlayers: PlayerInfo[] = [];
let streamEndAt = 0;
let streamCam = 0;
let streamCamAt = 0;
function startStreamRace(m: Extract<ServerMsg, { t: "start" }>) {
  players = m.players;
  race = new Race({ track: TRACKS[m.track] ?? TRACKS[0], machines: m.players.map((p) => p.machine), aiCount: m.ai, seed: m.seed, phys: livePhys() });
  race.cars.forEach((c, i) => i < m.players.length && (c.id = i));
  slotOfId = new Map(m.players.map((p, i) => [p.id, i]));
  targets.clear();
  m.players.forEach((_, i) => targets.set(i, { pos: 0, speed: 0, lat: 144, jet: false, at: performance.now() }));
  streamCam = 0;
  streamCamAt = -999;
  screen = "streamrace";
}
/** Rank of the human cars: finished first (by time), then by distance. */
function streamOrder(r: Race): number[] {
  const idx = players.map((_, i) => i).filter((i) => r.cars[i]);
  return idx.sort((a, b) => {
    const ca = r.cars[a], cb = r.cars[b];
    if (ca.finished !== cb.finished) return ca.finished ? -1 : 1;
    if (ca.finished && cb.finished) return ca.finishFrame - cb.finishFrame;
    return cb.pos - ca.pos;
  });
}
function stepStream() {
  const r = race!;
  r.step(Array(r.cars.length).fill(NO_INPUT));
  const now = performance.now();
  for (const [slot, t] of targets) { // every human is remote: ease towards the last received state
    const car = r.cars[slot];
    const age = (now - t.at) / 1000;
    const predicted = t.pos + 5 * Math.floor(t.speed / 16) * 16 * (age * 59.7275 / 5.7158);
    car.pos += (predicted - car.pos) * 0.25;
    car.speed = t.speed;
    car.lat += (t.lat - car.lat) * 0.25;
    car.jetOn = t.jet;
  }
  const order = streamOrder(r);
  const named = streamFollow ? players.findIndex((p) => p.name.toUpperCase() === streamFollow) : -1;
  if (named >= 0) streamCam = named;
  else if (order.length && (tick - streamCamAt > 240 || r.cars[streamCam]?.finished)) { // the camera sticks to the leader (it changes at most every 4 s)
    if (order[0] !== streamCam) streamCam = order[0];
    streamCamAt = tick;
  }
  const cam = r.cars[streamCam] ?? r.cars[0];
  const names = new Map(players.map((p, i) => [i, p.name] as [number, string]));
  setAccents(r.cars.indexOf(cam));
  renderRace(ctx, { race: r, me: cam, mirror: true, names }, tick);
  if (r.phase === "countdown") (renderIntroBanner(ctx, r), renderCountdown(ctx, r));
  else renderIntroBanner(ctx, r); // slides away as the race starts
  // live board over the sky: top 5
  const rows = order.slice(0, 5);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(1, 1, 52, 3 + rows.length * 7);
  rows.forEach((slot, i) => drawText(ctx, `${i + 1} ${(players[slot]?.name ?? "").slice(0, 8)}`, 3, 3 + i * 7, slot === streamCam ? PAL[0] : PAL[1]));
}
// ---- admin replay: a stored run played again locally (same deterministic physics as the server's check) ----
interface ReviewRun { board: string; name: string; frames: number; machine: number; dev?: number; track: number; def?: TrackDef; seed: number; ai: number; skill: number; vclass: number; phys?: number; rle: [number, number][] }
interface Review { run: ReviewRun; masks: Uint8Array; race: Race; idx: number; speed: number; paused: boolean; endAt: number; label: string; flags: string[] }
let review: Review | null = null;
let reviewErr = "";
const reviewRace = (r: ReviewRun) => new Race({ track: r.def ?? TRACKS[r.track], machines: [r.machine], aiCount: r.ai, seed: r.seed, skill: r.skill, vclass: r.vclass, phys: physOf(r.phys) });
function reviewSeek(to: number) {
  if (!review) return;
  const idx = Math.max(0, Math.min(review.masks.length, to));
  if (idx < review.idx) (review.race = reviewRace(review.run)), (review.idx = 0); // going back means playing again from the start (it is deterministic, and quick)
  while (review.idx < idx) review.race.step([inputOf(review.masks[review.idx++])]);
  review.endAt = -1;
}
async function startReview() {
  try {
    await loadTracks();
    const r = await fetch(api("/api/admin/runs?board=" + encodeURIComponent(reviewParam ?? "") + "&name=" + encodeURIComponent(reviewPilot)), { headers: { Authorization: "Bearer " + reviewToken }, cache: "no-store" });
    if (!r.ok) throw new Error(r.status === 401 || r.status === 403 ? "NOT ALLOWED" : "RUN NOT FOUND");
    const j = await r.json();
    const run = j.run as ReviewRun;
    if (!run?.rle?.length || !(run.def ?? TRACKS[run.track])) throw new Error("UNKNOWN CIRCUIT");
    const masks = new Uint8Array(run.rle.reduce((a, p) => a + p[1], 0));
    let i = 0;
    for (const [m, n] of run.rle) for (let k = 0; k < n; k++) masks[i++] = m;
    review = { run, masks, race: reviewRace(run), idx: 0, speed: 1, paused: false, endAt: -1, label: String(j.label ?? ""), flags: (j.flags ?? []).filter((f: { level: string }) => f.level !== "info").map((f: { level: string }) => f.level) };
    screen = "review";
  } catch (e) {
    reviewErr = String((e as Error).message || e).toUpperCase().slice(0, 28);
  }
}
function reviewKey(e: KeyboardEvent) {
  if (!review) return;
  const k = e.code;
  if (k === "Space") review.paused = !review.paused;
  else if (k === "Digit1") review.speed = 1;
  else if (k === "Digit2") review.speed = 2;
  else if (k === "Digit3") review.speed = 4;
  else if (k === "Digit4") review.speed = 0.5;
  else if (k === "ArrowLeft") reviewSeek(review.idx - 300);
  else if (k === "ArrowRight") reviewSeek(review.idx + 300);
  else if (k === "KeyR" || k === "Home") (reviewSeek(0), (review.paused = false));
  else return;
  e.preventDefault();
}
let reviewAcc = 0;
function stepReview() {
  const rv = review!;
  const r = rv.race, me = r.cars[0];
  if (!rv.paused && rv.idx < rv.masks.length && !(me.finished && rv.endAt >= 0 && tick - rv.endAt > 120)) {
    reviewAcc += rv.speed;
    while (reviewAcc >= 1) {
      reviewAcc -= 1;
      if (rv.idx < rv.masks.length && !me.finished) r.step([inputOf(rv.masks[rv.idx++])]);
      else if (me.finished) r.step([NO_INPUT]); // the grid carries on for a moment after the finish line
    }
    if (me.finished && rv.endAt < 0) rv.endAt = tick;
  }
  accents.on = false;
  renderRace(ctx, { race: r, me, mirror: true, names: new Map([[0, rv.run.name]]) }, tick);
  if (r.phase === "countdown") (renderIntroBanner(ctx, r), renderCountdown(ctx, r));
  // what the pilot pressed, as little keys in the sky: left, right, brake, throttle, jet
  const m = rv.masks[Math.max(0, rv.idx - 1)] ?? 0;
  const keys: [string, number][] = [["<", 8], [">", 16], ["B", 2], ["A", 1], ["J", 4]];
  ctx.fillStyle = PAL[3];
  ctx.fillRect(104, 1, 55, 9);
  keys.forEach(([label, bit], i) => {
    const on = (m & bit) !== 0;
    ctx.fillStyle = on ? PAL[0] : PAL[2];
    ctx.fillRect(106 + i * 10, 2, 8, 7);
    drawText(ctx, "\u0001" + label, 108 + i * 10, 3, on ? PAL[3] : PAL[1]);
  });
  // who, how long, and the progress of the replay
  ctx.fillStyle = PAL[3];
  ctx.fillRect(1, 1, 98, 9);
  drawText(ctx, `${rv.run.name} ${formatTime(rv.run.frames)}`, 3, 3, PAL[0]);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, SCREEN_H - 3, SCREEN_W, 3);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(0, SCREEN_H - 2, Math.round((rv.idx / Math.max(1, rv.masks.length)) * SCREEN_W), 1);
  const note = rv.paused ? "PAUSED" : me.finished && rv.endAt >= 0 && tick - rv.endAt > 120 ? "END - R: AGAIN" : rv.speed === 1 ? "" : "X" + rv.speed;
  if (note) (ctx.fillStyle = PAL[3], ctx.fillRect(1, 11, note.length * 4 + 4, 9), drawText(ctx, note, 3, 13, PAL[0]));
}
function drawReviewWait() {
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, SCREEN_H);
  drawTextC(ctx, "REPLAY", 80, 50, PAL[0], 2);
  drawTextC(ctx, reviewErr || "LOADING RUN...", 80, 80, reviewErr ? PAL[1] : PAL[2]);
}
function drawStreamWait() {
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, SCREEN_H);
  drawTextC(ctx, "FORMULE RACE", 80, 24, PAL[0], 2);
  drawTextC(ctx, lobbyTourn ? lobbyTourn.name.slice(0, 20) : "ROOM " + room, 80, 46, PAL[1]);
  streamPlayers.slice(0, 8).forEach((p, i) => drawTextC(ctx, p.name.slice(0, 10), 80, 62 + i * 8, PAL[2]));
  if (Math.floor(tick / 30) % 2 === 0) drawTextC(ctx, online ? "WAITING FOR THE RACE" : "CONNECTING", 80, 132, PAL[0]);
}
function drawStreamEnd() {
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, SCREEN_H);
  drawTextC(ctx, "RESULT", 80, 8, PAL[0], 2);
  const humans = resultOrder.filter((o) => players[o.id]);
  humans.slice(0, 8).forEach((o, i) => {
    drawText(ctx, `${i + 1}`, 14, 30 + i * 12, PAL[1]);
    drawText(ctx, (players[o.id]?.name ?? "").slice(0, 10), 26, 30 + i * 12, PAL[0]);
    drawText(ctx, formatTime(o.frame), 96, 30 + i * 12, PAL[2]);
  });
  if (tick - streamEndAt > 900) screen = "stream"; // after 15 s: back to the waiting screen
}
let mySlot = 0;
let slotOfId = new Map<number, number>();

for (const ev of ["pointerup", "touchend", "click", "keydown", "mouseup"]) addEventListener(ev, () => sfx.unlock(), { passive: true });
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) sfx.unlock();
});
/** Rear-view mirror (R key), remembered between visits. */
let mirrorOn = true;
try {
  mirrorOn = localStorage.getItem("f1mirror") !== "0";
} catch {
  /* storage unavailable */
}
const setMirror = (on: boolean) => {
  mirrorOn = on;
  try {
    localStorage.setItem("f1mirror", on ? "1" : "0");
  } catch {
    /* storage unavailable */
  }
};
const handlePress = (code: string) => {
  sfx.unlock();
  if (code === "btn:back") code = "Escape";
  if (code === "btn:snd") return void sfx.toggleMute();
  if (code === "btn:chat") {
    const wb = document.getElementById("saybtn");
    if (wb) ((wb.style.display = sayAllowed() ? "" : "none"), (wb.textContent = currentLang() === "fr" ? "ECRIRE UN MESSAGE" : "WRITE A MESSAGE"));
    return void (chatOn() ? document.body.classList.toggle("chat-open") : closeChat());
  }
  if (/^Digit[1-8]$/.test(code) && chatOn()) return void sendChat(Number(code.slice(5)) - 1);
  if (code === "KeyM") sfx.toggleMute();
  if (code === "KeyN") sfx.toggleMusic();
  if (code === "KeyR") setMirror(!mirrorOn);
  const confirm = code === "Enter" || code === "Space" || code === "KeyZ" || code === "btn:accel" || code === "btn:start";
  const up = code === "ArrowUp" || code === "btn:up", down = code === "ArrowDown" || code === "btn:down";
  const left = code === "ArrowLeft" || code === "btn:left", right = code === "ArrowRight" || code === "btn:right";
  if (screen === "title") {
    fixMenuIdx();
    const pos = menuOrder().indexOf(menuIdx);
    if (up) menuIdx = menuOrder()[(pos + menuOrder().length - 1) % menuOrder().length];
    if (down) menuIdx = menuOrder()[(pos + 1) % menuOrder().length];
    if (confirm) {
      if (pilotChecked && !pilotName && menuIdx !== 3 && menuIdx !== 8 && menuIdx !== 10 && menuIdx !== 12 && menuIdx !== 13) startClaim("PICK A PILOT NAME FIRST");
      else if (menuIdx === 8) (screen = "options"), (optRow = 0);
      else if (menuIdx === 10) openPopular();
      else if (menuIdx === 12) openQual();
      else if (menuIdx === 13) openTeam();
      else if (menuIdx === 11) (screen = "solo"), (soloRow = 0), (rally = false);
      else if (menuIdx === 3) enterName();
      else if (menuIdx === 9 && weeklyInfo) {
        screen = "select";
        selRow = 1;
        if (weeklyInfo.machine >= 0) machine = weeklyInfo.machine; // the week imposes the machine
        loadBoard("weekly");
      } else if (menuIdx === 4) {
        screen = "records";
        if (!SEASON.includes(recTrack)) recTrack = SEASON[0];
        loadRecBoard();
      } else if (menuIdx === 2) enterOnline();
      else {
        screen = "select";
        selRow = menuIdx === 5 ? 2 : 1;
        if (menuIdx === 6) loadBoard("daily");
      }
    }
  } else if (screen === "room") {
    if (code === "Escape") (roomField.blur(), (screen = "title"));
    if (up || down) roomRow = (roomRow + (up ? 2 : 1)) % 3;
    if (roomRow === 1 && (left || right)) roomCode = newCode();
    if (confirm) {
      if (roomRow === 0) ((room = "PUBLIC"), (screen = "select"), (selRow = 1));
      else if (roomRow === 1) ((room = roomCode), (screen = "select"), (selRow = 1));
      else if (joinDraft.length >= 3 && document.activeElement !== roomField) commitJoin();
      else (roomField.value = joinDraft), roomField.focus({ preventScroll: true });
    }
  } else if (screen === "options") {
    if (up) optRow = (optRow + optRows() - 1) % optRows();
    if (down) optRow = (optRow + 1) % optRows();
    if (left || right) optStep(optRow, left ? -1 : 1, false);
    else if (confirm && (optRow === 1 || optRow === 2 || optRow === 6 || optRow === 7 || optRow === 8 || optRow === 9 || optRow === 10 || optRow === 11)) optStep(optRow, 1, true);
    if (confirm && optRow === 12) openPadMap();
    if (confirm && installAvail() && optRow === 13) installApp();
    if ((confirm && optRow === optRows() - 1) || code === "Escape") screen = "title";
  } else if (screen === "padmap") {
    padMapPress(code);
  } else if (screen === "spectate") {
    if (code === "Escape") (net?.close(), (screen = "title"));
  } else if (screen === "records") {
    if (left || right) {
      if (recCat === 5) champPeriod = champPeriod === "month" ? "all" : "month";
      else if (recCat === 7) teamPeriod = teamPeriod === "month" ? "all" : "month";
      else if (recCat !== 3 && recCat !== 4) recTrack = cycleIn([...SEASON, ...stageList()], recTrack, left);
      loadRecBoard();
    }
    if (up || down) {
      const tabs = recTabs(), at = Math.max(0, tabs.indexOf(recCat));
      recCat = tabs[(at + (up ? tabs.length - 1 : 1)) % tabs.length];
      loadRecBoard();
    }
    if (confirm || code === "Escape") screen = "title";
  } else if (screen === "name") {
    if (confirm) commitName();
    if (code === "Escape" || code === "btn:back") nameBack();
    if (up || down) toggleNameMode();
  } else if (screen === "licence") {
    if (licRemove) {
      if (confirm) void removeEmail();
      else if (code === "Escape" || code === "btn:back" || up || down || left || right) licRemove = false;
    } else if (left || right) licenceSwitch();
    else if (confirm) void (licPage === 0 ? copyLicence() : shareBadge());
    else if (down && licPage === 0 && emailOn) emailAction();
    else if (code === "Escape" || up || down) screen = "title";
  } else if (screen === "select") {
    const rows = menuIdx === 0 ? [0, 1] : menuIdx === 1 ? [0, 1, 2] : menuIdx === 5 ? [3, 1, 2] : menuIdx === 6 ? [1, 3] : [1];
    if (!rows.includes(selRow)) selRow = 1;
    if (up || down) selRow = rows[(rows.indexOf(selRow) + (up ? rows.length - 1 : 1)) % rows.length];
    if (left || right) {
      if (selRow === 3 && menuIdx === 6) {
        dailyGhostOn = !dailyGhostOn;
        try {
          localStorage.setItem("f1dghost", dailyGhostOn ? "1" : "0");
        } catch {
          /* ignore */
        }
      } else if (selRow === 1) {
        if (!(menuIdx === 9 && weeklyInfo && weeklyInfo.machine >= 0)) machine = 1 - machine; // an imposed machine cannot be changed
      }
      else if (selRow === 0) {
        skillIdx = (skillIdx + (left ? SKILLS.length - 1 : 1)) % SKILLS.length;
        try {
          localStorage.setItem("f1skill", String(skillIdx));
        } catch {
          /* ignore */
        }
      } else if (selRow === 3) ghostSrc = (ghostSrc + (left ? GHOST_SRC.length - 1 : 1)) % GHOST_SRC.length;
      else trackIdx = rally ? cycleIn(stageList(), trackIdx, left) : cycleSeason(trackIdx, left);
    }
    if (confirm) {
      if (menuIdx === 0) (runMode = "season"), (round = 0), (seasonPts = []), startSolo(SEASON[0]);
      else if (menuIdx === 1) (runMode = "quick"), startSolo(trackIdx);
      else if (menuIdx === 5) void startTrial();
      else if (menuIdx === 9 && weeklyInfo) (runMode = "weekly"), startSolo(weeklyInfo.track);
      else if (menuIdx === 6) {
        runMode = "daily";
        startSolo(dailyInfo.track);
        if (dailyGhostOn && dailyGhost && dailyGhost.day === dailyInfo.day) makeGhost(dailyInfo.track, dailyGhost, { seed: dailyInfo.seed, skill: dailyInfo.skill, vclass: dailyInfo.vclass, ai: dailyInfo.ai });
      }
      else void goOnline();
    }
    if (code === "Escape") screen = menuIdx === 2 && !urlRoom ? "room" : SOLO_IDS.includes(menuIdx) ? "solo" : "title";
  } else if (screen === "lobby") {
    if ((up || down || code === "KeyV") && rematchTrack >= 0) net?.send({ t: "rematch", on: !rematchIds.includes(myId) });
    if ((left || right) && !lobbyTourn && room !== "PUBLIC" && lobbyHost === myId) {
      // host: cycles season order -> each circuit of the season
      const opts = [-1, ...SEASON];
      const at = Math.max(0, opts.indexOf(lobbyPick));
      net?.send({ t: "pick", track: opts[(at + (right ? 1 : -1) + opts.length) % opts.length] });
    }
    if (confirm) {
      ready = !ready; // the machine was chosen on the select screen
      net?.send({ t: "ready", ready, machine });
    }
    if (code === "KeyC" && room !== "PUBLIC") void shareInvite();
    if (code === "Escape") (net?.close(), (screen = "title"));
  } else if (screen === "result") {
    if (confirm && podiumActive()) podiumAt = -1; // first press skips the podium, the second leaves
    else if ((code === "KeyL" || code === "KeyK") && canVote()) void voteShared(code === "KeyL" ? 1 : -1);
    else if (code === "KeyC" && !online && runMode === "tt" && !activeQual) void (activeShared ? shareCircuit() : challengeFriend());
    else if (confirm && !online && activeQual) openQual(); // retry or leave from the qualifier screen
    else if (confirm && !online && activeShared) (sharedC = activeShared), (screen = "circuit"); // retry or leave from the circuit screen
    else if (confirm && !online && activeChallenge) (challenge = activeChallenge), (screen = "challenge"); // retry or leave from the challenge screen
    else if (confirm && online && tournFinal && !showFinal) (showFinal = true), (podiumAt = tick); // after the last round: the tournament's podium
    else if (confirm) {
      if (online) screen = imIn() ? "lobby" : "spectate";
      else if (menuIdx === 0) {
        screen = "standings";
        if (round + 1 >= SEASON.length) startSeasonFinalPodium();
      } else screen = "select";
    }
  } else if (screen === "challenge") {
    if (left || right) machine = 1 - machine;
    if (confirm) acceptChallenge();
    if (code === "Escape") screen = "title";
  } else if (screen === "teamcode") {
    if (confirm) void submitTeamCode();
    if (code === "Escape") closeTeamCode();
  } else if (screen === "team") {
    if (teamLoaded === "ok" && myTeam && !myTeam.team) {
      if (up || down) teamSel = 1 - teamSel;
      if (confirm) teamSel === 1 ? openTeamCode() : teamCreateGo();
    } else if (confirm) openTeamPage();
    if ((left || right) && myTeam?.team) setTeamColor(myTeam.team.color + (left ? -1 : 1));
    if (code === "KeyC") copyTeamInvite();
    if (code === "KeyL") leaveTeam();
    else if (code !== "KeyL") teamLeaveAsk = false;
    if (code === "Escape") screen = "title";
  } else if (screen === "teaminvite") {
    if (confirm) void acceptTeamInvite();
    if (code === "Escape") screen = inviteBack;
  } else if (screen === "qual") {
    if ((left || right) && qualD && typeof qualD === "object" && qualD.info.machine < 0) machine = 1 - machine;
    if (confirm) acceptQual();
    if (code === "Escape") screen = "title";
  } else if (screen === "solo") {
    const n = soloRows();
    if (up) soloRow = (soloRow + n - 1) % n;
    if (down) soloRow = (soloRow + 1) % n;
    if (confirm) {
      rally = soloRow === 3;
      menuIdx = rally ? 5 : SOLO_IDS[soloRow];
      if (rally) {
        if (!TRACKS[trackIdx]?.stage) trackIdx = stageList()[0];
      } else if (TRACKS[trackIdx]?.stage) trackIdx = SEASON[0];
      screen = "select";
      selRow = menuIdx === 5 ? 2 : 1;
    }
    if (code === "Escape") screen = "title";
  } else if (screen === "popular") {
    const n = Array.isArray(popular) ? popular.length : 0;
    if (up) popRow = (popRow + n) % (n + 1);
    if (down) popRow = (popRow + 1) % (n + 1);
    if (left || right) (comSort = (comSort + (right ? 1 : 3)) % 4), (popRow = 0), loadCommunity();
    if (confirm) {
      if (popRow === 0) openCircuitCode();
      else if (Array.isArray(popular) && popular[popRow - 1]) void loadShared(popular[popRow - 1].code);
    }
    if (code === "Escape") screen = "title";
  } else if (screen === "circuit") {
    if (left || right) machine = 1 - machine;
    if (confirm) acceptShared();
    if (code === "Escape") screen = "title";
  } else if (screen === "standings") {
    if (confirm && podiumActive()) podiumAt = -1;
    else if (confirm) {
      if (round + 1 < SEASON.length) (round++), startSolo(SEASON[round]);
      else screen = "title";
    }
  } else if (screen === "race" && code === "Escape") {
    net?.close();
    screen = "title";
    race = null;
  }
};
bindInput(handlePress);

function frame() {
  tick++;
  if (copied > 0) copied--;
  if (installHelp > 0) installHelp--;
  ctx.fillStyle = PAL[0];
  ctx.fillRect(0, 0, SCREEN_W, SCREEN_H);
  if (screen === "title") drawTitle();
  else if (screen === "name") (placeField(), drawName());
  else if (screen === "licence") drawLicence();
  else if (screen === "room") drawRoom();
  else if (screen === "records") drawRecords();
  else if (screen === "popular") drawPopular();
  else if (screen === "solo") drawSolo();
  else if (screen === "qual") drawQual();
  else if (screen === "team") drawTeam();
  else if (screen === "teaminvite") drawTeamInvite();
  else if (screen === "teamcode") drawTeamCode();
  else if (screen === "options") drawOptions();
  else if (screen === "padmap") drawPadMap();
  else if (screen === "spectate") drawSpectate();
  else if (screen === "select") drawSelect();
  else if (screen === "lobby") drawLobby();
  else if (screen === "race" && race) stepRace();
  else if (screen === "streamrace" && race) stepStream();
  else if (reviewMode && screen === "review" && review) stepReview();
  else if (reviewMode) drawReviewWait();
  else if (screen === "stream") drawStreamWait();
  else if (screen === "streamend") drawStreamEnd();
  else if (screen === "result") drawResult();
  else if (screen === "standings") drawStandings();
  else if (screen === "challenge") drawChallenge();
  else if (screen === "circuit") drawCircuit();
  drawChat();
  if (maint.on && (screen === "lobby" || screen === "spectate" || screen === "result" || screen === "standings") && performance.now() - maintToastAt > 60_000) maintToastAt = performance.now(); // gentle reminder every minute
  if (screen === "race") badgeDirty = true;
  else if (badgeDirty && performance.now() - badgeChecked > 15_000) void checkBadges(); // back from a race: look for a new badge
  if (screen !== "race" || race?.phase !== "racing") (drawMaintToast(), drawNoticeToast(), drawBadgeToast());
  {
    const note = padInfo.since && padInfo.name && performance.now() - padInfo.since < 4000 ? "GAMEPAD READY" : screen === "title" && padConnected() && sfx.state !== "running" ? "PRESS A KEY FOR SOUND" : "";
    if (note) {
      ctx.fillStyle = PAL[3];
      ctx.fillRect(2, 19, tr(note).length * 4 + 4, 9);
      drawText(ctx, note, 4, 21, PAL[0]);
    }
  }
  const racing = screen === "race" && !!race && race.phase === "racing" && !race.cars[mySlot]?.finished;
  const RACE_MUSIC_DELAY = 270; // frames of engine noise only after the lights go out (4.5 s) before the race tune comes in
  const raceTune = racing && race!.raceFrame >= RACE_MUSIC_DELAY;
  const gridLights = screen === "race" && !!race && race.phase === "countdown"; // the fanfare plays while the starting lights count down
  const podiumTune = (screen === "result" || screen === "standings") && podiumActive();
  const want: "title" | "intro" | "victory" | number | null = podiumTune ? "victory" : raceTune ? race!.track.id - 1 : racing ? null : gridLights ? "intro" : screen === "title" || screen === "options" || screen === "padmap" || screen === "records" || screen === "popular" || screen === "solo" || screen === "qual" || screen === "team" || screen === "teaminvite" || screen === "teamcode" || screen === "name" || screen === "licence" || screen === "room" || screen === "select" || screen === "lobby" ? "title" : null;
  if (want === null) sfx.stopMusic();
  else sfx.startMusic(want);
  {
    const m = race?.cars[mySlot];
    const sliding = !!m && screen === "race" && race!.phase === "racing" && !m.finished && Math.abs(m.yaw) === 2 && m.speed > 150 && !m.inWall;
    sfx.skid(sliding, m?.speed ?? 0);
    if (race && m && screen === "race") {
      skidTrace(race, race.track.lapUnits, m.id, 0, m.pos, m.lat, sliding && options.skids > 0);
      for (const c of race.cars) if (c !== m) skidTrace(race, race.track.lapUnits, c.id, 1, c.pos, c.lat, options.skids >= 2 && (options.skids === 3 || !c.isAI) && race.phase === "racing" && !c.finished && Math.abs(c.yaw) === 2 && c.speed > 150 && !c.inWall);
      const gc = ghost?.race.cars[0]; // the ghost's own slides, dotted and faint
      if (gc) skidTrace(race, race.track.lapUnits, -100, 2, gc.pos, gc.lat, options.skids >= 2 && race.phase === "racing" && !gc.finished && Math.abs(gc.yaw) === 2 && gc.speed > 150 && !gc.inWall);
    }
    if (sliding) rumble(60, 0.08, 0.25); // a light buzz on pads while the tyres slide
    // rumble strips: the car runs along the kerb (about 42 units from the centre, the barrier being at 57)
    const onKerb = !!m && screen === "race" && race!.phase === "racing" && !m.finished && !m.inWall && m.speed > 40 && Math.abs(m.lat - LAT_CENTER) > KERB_LAT;
    sfx.kerb(onKerb, m?.speed ?? 0);
    if (onKerb) rumble(60, 0.1, 0.2);
  }
  sfx.engine(race?.cars[mySlot]?.speed ?? 0, screen === "race" && race?.phase === "racing", !!race?.cars[mySlot]?.jetOn, screen === "race" && readInput().brake);
  if (document.body.dataset.screen !== screen) (document.body.dataset.screen = screen), closeChat(), clearZones();
  const on = chatOn() ? "1" : "0";
  if (document.body.dataset.online !== on) document.body.dataset.online = on;
  (window as any).__state = { input: readInput(), maint, chat: chatLog.length, runMode, ghost: !!ghost, daily: dailyInfo, recCat, room, roomRow, roomCode, nPlayers: players.length, menuIdx, selRow, skillIdx, raceSkill, audio: sfx.state, skid: sfx.skidLevel, kerb: sfx.kerbLevel, yaw: race?.cars[mySlot]?.yaw, music: sfx.musicKind, screen, frame: race?.frame, pos: race?.cars[mySlot]?.pos, lat: race?.cars[mySlot]?.lat, fin: race?.cars[mySlot]?.finished, speed: race?.cars[mySlot]?.speed, phase: race?.phase };
}

// ---- title screen: a live demo lap (the real renderer, a CPU-driven car) above a compact scrolling menu ----
let demo: Race | null = null;
let demoLeft = 0;
let demoDriver = new DemoDriver();
let news: string[] = []; // community messages scrolled under the title demo (admin panel)
let demoSkill = 2; // CPU level of the title-screen demo; the server can override it (DEMO_SKILL in .env)
/** Public lobby census for the main menu (counts only), polled while the menu is up. */
let lobbyInfo: { waiting: number; racing: number; evening?: { phase: "soon" | "open"; hour: number } } = { waiting: 0, racing: 0 };
let lobbyPollAt = -1e9;
function pollLobby(now: number) {
  if ((screen !== "title" && screen !== "select") || document.hidden || now - lobbyPollAt < 15000) return;
  lobbyPollAt = now;
  fetch(api("/api/lobby"))
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d) {
        const ev = d.evening;
        const hour = Number(ev?.hour);
        lobbyInfo = { waiting: Math.max(0, Number(d.waiting) | 0), racing: Math.max(0, Number(d.racing) | 0), evening: (ev?.phase === "soon" || ev?.phase === "open") && hour >= 0 && hour <= 23 ? { phase: ev.phase, hour } : undefined };
      }
    })
    .catch(() => {});
}
function loadConfig() {
  fetch(api("/api/config"))
    .then((r) => (r.ok ? r.json() : null))
    .then((c) => {
      if (!c) return;
      if (Number.isInteger(c.demoSkill) && c.demoSkill >= 0 && c.demoSkill <= 3) demoSkill = c.demoSkill;
      const don = document.getElementById("donate") as HTMLAnchorElement | null;
      if (don) {
        const ok = typeof c.donate === "string" && /^https:\/\/(www\.)?(paypal\.com|paypal\.me)\//.test(c.donate);
        don.hidden = !ok;
        if (ok) don.href = c.donate;
      }
      popularOn = c.features?.popularCircuits === true;
      lappedCarsOn = c.features?.lappedCars !== false;
      seasonTheme = typeof c.features?.theme === "string" ? c.features.theme : "off";
      macroWarnOn = c.features?.macroWarning === true;
      freeChatOn = c.features?.freeChat === true;
      emailOn = c.emailLogin === true;
      qualInfo = validQual(c.qualifier) ? c.qualifier : null;
      if (!qualInfo && menuIdx === 12) menuIdx = 6;
      if (!popularOn && menuIdx === 10) menuIdx = 6;
      if (c.chat && Array.isArray(c.chat.fr) && Array.isArray(c.chat.en) && c.chat.fr.length === 8 && c.chat.en.length === 8) {
        chatLists = { fr: c.chat.fr.map(String), en: c.chat.en.map(String) };
        labelChat();
      }
      news = Array.isArray(c.announcements) ? c.announcements.filter((x: unknown) => typeof x === "string" && x).slice(0, 10) : [];
      if (c.maintenance && typeof c.maintenance.on === "boolean") setMaint(c.maintenance.on, String(c.maintenance.message ?? ""), false);
    })
    .catch(() => {});
}
loadConfig();
setInterval(loadConfig, 30_000); // picks up a maintenance switch within half a minute, even for solo players
let seasonTheme = "off"; // set by the admin (title-screen decorations), refreshed with the config
let demoTrack = SEASON[Math.floor(Math.random() * SEASON.length)];
function startDemo() {
  demoTrack = cycleSeason(demoTrack, false);
  demo = new Race({ track: TRACKS[demoTrack], machines: [demoTrack % 2], aiCount: 7, seed: (Math.random() * 2 ** 32) >>> 0, skill: demoSkill, phys: livePhys() });
  while (demo.phase === "countdown") demo.step([NO_INPUT]); // skip the lights: straight into the action
  demoDriver = new DemoDriver();
  demoLeft = 60 * 15;
}
function drawTitle() {
  fixMenuIdx();
  if (!demo || --demoLeft <= 0) startDemo();
  const d = demo!;
  const me = d.cars[0];
  d.step([demoDriver.input(d)]);
  ensureMyTeam();
  accents.on = colorsActive();
  accents.carColors.clear();
  if (accents.on && myTeam?.team) accents.carColors.set(-1, myTeam.team.color);
  renderRace(ctx, { race: d, me }, tick);
  const theme = resolveTheme(seasonTheme);
  if (theme) drawThemeEffect(ctx, theme, tick, colorsActive());
  // slim logo plate in the sky: the scenery behind stays visible
  ctx.fillStyle = PAL[3];
  ctx.fillRect(18, 2, 124, 15);
  ctx.fillStyle = PAL[1];
  ctx.fillRect(18, 17, 124, 1);
  drawTextC(ctx, "FORMULE", 65, 5, PAL[0], 2);
  drawTextC(ctx, "\u0001RACE", 117, 5, PAL[1], 2); // the logo stays as it is
  if (theme) drawThemeLogo(ctx, theme, colorsActive());
  drawText(ctx, TRACKS[demoTrack].name, 156 - tr(TRACKS[demoTrack].name).length * 4, 21, PAL[3]);
  // menu panel (replaces the HUD area), 5 rows scrolling
  ctx.fillStyle = PAL[0];
  ctx.fillRect(0, 88, SCREEN_W, 56);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 88, SCREEN_W, 1);
  const labels: Record<number, string> = { 0: "SOLO SEASON" + (seasonClass ? " - " + vclassOf(seasonClass).name : ""), 1: "QUICK RACE", 5: "TIME TRIAL", 9: weeklyInfo ? "WEEKLY: " + weeklyInfo.title : "WEEKLY", 6: dailyInfo.vclass ? "DAILY - " + vclassOf(dailyInfo.vclass).name : "DAILY CHALLENGE", 2: "ONLINE" + (urlRoom ? ": " + urlRoom : lobbyInfo.waiting ? " - " + (lobbyInfo.evening ? lobbyInfo.evening.hour + "H - " : "") + lobbyInfo.waiting + " WAITING" : lobbyInfo.racing ? " - RACE ON" : lobbyInfo.evening ? " - " + lobbyInfo.evening.hour + "H" : ""), 4: "RECORDS", 3: "DRIVER: " + (pilotName ?? "REGISTER"), 8: "OPTIONS", 10: "COMMUNITY", 11: "SOLO", 13: myTeam?.team ? "TEAM [" + myTeam.team.tag + "]" : "TEAM", 12: qualInfo ? "TOURNAMENT" + (qualInfo.phase === "open" ? " - OPEN" : qualInfo.phase === "upcoming" ? " - SOON" : "") : "TOURNAMENT" };
  const VIS = 5;
  const pos = menuOrder().indexOf(menuIdx);
  const first = Math.max(0, Math.min(menuOrder().length - VIS, pos - 2));
  for (let r = 0; r < VIS; r++) {
    const id = menuOrder()[first + r];
    const y = 91 + r * 9;
    const sel = id === menuIdx;
    if (sel) {
      ctx.fillStyle = PAL[3];
      ctx.fillRect(8, y - 1, 138, 9);
    }
    drawText(ctx, (sel ? "> " : "  ") + labels[id], 12, y + 1, sel ? PAL[0] : PAL[3]);
  }
  // community news: a ticker across the bottom of the demo picture
  const eveLine = lobbyInfo.evening ? (lobbyInfo.evening.phase === "open" ? "PUBLIC RACE NOW - GO ONLINE" : "PUBLIC RACE AT " + lobbyInfo.evening.hour + "H - ONLINE") : "";
  // the tournament qualifier is open: the menu list scrolls (5 rows), so a line on the ticker keeps it in view whatever row is selected
  const qualLine = qualInfo?.phase === "open" ? "TOURNAMENT OPEN - " + qualInfo.name : "";
  let tipLine = "";
  if (installAvail() && !tipSeen()) {
    tipLine = "INSTALL THE GAME: OPTIONS - INSTALL APP";
    if (++installTipFrames > 1800) tipDone(); // ~30 s of menu time, then never again
  }
  const ticker = [...(eveLine ? [eveLine] : []), ...(qualLine ? [qualLine] : []), ...(tipLine ? [tipLine] : []), ...news];
  if (ticker.length) {
    ctx.fillStyle = PAL[3];
    ctx.fillRect(0, 79, SCREEN_W, 9);
    ctx.fillStyle = PAL[1];
    ctx.fillRect(0, 79, 24, 9);
    drawText(ctx, "NEWS", 4, 82, PAL[3]);
    ctx.save();
    ctx.beginPath();
    ctx.rect(25, 79, SCREEN_W - 25, 9);
    ctx.clip();
    const txt = ticker.join("   +   ");
    const span = tr(txt).length * 4 + 40;
    drawText(ctx, txt, SCREEN_W - (Math.floor(tick / 2) % (span + SCREEN_W - 25)), 82, PAL[0]);
    ctx.restore();
  }
  // scroll hints
  ctx.fillStyle = PAL[2];
  if (first > 0) ctx.fillRect(150, 92, 5, 1), ctx.fillRect(151, 91, 3, 1), ctx.fillRect(152, 90, 1, 1);
  if (first + VIS < menuOrder().length) ctx.fillRect(150, 134, 5, 1), ctx.fillRect(151, 135, 3, 1), ctx.fillRect(152, 136, 1, 1);
  if (maint.on) {
    // scrolling maintenance bar instead of the hint line
    ctx.fillStyle = PAL[1]; // mid-light: stands out from both the game's dark frame and the menu panel
    ctx.fillRect(0, 135, SCREEN_W, 9);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 135, SCREEN_W, 9);
    ctx.clip();
    const txt = "MAINTENANCE: " + maint.message;
    const period = tr(txt).length * 4 + 40;
    const x0 = SCREEN_W - (Math.floor(tick / 2) % (period + SCREEN_W));
    drawText(ctx, txt, x0, 138, PAL[3]);
    ctx.restore();
  } else if (netErr) {
    // connection message: centred on a dark label so it never overlaps the version number
    const w = tr(netErr).length * 4 + 8;
    ctx.fillStyle = PAL[3];
    ctx.fillRect(Math.round(80 - w / 2), 135, w, 9);
    drawTextC(ctx, netErr, 80, 138, PAL[0]);
  } else drawText(ctx, "UP/DN CHOOSE  START OK", 80 - (22 * 4) / 2, 138, PAL[2]);
  // game version, bottom left (the right side holds the menu's navigation arrow): tells at a glance whether a refresh picked up the latest update
  const ver = "V" + VERSION;
  const vw = textWidth(ver);
  ctx.fillStyle = maint.on ? PAL[1] : PAL[0];
  ctx.fillRect(0, 135, vw + 4, 9);
  drawText(ctx, ver, 2, 138, maint.on ? PAL[3] : PAL[2]);
}
function drawSpectate() {
  const t = TRACKS[specTrack];
  const now = performance.now();
  const live = now - specLastStates < 3000;
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, 17);
  if (Math.floor(tick / 30) % 2 === 0) {
    ctx.fillStyle = PAL[0];
    ctx.fillRect(5, 5, 5, 5);
  }
  drawText(ctx, live ? "LIVE" : "WAIT", 14, 6, PAL[0]);
  drawTextC(ctx, t.name, 80, 6, PAL[0]);
  drawText(ctx, `RD ${specRound + 1}/${SEASON.length}`, 118, 6, PAL[0]);

  const frames = Math.max(0, ((now - specStart) / 1000) * 59.7275 - 220);
  const cars = [...specCars.values()];
  const lead = cars.reduce((m, c) => Math.max(m, c.pos), 0);
  const leadLap = Math.min(t.laps, Math.floor(lead / t.lapUnits) + 1);
  drawText(ctx, "TIME", 5, 21, PAL[2]);
  drawText(ctx, formatTime(frames), 5, 28, PAL[3], 2);
  drawText(ctx, t.stage ? `STAGE ${Math.min(100, Math.floor((lead / t.lapUnits) * 100))}%` : `LAP ${leadLap}/${t.laps}`, 5, 44, PAL[3]);
  drawText(ctx, `${cars.length} RACING`, 5, 52, PAL[2]);

  // minimap with a dot per driver
  const pts = minimapPoints(t, 100);
  let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
  for (const p of pts) (x0 = Math.min(x0, p.x)), (x1 = Math.max(x1, p.x)), (y0 = Math.min(y0, p.y)), (y1 = Math.max(y1, p.y));
  const sc = Math.min(46 / (x1 - x0 || 1), 34 / (y1 - y0 || 1));
  const ox = 106 + (46 - (x1 - x0) * sc) / 2, oy = 22 + (34 - (y1 - y0) * sc) / 2;
  const mp = (f: number) => {
    const p = pts[Math.floor((((f % 1) + 1) % 1) * (pts.length - 1))];
    return [Math.round(ox + (p.x - x0) * sc), Math.round(oy + (p.y - y0) * sc)];
  };
  ctx.fillStyle = PAL[2];
  for (let i = 0; i < pts.length; i++) {
    const [x, y] = mp(i / (pts.length - 1));
    ctx.fillRect(x, y, 1, 1);
  }
  cars.forEach((c) => {
    const [x, y] = mp(c.pos / t.lapUnits);
    ctx.fillStyle = PAL[3];
    ctx.fillRect(x - 1, y - 1, 3, 3);
  });

  // standings among the human drivers
  const order = [...cars].sort((a, b) => (a.fin && b.fin ? a.fin - b.fin : a.fin ? -1 : b.fin ? 1 : b.pos - a.pos));
  const rowH = order.length > 5 ? 7 : 9; // eight drivers still fit above the status lines
  ctx.fillStyle = PAL[1];
  ctx.fillRect(2, 62, 156, 8);
  drawText(ctx, "POS NAME", 5, 64, PAL[3]);
  drawText(ctx, "LAP", 84, 64, PAL[3]);
  drawText(ctx, "KM/H", 112, 64, PAL[3]);
  order.forEach((c, i) => {
    const y = 73 + i * rowH;
    const nm = players.find((p) => p.id === c.id)?.name ?? "?";
    drawText(ctx, String(i + 1), 9, y, PAL[3]);
    drawText(ctx, nm, 24, y, PAL[3]);
    drawText(ctx, t.stage ? `${Math.min(100, Math.floor((c.pos / t.lapUnits) * 100))}%` : `${Math.min(t.laps, Math.floor(c.pos / t.lapUnits) + 1)}/${t.laps}`, 84, y, PAL[3]);
    drawText(ctx, c.fin ? formatTime(c.fin) : String(Math.round(c.speed)), c.fin ? 112 : 124, y, c.fin ? PAL[3] : PAL[2]);
  });
  if (!order.length) drawTextC(ctx, live ? "STARTING..." : "NEXT RACE SOON", 80, 80, PAL[2]);
  drawTextC(ctx, imIn() ? "YOU RACE NEXT" : specLocked ? "FINAL: FINALISTS RACE" : players.length >= MAX_PLAYERS ? "ROOM FULL - WATCHING" : "YOU JOIN NEXT RACE", 80, 126, PAL[3]);
  drawTextC(ctx, specLocked ? "FINALISTS ONLY   ESC LEAVE" : "AI CARS NOT SHOWN   ESC LEAVE", 80, 135, PAL[2]);
}
/** Tiny 7-pixel-wide pictures of the control a time was set with: keyboard, touch screen, gamepad. */
const DEVICE_ICONS = [
  ["#######", "#.#.#.#", "#######", "#.###.#", "#######"],
  [".#####.", ".#...#.", ".#...#.", ".#...#.", ".#...#.", ".##.##.", ".#####."],
  [".#####.", "#.#.#.#", "#######", "##...##"],
];
function drawDevice(c: CanvasRenderingContext2D, dev: number, x: number, y: number, col: string) {
  const g = DEVICE_ICONS[dev];
  if (!g) return;
  c.fillStyle = col;
  const oy = y + Math.floor((6 - g.length) / 2);
  g.forEach((row, j) => [...row].forEach((ch, i) => ch === "#" && c.fillRect(x + i, oy + j, 1, 1)));
}
function drawRecords() {
  const daily = recCat === 3;
  const weekly = recCat === 4 && !!weeklyInfo;
  const champ = recCat === 5;
  const teamTab = recCat === 7;
  const t = TRACKS[daily ? dailyInfo.track : weekly ? weeklyInfo!.track : recTrack];
  drawTextC(ctx, "RECORDS", 80, 3, PAL[3], 1);
  const cd = champs.get(champPeriod);
  const td = teamRecs.get(teamPeriod);
  drawTextC(ctx, teamTab ? `< TEAMS ${teamPeriod === "all" ? "ALL TIME" : typeof td === "object" ? td.label : ""} >` : champ ? `< CHAMPIONSHIP ${champPeriod === "all" ? "ALL TIME" : typeof cd === "object" ? cd.label : ""} >` : daily ? `TODAY ${t.name}` : weekly ? `${weeklyInfo!.title} ${t.name}` : `< ${t.name} >`, 80, 12, PAL[3], 1);
  recTabs().forEach((i, k) => {
    const x = 2 + k * 19;
    ctx.fillStyle = i === recCat ? PAL[3] : PAL[1];
    ctx.fillRect(x, 22, 18, 8);
    drawText(ctx, "\u0001" + (currentLang() === "fr" ? REC_TABS_FR[i] : REC_TABS[i]), x + 2, 24, i === recCat ? PAL[0] : PAL[3]);
  });
  const me = nameOf();
  const b = boards.get(recKey());
  if (teamTab) {
    if (td === undefined || td === "loading") drawTextC(ctx, "LOADING...", 80, 70, PAL[2]);
    else if (td === "offline") drawTextC(ctx, "SERVER OFFLINE", 80, 70, PAL[3]);
    else {
      drawText(ctx, "TEAM", 22, 33, PAL[2]);
      drawText(ctx, "PTS", 90, 33, PAL[2]);
      drawText(ctx, "ACT", 124, 33, PAL[2]);
      const rows = td.teams.filter((r) => r.points > 0);
      if (!rows.length) drawTextC(ctx, "NO POINTS YET", 80, 70, PAL[2]);
      rows.slice(0, 10).forEach((r, i) => {
        const y = 41 + i * 8;
        const mine = !!myTeam?.team && r.name === myTeam.team.name;
        if (mine) (ctx.fillStyle = PAL[3]), ctx.fillRect(6, y - 1, 148, 8);
        const c = mine ? PAL[0] : PAL[3];
        drawText(ctx, String(r.rank).padStart(2), 6, y, c);
        drawText(ctx, "\u0001" + r.name.slice(0, 11), 22, y, c);
        drawText(ctx, String(r.points).padStart(4), 86, y, c);
        drawText(ctx, `${r.active}/${r.members}`, 124, y, c);
      });
    }
  } else if (champ) {
    if (cd === undefined || cd === "loading") drawTextC(ctx, "LOADING...", 80, 70, PAL[2]);
    else if (cd === "offline") drawTextC(ctx, "SERVER OFFLINE", 80, 70, PAL[3]);
    else {
      drawText(ctx, "NAME", 22, 33, PAL[2]);
      drawText(ctx, "PTS", 68, 33, PAL[2]);
      drawText(ctx, "W", 92, 33, PAL[2]);
      drawText(ctx, "D", 108, 33, PAL[2]);
      drawText(ctx, "STK", 128, 33, PAL[2]);
      if (!cd.standings.length) drawTextC(ctx, "NO POINTS YET", 80, 70, PAL[2]);
      cd.standings.slice(0, 10).forEach((r, i) => {
        const y = 41 + i * 8;
        const mine = r.name === me;
        if (mine) {
          ctx.fillStyle = PAL[3];
          ctx.fillRect(6, y - 1, 148, 8);
        }
        const c = mine ? PAL[0] : PAL[3];
        drawText(ctx, String(r.rank).padStart(2), 6, y, c);
        drawText(ctx, r.name, 22, y, c);
        drawText(ctx, String(r.points).padStart(4), 64, y, c);
        drawText(ctx, String(r.wins).padStart(2), 88, y, c);
        drawText(ctx, String(r.played).padStart(2), 104, y, c);
        drawText(ctx, r.streak > 1 ? "S" + r.streak : "-", 128, y, c);
      });
      if (cd.provisional) drawTextC(ctx, "TODAY COUNTS - LIVE", 80, 122, PAL[2]);
    }
  } else if (recCat === 6) {
    const pb = pbs[String(recTrack)] ?? {};
    const tt = pbs["tt" + recTrack] ?? {};
    const line = (label: string, v: number | undefined, y: number) => {
      drawText(ctx, label, 14, y, PAL[3]);
      drawText(ctx, v !== undefined ? formatTime(v) : "--:--:--", 90, y, PAL[3]);
    };
    line("RACE LAP", pb.lap, 40);
    line("RACE TOTAL", pb.race, 50);
    line("TRIAL LAP", tt.lap, 64);
    line("TRIAL TOTAL", tt.race, 74);
    drawTextC(ctx, t.stage ? "STAGE" : `${t.laps} LAPS`, 80, 94, PAL[2]);
  } else if (b === "loading" || b === undefined) {
    drawTextC(ctx, "LOADING...", 80, 70, PAL[2]);
  } else if (b === "offline") {
    drawTextC(ctx, "SERVER OFFLINE", 80, 64, PAL[3]);
    drawTextC(ctx, "SHOWING NOTHING", 80, 74, PAL[2]);
  } else {
    const rows = recCat === 0 ? b.lap : b.race; // lap board for tab 0, total times for the others
    if (!rows.length) drawTextC(ctx, "NO TIMES YET", 80, 70, PAL[2]);
    rows.forEach((r, i) => {
      const y = 34 + i * 8;
      const mine = r.name === me;
      if (mine) {
        ctx.fillStyle = PAL[3];
        ctx.fillRect(6, y - 1, 148, 8);
      }
      const c = mine ? PAL[0] : PAL[3];
      drawText(ctx, String(i + 1).padStart(2), 8, y, c);
      drawText(ctx, r.name, 24, y, c);
      if (r.dev !== undefined) drawDevice(ctx, r.dev, 63, y, c);
      drawText(ctx, formatTime(r.frames), 84, y, c);
      drawText(ctx, MACHINES[r.machine]?.name ?? "?", 146, y, c);
    });
  }
  drawTextC(ctx, daily || weekly ? "UP/DN BOARD" : champ || teamTab ? "< > PERIOD  UP/DN BOARD" : "< > CIRCUIT  UP/DN BOARD", 80, 130, PAL[2]);
  drawTextC(ctx, "START BACK", 80, 138, PAL[2]);
}
function drawName() {
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, 30);
  drawTextC(ctx, nameMode === "code" ? "PILOT CODE" : nameMode === "name" ? "PILOT NAME" : "EMAIL", 80, 11, PAL[0], 2);
  if (nameMode === "mail" || nameMode === "link") {
    if (!mailStep) {
      const m = nameDraft.toUpperCase();
      drawTextC(ctx, m.slice(0, 30), 80, 44, PAL[3]);
      drawTextC(ctx, m.slice(30) + (nameStage === "type" && Math.floor(tick / 20) % 2 === 0 ? "_" : " "), 80, 54, PAL[3]);
      drawTextC(ctx, nameMode === "link" ? "LINK YOUR EMAIL TO THIS" : "TYPE THE EMAIL LINKED TO", 80, 72, PAL[2]);
      drawTextC(ctx, nameMode === "link" ? "LICENCE: IT RESTORES IT ON" : "YOUR LICENCE: A CODE", 80, 80, PAL[2]);
      drawTextC(ctx, nameMode === "link" ? "ANOTHER DEVICE" : "IS SENT TO IT", 80, 88, PAL[2]);
    } else {
      const w = 6 * 8 + 12;
      ctx.fillStyle = PAL[3];
      ctx.fillRect(80 - w / 2, 40, w, 22);
      ctx.fillStyle = PAL[0];
      ctx.fillRect(80 - w / 2 + 2, 42, w - 4, 18);
      drawText(ctx, nameDraft, 80 - w / 2 + 6, 46, PAL[3], 2);
      drawTextC(ctx, "A CODE WAS SENT TO", 80, 72, PAL[2]);
      drawTextC(ctx, mailAddr.toUpperCase().slice(0, 38), 80, 80, PAL[3]);
      drawTextC(ctx, "CHECK SPAM - VALID 10 MIN", 80, 88, PAL[2]);
    }
  } else if (nameMode === "code") {
    const g = formatPilotKey(nameDraft.padEnd(0));
    drawTextC(ctx, g.slice(0, 19), 80, 46, PAL[3], 1);
    drawTextC(ctx, g.slice(20), 80, 56, PAL[3], 1);
    if (nameStage === "type" && Math.floor(tick / 20) % 2 === 0) drawTextC(ctx, "_", 80, 66, PAL[3]);
    drawTextC(ctx, "PASTE THE CODE OF YOUR", 80, 80, PAL[2]);
    drawTextC(ctx, "LICENCE: 32 CHARACTERS", 80, 88, PAL[2]);
    if (coarse() && nameStage === "type" && !nameMsg) drawTextC(ctx, "TAP THE CODE BOX TO PASTE", 80, 99, PAL[3]);
  } else {
    const w = 8 * 4 * 2 + 12;
    ctx.fillStyle = PAL[3];
    ctx.fillRect(80 - w / 2, 40, w, 22);
    ctx.fillStyle = PAL[0];
    ctx.fillRect(80 - w / 2 + 2, 42, w - 4, 18);
    const tx = 80 - w / 2 + 6;
    drawText(ctx, nameDraft, tx, 46, PAL[3], 2);
    if (nameStage === "type" && Math.floor(tick / 20) % 2 === 0) {
      ctx.fillStyle = PAL[3];
      ctx.fillRect(tx + nameDraft.length * 8, 46, 2, 10);
    }
    if (nameStage === "confirm") {
      drawTextC(ctx, "THIS NAME IS YOURS", 80, 74, PAL[3]);
      drawTextC(ctx, "AND CANNOT BE CHANGED", 80, 82, PAL[3]);
      drawTextC(ctx, "BY YOU LATER", 80, 90, PAL[3]);
    } else {
      drawTextC(ctx, "A-Z 0-9 - MAX 8", 80, 74, PAL[2]);
      drawTextC(ctx, "SEEN BY ALL PLAYERS", 80, 82, PAL[2]);
      drawTextC(ctx, "IN THE RECORDS", 80, 90, PAL[2]);
    }
  }
  if (nameStage === "busy") drawTextC(ctx, "...", 80, 100, PAL[3]);
  else if (nameMsg) drawTextC(ctx, nameMsg, 80, 100, PAL[3]);
  if (nameStage === "type" && nameMode !== "link") {
    const k = coarse() ? "TAP HERE:" : "TAB:";
    drawTextC(ctx, nameMode === "name" ? k + " I HAVE A CODE" : nameMode === "code" && emailOn ? k + " USE MY EMAIL" : k + " NEW NAME", 80, 114, PAL[2]);
  }
  drawTextC(ctx, nameStage === "confirm" ? "ENTER YES   ESC NO" : "ENTER OK   ESC BACK", 80, 130, PAL[3]);
}
function drawBadge() {
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, 30);
  drawTextC(ctx, "PILOT BADGE", 80, 11, PAL[0], 2);
  drawTextC(ctx, nameOf(), 80, 36, PAL[3], 2);
  const d = badgeData;
  if (!d) return void drawTextC(ctx, "...", 80, 80, PAL[2]);
  const p = d.profile;
  const row = (l: string, v: unknown, x: number, y: number) => (drawText(ctx, l, x, y, PAL[2]), drawText(ctx, String(v), x + 56, y, PAL[3]));
  row("RUNS", p.runs, 8, 50);
  row("DAILY", p.dailyPlayed, 8, 57);
  row("DAILY WON", p.dailyWins, 8, 64);
  row("ONLINE", p.online, 84, 50);
  row("WINS", p.wins, 84, 57);
  row("RECORDS", p.records, 84, 64);
  drawText(ctx, `BADGES ${d.badges.length}/${d.total}`, 8, 71, PAL[3]);
  const shown = performance.now() - badgeMsgAt < 4000;
  const hint = shown ? badgeMsg : d.share ? "START: SHARE" : "";
  if (hint) drawText(ctx, hint, 152 - textWidth(hint), 71, PAL[3]);
  // every badge of the game: earned ones in dark with a >, the ones still to win in light with a -
  BADGES.forEach((b, i) => {
    const got = d.badges.includes(b.id);
    drawText(ctx, (got ? "> " : "- ") + b.en, 6 + (i % 2) * 78, 77 + Math.floor(i / 2) * 6, got ? PAL[3] : PAL[1]);
  });
}
function drawLicence() {
  if (licPage === 1) {
    drawBadge();
    drawTextC(ctx, "< LICENCE   ESC BACK", 80, 137, PAL[2]);
    return;
  }
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, 0, SCREEN_W, 30);
  drawTextC(ctx, "PILOT LICENCE", 80, 11, PAL[0], 2);
  drawTextC(ctx, nameOf(), 80, 38, PAL[3], 2);
  const g = formatPilotKey(pilotKey);
  drawTextC(ctx, g.slice(0, 19), 80, 62, PAL[3]);
  drawTextC(ctx, g.slice(20), 80, 71, PAL[3]);
  drawTextC(ctx, "YOUR SECRET CODE", 80, 82, PAL[2]);
  drawTextC(ctx, "KEEP IT: IT RESTORES YOUR", 80, 92, PAL[2]);
  drawTextC(ctx, "NAME ON ANOTHER DEVICE", 80, 100, PAL[2]);
  drawTextC(ctx, "NEVER SHARE IT", 80, 110, PAL[3]);
  if (mailMsgAt > 0) mailMsgAt--;
  if (emailOn && licRemove) drawTextC(ctx, "REMOVE EMAIL? ENTER YES ESC NO", 80, 120, PAL[3]);
  else if (emailOn) drawTextC(ctx, mailMsgAt > 0 ? "EMAIL LINKED!" : pilotEmail ? "EMAIL " + pilotEmail.toUpperCase().slice(0, 17) + " DOWN: REMOVE" : "DOWN: LINK AN EMAIL", 80, 120, pilotEmail ? PAL[3] : PAL[2]);
  if (licCopied > 0) licCopied--;
  drawTextC(ctx, licCopied > 0 ? "COPIED" : "START COPY   > BADGE", 80, 130, PAL[3]);
  drawTextC(ctx, "ESC BACK", 80, 138, PAL[2]);
}

// ---- machine cards, shared by the select screen and the online lobby: the chosen machine is a dark card tagged SELECTED, the other one a pale, dimmed card ----
const CARD_X = [8, 84];
const CARD_STATS: [string, (s: (typeof MACHINES)[number]) => number, number, number][] = [
  ["MAX", (s) => s.vmax, 180, 340],
  ["JET", (s) => s.jmax, 220, 380],
  ["ACC", (s) => s.accel, 8, 15],
  ["JACC", (s) => s.jetAccel, 14, 26], // how hard the turbo pulls: what a launch with the jet feels like
  ["FUEL", (s) => s.fuel, 35, 80],
];
/** Vehicle category of the mode the select screen is for (the lobby and every other mode: formula). */
const cardClass = () => (screen === "select" ? (menuIdx === 0 ? seasonClass : menuIdx === 6 ? dailyInfo.vclass : menuIdx === 9 ? weeklyInfo?.vclass ?? 0 : 0) : 0);
function drawMachineCards(top: number, h: number, compact: boolean) {
  const stats = compact ? CARD_STATS.slice(0, 2) : CARD_STATS;
  for (let m = 0; m < 2; m++) {
    const sel = m === machine;
    const x = CARD_X[m];
    const vc = vclassOf(cardClass());
    const s = vc.machines[m];
    const fg = sel ? PAL[0] : PAL[2];
    ctx.fillStyle = sel ? PAL[3] : PAL[1];
    ctx.fillRect(x - (sel ? 1 : 0), top, sel ? 70 : 68, h);
    ctx.fillStyle = sel ? PAL[3] : PAL[0];
    ctx.fillRect(x + (sel ? 0 : 1), top + (sel ? 0 : 1), sel ? 68 : 66, h - (sel ? 0 : 2));
    const plateY = top + (compact ? 11 : 21);
    if (compact) {
      drawText(ctx, "TYPE " + s.name, x + 4, top + 4, fg);
      const tag = sel ? "SELECTED" : m === 0 ? vc.tagA : vc.tagB;
      drawText(ctx, tag, x + 64 - tr(tag).length * 4, top + 4, sel ? PAL[0] : PAL[2]);
    } else drawTextC(ctx, "TYPE " + s.name, x + 34, top + 5, fg, 2);
    const col = colorsActive();
    ctx.fillStyle = col ? cardSky() : sel ? PAL[0] : PAL[1];
    ctx.fillRect(x + 4, plateY, 60, compact ? 15 : 22);
    if (col) {
      ctx.fillStyle = "#5fae4a";
      ctx.fillRect(x + 4, plateY + (compact ? 12 : 19), 60, compact ? 3 : 3);
    }
    cardTint(col, myTeam?.team?.color);
    if (!sel) ctx.globalAlpha = 0.6;
    drawCarSprite(ctx, x + 34, plateY + (compact ? 13 : 20), (compact ? 0.72 : 1) * spriteWidth(cardClass(), m), false, 0, 0, 0, cardClass());
    ctx.globalAlpha = 1;
    cardTint(false);
    stats.forEach(([label, get, lo, hi], i) => {
      const y = top + (compact ? 29 : 46) + i * (compact ? 6 : 7);
      drawText(ctx, `${label} ${get(s)}`, x + 4, y, fg);
      const fill = Math.round(Math.max(0, Math.min(1, (get(s) - lo) / (hi - lo))) * 21);
      ctx.fillStyle = sel ? PAL[2] : PAL[1];
      ctx.fillRect(x + 41, y + 1, 23, 4);
      ctx.fillStyle = sel ? PAL[0] : PAL[2];
      ctx.fillRect(x + 42, y + 2, fill, 2);
    });
    if (!compact) {
      if (sel) {
        ctx.fillStyle = PAL[0];
        ctx.fillRect(x + 8, top + h - 12, 52, 9);
        drawTextC(ctx, "SELECTED", x + 34, top + h - 10, PAL[3]);
      } else drawTextC(ctx, m === 0 ? vc.tagA : vc.tagB, x + 34, top + h - 10, PAL[2]);
    }
    if (sel && Math.floor(tick / 20) % 2 === 0) {
      // blinking arrow towards the other card
      const d = m === 0 ? 1 : -1;
      const ax = m === 0 ? x + 72 : x - 4;
      const ay = top + Math.round(h / 2);
      ctx.fillStyle = PAL[3];
      for (let k = 0; k < 3; k++) ctx.fillRect(ax + (d > 0 ? k : -k), ay - 2 + k, 1, 5 - 2 * k);
    }
  }
}
/** Touch: tapping a card chooses that machine. Returns true when a card was hit. */
function tapMachine(e: PointerEvent, top: number, h: number): boolean {
  const r = canvas.getBoundingClientRect();
  const x = ((e.clientX - r.left) / r.width) * SCREEN_W;
  const y = ((e.clientY - r.top) / r.height) * SCREEN_H;
  if (y < top || y > top + h) return false;
  const m = x >= 8 && x < 78 ? 0 : x >= 84 && x < 154 ? 1 : -1;
  if (m < 0) return false;
  machine = m;
  return true;
}
let optRow = 0;
// palette, vibration, touch layout, music, car, tyre squeal, (Discord link when the server has one), back
const optRows = () => 14 + (installAvail() ? 1 : 0); // + language (row 6), colours (row 7), button size (8), button opacity (9), speedometer (10), tyre marks (11), controller (12)
const OPT_Y0 = 18;
const optDy = () => Math.floor(112 / optRows());
const VOL_ROWS: { kind: VolumeKind; label: string }[] = [
  { kind: "music", label: "MUSIC" },
  { kind: "car", label: "CAR" },
  { kind: "skid", label: "SKID" },
];
/** Changes one setting of the options screen. */
function optStep(row: number, dir: number, wrap: boolean) {
  if (row === 0) setPalette(options.palette + dir);
  else if (row === 1) {
    setRumble(options.rumble + dir);
    rumble(350, 0.9, 0.6, true); // feel the new strength straight away
  } else if (row === 2) setTouch(options.touch + dir);
  else if (row === 7) setColors(options.colors + dir);
  else if (row === 8) setBtnSize(options.btnSize + dir);
  else if (row === 9) setBtnAlpha(options.btnAlpha + dir);
  else if (row === 10) setSpeedo(options.speedo + dir);
  else if (row === 11) setSkids(options.skids + dir);
  else if (row === 6) (setLang(currentLang() === "fr" ? "en" : "fr"), labelChat());
  else if (row >= 3 && row <= 5) {
    const k = VOL_ROWS[row - 3].kind;
    setVolume(k, options.vol[k] + dir, wrap);
    sfx.applyVolumes();
    if (k !== "music") sfx.preview(k); // the music is playing already
  }
}
function drawOptions() {
  drawTextC(ctx, "OPTIONS", 80, 5, PAL[3], 2);
  const rows: [string, string][] = [
    ["PALETTE", PALETTES[options.palette].name],
    ["VIBRATION", RUMBLE_LEVELS[options.rumble].name],
    ["TOUCH", TOUCH_MODES[options.touch]],
    ...VOL_ROWS.map((v) => [v.label, options.vol[v.kind] * (100 / VOLUME_STEPS) + "%"] as [string, string]),
    ["LANGUAGE", currentLang() === "fr" ? "FRANCAIS" : "ENGLISH"],
    ["COLORS", options.colors ? (options.palette === 0 ? "YES" : "CLASSIC ONLY") : "OFF"],
    ["BUTTON SIZE", BTN_SIZES[options.btnSize].name],
    ["BUTTON OPACITY", BTN_ALPHAS[options.btnAlpha].name],
    ["SPEEDOMETER", SPEEDO_MODES[options.speedo]],
    ["TIRE MARKS", ["OFF", "ME ONLY", "PLAYERS", "ALL CARS"][options.skids]],
  ];
  rows.forEach(([label, value], i) => {
    const y = OPT_Y0 + i * optDy();
    const sel = optRow === i;
    ctx.fillStyle = sel ? PAL[3] : PAL[1];
    ctx.fillRect(8, y, 144, optDy() - 1);
    drawText(ctx, (sel ? "> " : "  ") + label, 10, y + (optDy() - 6) / 2 | 0, sel ? PAL[0] : PAL[3]);
    const text = `< ${value} >`;
    drawText(ctx, text, 148 - textWidth(text), y + (optDy() - 6) / 2 | 0, sel ? PAL[0] : PAL[3]);
    if (i === 0) {
      // the four shades of the current palette, framed
      ctx.fillStyle = sel ? PAL[0] : PAL[3];
      ctx.fillRect(50, y + 3, 26, 9);
      for (let k = 0; k < 4; k++) {
        ctx.fillStyle = PAL[k];
        ctx.fillRect(51 + k * 6, y + 4, 6, 7);
      }
    }
  });
  const btn = (i: number, label: string) => {
    const by = OPT_Y0 + i * optDy(), sel = optRow === i;
    ctx.fillStyle = sel ? PAL[3] : PAL[1];
    ctx.fillRect(8, by, 144, optDy() - 1);
    drawTextC(ctx, (sel ? "> " : "") + label, 80, by + ((optDy() - 6) / 2 | 0), sel ? PAL[0] : PAL[3]);
  };
  btn(12, "CONTROLLER");
  if (installAvail()) btn(13, "INSTALL APP");
  btn(optRows() - 1, "BACK");
  drawTextC(ctx, installHelp > 0 ? "SHARE > ADD TO HOME SCREEN" : "UP/DN ROW  < > CHANGE", 80, 133, PAL[2]);
}
// ---- controller options: which button is gas, brake and jet ----
const PAD_ACTIONS: { action: "accel" | "brake" | "jet"; label: string }[] = [
  { action: "accel", label: "GAS" },
  { action: "brake", label: "BRAKE" },
  { action: "jet", label: "JET" },
];
let padRow = 0;
let padAsk = false; // waiting for the button to assign
let padMsg = "";
function openPadMap() {
  screen = "padmap";
  padRow = 0;
  padAsk = false;
  padMsg = padConnected() ? "" : "CONNECT A GAMEPAD";
}
function padMapPress(code: string) {
  if (padAsk) {
    if (code === "Escape") (capturePadButton(null), (padAsk = false), (padMsg = ""));
    return;
  }
  if (code === "ArrowUp") padRow = (padRow + 4) % 5;
  if (code === "ArrowDown") padRow = (padRow + 1) % 5;
  if (code === "Escape") screen = "options";
  if (code === "Enter" || code === "Space" || code === "KeyZ" || code === "btn:start" || code === "btn:accel") {
    if (padRow < 3) {
      if (!padConnected()) return void (padMsg = "CONNECT A GAMEPAD");
      padAsk = true;
      padMsg = "";
      capturePadButton((b) => {
        padAsk = false;
        if (b === null) return void (padMsg = "NOTHING PRESSED");
        setPadButton(PAD_ACTIONS[padRow].action, b);
        padMsg = "\u0001" + (currentLang() === "fr" ? { accel: "GAZ", brake: "FREIN", jet: "JET" }[PAD_ACTIONS[padRow].action] : PAD_ACTIONS[padRow].label) + ": " + padButtonName(b);
      });
    } else if (padRow === 3) (setPadButton(null, null), (padMsg = "ORIGINAL BUTTONS"));
    else screen = "options";
  }
}
function drawPadMap() {
  drawTextC(ctx, "CONTROLLER", 80, 5, PAL[3], 2);
  drawTextC(ctx, padConnected() ? "\u0001" + (padInfo.name || "GAMEPAD").replace(/\(.*$/, "").toUpperCase().replace(/[^A-Z0-9 .-]/g, "").trim().slice(0, 36) : "NO GAMEPAD", 80, 24, PAL[2]);
  const rows = [...PAD_ACTIONS.map((a) => [a.label, padMap[a.action].map(padButtonName).join(" ")] as [string, string]), ["ORIGINAL BUTTONS", ""] as [string, string], ["BACK", ""] as [string, string]];
  rows.forEach(([label, value], i) => {
    const y = 34 + i * 14, sel = padRow === i;
    ctx.fillStyle = sel ? PAL[3] : PAL[1];
    ctx.fillRect(8, y, 144, 13);
    const waiting = padAsk && sel;
    if (i < 3) {
      drawText(ctx, (sel ? "> " : "  ") + label, 10, y + 4, sel ? PAL[0] : PAL[3]);
      const t = waiting ? "PRESS A BUTTON" : value;
      drawText(ctx, "\u0001" + t, 148 - tr(t).length * 4, y + 4, sel ? PAL[0] : PAL[3]);
    } else drawTextC(ctx, (sel ? "> " : "") + label, 80, y + 4, sel ? PAL[0] : PAL[3]);
  });
  drawTextC(ctx, padMsg || (padAsk ? "ESC: CANCEL" : "START: CHANGE  STEER: STICK"), 80, 124, padMsg ? PAL[3] : PAL[2]);
  drawTextC(ctx, "SELECT AND START CANNOT BE MOVED", 80, 134, PAL[2]);
}
function drawSelect() {
  if (menuIdx === 0 || menuIdx === 1) {
    const a = selRow === 0;
    drawTextC(ctx, `< ${SKILLS[skillIdx].name} >`, 80, 8, a ? PAL[3] : PAL[2], 1);
    drawText(ctx, "LVL", 4, 8, a ? PAL[3] : PAL[2]);
  } else if (menuIdx === 5) {
    const a = selRow === 3;
    drawTextC(ctx, `< ${GHOST_SRC[ghostSrc]} >`, 84, 8, a ? PAL[3] : PAL[2], 1);
    drawText(ctx, "GHOST", 4, 8, a ? PAL[3] : PAL[2]);
  } else if (menuIdx === 6) drawTextC(ctx, `DAILY ${dailyInfo.day.slice(4, 6)}/${dailyInfo.day.slice(6)}  ${dailyInfo.ai === 0 ? "NO CPU" : SKILLS[dailyInfo.skill].name}`, 80, 8, PAL[3], 1);
  else if (menuIdx === 9 && weeklyInfo) drawTextC(ctx, `WEEKLY ${weeklyInfo.title}`, 80, 8, PAL[3], 1);
  else drawTextC(ctx, "SELECT MACHINE", 80, 8, PAL[3], 1);
  drawMachineCards(17, 94, false);
  const pick = menuIdx === 1 || menuIdx === 5;
  const tn = menuIdx === 0 ? `SEASON: ${SEASON.length} CIRCUITS` + (seasonClass ? " - " + vclassOf(seasonClass).name : "") : pick ? (rally && menuIdx === 5 ? "STAGE " : "") + "< " + TRACKS[trackIdx].name + " >" : menuIdx === 6 ? "TODAY: " + TRACKS[dailyInfo.track].name + (dailyInfo.vclass ? " - " + vclassOf(dailyInfo.vclass).name : "") : menuIdx === 9 && weeklyInfo ? "WEEK: " + weeklyInfo.trackName + (weeklyInfo.vclass ? " - " + vclassOf(weeklyInfo.vclass).name : "") : "ONLINE SEASON";
  drawTextC(ctx, tn, 80, 118, pick && selRow === 2 ? PAL[3] : PAL[2]);
  if (selRow === 1) drawText(ctx, ">", 2, 60, PAL[3]);
  if (pick && selRow === 2) drawText(ctx, ">", 2, 118, PAL[3]);
  if (menuIdx === 6) {
    // the bottom line is the champion-ghost switch (UP/DN to reach it, < > to flip it)
    const label = !dailyGhost ? "NONE YET" : dailyGhostOn ? dailyGhost.name.slice(0, 8) + " " + formatTime(dailyGhost.frames) : "OFF";
    const a = selRow === 3;
    drawTextC(ctx, (a ? "> " : "") + "GHOST " + (dailyGhost ? `< ${label} >` : label), 80, 130, a ? PAL[3] : PAL[2]);
  } else if (menuIdx === 9 && weeklyInfo) {
    const w = weeklyInfo;
    drawTextC(ctx, [w.ai === 0 ? "NO CPU" : "CPU " + SKILLS[w.skill].name, w.nojet ? "NO TURBO" : "", w.machine >= 0 ? "TYPE " + "AB"[w.machine] : ""].filter(Boolean).join("  "), 80, 130, PAL[2]);
  } else drawTextC(ctx, menuIdx === 2 ? "< > MACHINE   START" : "UP/DN ROW  < > CHANGE", 80, 130, PAL[2]);
}
function drawRoom() {
  drawTextC(ctx, "ONLINE ROOM", 80, 8, PAL[3], 2);
  const rows = [
    ["PUBLIC ROOM", "OPEN TO EVERYONE"],
    ["NEW PRIVATE ROOM", "CODE " + roomCode + "  < > NEW"],
    ["JOIN WITH CODE", (joinDraft + "------").slice(0, Math.max(4, joinDraft.length)) + (document.activeElement === roomField && Math.floor(tick / 20) % 2 ? "<" : "")],
  ];
  rows.forEach(([a, b], i) => {
    const y = 40 + i * 24;
    ctx.fillStyle = roomRow === i ? PAL[3] : PAL[1];
    ctx.fillRect(8, y, 144, 20);
    drawText(ctx, (roomRow === i ? "> " : "  ") + a, 12, y + 4, roomRow === i ? PAL[0] : PAL[3]);
    drawText(ctx, b, 24, y + 12, roomRow === i ? PAL[1] : PAL[2]);
  });
  drawTextC(ctx, "SHARE THE CODE WITH FRIENDS", 80, 116, PAL[2]);
  drawTextC(ctx, "ESC BACK", 80, 132, PAL[2]);
}
function drawLobby() {
  if (lobbyTourn) (drawTextC(ctx, lobbyTourn.name, 80, 2, PAL[3], 1), drawTextC(ctx, "CODE " + room, 80, 9, PAL[2], 1)); // the tournament's name, and the code to share
  else if (room === "PUBLIC") drawTextC(ctx, "PUBLIC ROOM", 80, 4, PAL[3], 1);
  else (drawTextC(ctx, room, 80, 3, PAL[3], 2), drawText(ctx, "CODE", 4, 4, PAL[2]));
  players.forEach((p, i) => {
    drawText(ctx, `${p.id === myId ? ">" : " "}${p.name} ${MACHINES[p.machine].name}`, 10, 15 + i * 8, PAL[3]);
    drawText(ctx, p.ready ? "READY" : "...", 100, 15 + i * 8, p.ready ? PAL[3] : PAL[2]);
  });
  drawTextC(ctx, ready ? "WAITING OTHERS" : "PRESS START WHEN READY", 80, 80, PAL[3]);
  if (rematchTrack >= 0) {
    const voted = rematchIds.includes(myId);
    drawTextC(ctx, voted ? `REMATCH ${rematchIds.length}/${players.length} UP: CANCEL` : `UP: REMATCH ${(TRACKS[rematchTrack] ?? TRACKS[0]).name.slice(0, 12)}${rematchIds.length ? " " + rematchIds.length + "/" + players.length : ""}`, 80, 89, voted ? PAL[3] : PAL[2]);
  } else drawTextC(ctx, "YOUR MACHINE: " + MACHINES[machine].name, 80, 89, PAL[2]);
  if (!sayOpen && !chatLog.some((m) => performance.now() - m.at < 5000)) CHAT_MSGS.forEach((_t, i) => drawText(ctx, `\u0001${i + 1} ${chatWord(i)}`, 10 + (i % 2) * 76, 99 + Math.floor(i / 2) * 6, PAL[2]));
  if (lobbyTourn) drawTextC(ctx, `ROUND ${lobbyRound + 1}/${lobbyTourn.rounds} ${(TRACKS[lobbyTrack] ?? TRACKS[0]).name}`, 80, 123, PAL[3]);
  else {
    const txt = lobbyPick >= 0 ? `FREE RACE ${(TRACKS[lobbyTrack] ?? TRACKS[0]).name}` : `ROUND ${lobbyRound + 1}/${SEASON.length} ${(TRACKS[lobbyTrack] ?? TRACKS[0]).name}`;
    drawTextC(ctx, txt, 80, 123, PAL[3]);
    if (room !== "PUBLIC" && lobbyHost === myId) (drawText(ctx, "<", 3, 123, PAL[2]), drawText(ctx, ">", 153, 123, PAL[2])); // the host picks the circuit with left/right
  }
  drawTextC(ctx, "PING " + Math.round(net?.rttAvg ?? 0) + "MS", 40, 130, PAL[2]);
  drawNetStatus(4, 128, false);
  drawText(ctx, sayAllowed() && !coarse() ? "T WRITE 1-8 CHAT" : "1-8 CHAT", sayAllowed() && !coarse() ? 94 : 112, 130, PAL[2]);
  drawTextC(ctx, room === "PUBLIC" ? "ANYONE CAN JOIN" : copied > 0 ? "INVITE LINK COPIED" : "TAP OR C: SHARE INVITE", 80, 137, copied > 0 ? PAL[3] : PAL[2]);
}
let podiumAt = -1;
// solo season: podium of each race, then of the championship (the top 3 are worked out when the screen opens)
let soloTop: { title: string; top: { name: string; time: string; pts: number }[] } | null = null;
const podiumActive = () => podiumAt >= 0 && tick - podiumAt < PODIUM_FRAMES && (online ? showFinal || resultOrder.some((r) => r.frame < 1e9) : !!soloTop);
const cpuName = (id: number) => (id === mySlot ? nameOf() : "CPU" + id);
function startSeasonRacePodium() {
  const r = race!;
  const order = [...r.cars].sort((a, b) => r.rankOf(a) - r.rankOf(b));
  const mine = order.findIndex((c) => c.id === mySlot) + 1;
  soloTop = {
    title: r.track.name + (mine > 3 ? ` - YOU ${mine}TH` : ""),
    top: order.slice(0, 3).map((c, i) => ({ name: cpuName(c.id), time: c.finished ? formatTime(c.finishFrame) : "---", pts: POINTS[i] ?? 0 })),
  };
  podiumAt = tick;
}
function startSeasonFinalPodium() {
  const rows = seasonPts.map((p, id) => ({ id, p: p ?? 0 })).sort((a, b) => b.p - a.p);
  const mine = rows.findIndex((r) => r.id === mySlot) + 1;
  soloTop = { title: "CHAMPIONSHIP" + (mine > 3 ? ` - YOU ${mine}TH` : ""), top: rows.slice(0, 3).map((r) => ({ name: cpuName(r.id), time: r.p + " PTS", pts: 0 })) };
  podiumAt = tick;
}
function drawResult() {
  if (!online && soloTop && podiumActive()) return void drawPodium(ctx, tick - podiumAt, soloTop.title, soloTop.top);
  if (online && showFinal && tournFinal && podiumActive()) return void drawPodium(ctx, tick - podiumAt, tournFinal.title, tournFinal.top);
  if (online && podiumActive()) {
    const t = tick - podiumAt;
    const top = resultOrder.filter((r) => r.frame < 1e9).slice(0, 3).map((r) => ({
      name: players.find((q) => q.id === r.id)?.name ?? "?",
      team: players.find((q) => q.id === r.id)?.team,
      tc: players.find((q) => q.id === r.id)?.tc,
      time: formatTime(r.frame),
      pts: friendlyResult ? 0 : onlineStandings.find((q) => q.id === r.id)?.pts ?? 0,
    }));
    drawPodium(ctx, t, TRACKS[resultTrack].name, top);
    return;
  }
  drawTextC(ctx, (!online && runMode === "tt" ? "TRIAL " : !online && runMode === "daily" ? "DAILY " : !online && runMode === "weekly" ? "WEEKLY " : "RESULT ") + (online ? TRACKS[resultTrack] : race?.track ?? TRACKS[trackIdx]).name, 80, 4, PAL[3], 1);
  if (online && resultTourn) drawTextC(ctx, `${resultTourn.name.slice(0, 10)} ROUND ${resultTourn.round}/${resultTourn.rounds}`, 80, 11, PAL[2]);
  if (online) {
    if (friendlyResult) drawTextC(ctx, "FRIENDLY - NO POINTS", 80, 120, PAL[2]);
    resultOrder.forEach((r, i) => {
      const p = players.find((q) => q.id === r.id);
      drawText(ctx, `${i + 1} ${p?.name ?? "?"}`, 20, 18 + i * 8, PAL[3]);
      drawText(ctx, r.frame < 1e9 ? formatTime(r.frame) : "DNF", 80, 18 + i * 8, PAL[3]);
      const pts = onlineStandings.find((q) => q.id === r.id)?.pts ?? 0;
      drawText(ctx, `${pts}P`, 134, 18 + i * 8, PAL[2]);
    });
  } else if (race && runMode === "tt") {
    const me = race.cars[mySlot];
    drawText(ctx, "TIME", 14, 18, PAL[2]);
    drawText(ctx, formatTime(me.finishFrame), 14, 25, PAL[3], 2);
    (race.track.stage ? [] : me.lapFrames).forEach((f, i) => {
      drawText(ctx, `LAP ${i + 1}`, 14, 44 + i * 8, PAL[3]);
      drawText(ctx, formatTime(f), 70, 44 + i * 8, f === Math.min(...me.lapFrames) ? PAL[3] : PAL[2]);
    });
    if (ghost && me.finished && ghost.frames > 0) {
      const d = (me.finishFrame - ghost.frames) / 59.7275; // the ghost's time is known even when I finished before it
      drawText(ctx, `VS ${activeChallenge ? ghost.name.slice(0, 8) : "GHOST"} ${d > 0 ? "+" : "-"}${Math.abs(d).toFixed(2)}`, 14, 44 + me.lapFrames.length * 8 + 4, d > 0 ? PAL[2] : PAL[3]);
      if (activeChallenge) drawTextC(ctx, d > 0 ? `${ghost.name.slice(0, 8)} WINS BY ${d.toFixed(2)}` : d < 0 ? `YOU BEAT ${ghost.name.slice(0, 8)}!` : "A PERFECT TIE!", 80, 90, d > 0 ? PAL[2] : PAL[3]);
    }
    if (newRace) drawTextC(ctx, "NEW PERSONAL BEST!", 80, 100, PAL[3]);
  } else if (race) {
    const order = [...race.cars].sort((a, b) => race!.rankOf(a) - race!.rankOf(b));
    if (menuIdx === 0 && !seasonScored) {
      seasonScored = true;
      order.forEach((c, i) => (seasonPts[c.id] = (seasonPts[c.id] ?? 0) + (POINTS[i] ?? 0)));
    }
    order.forEach((c, i) => {
      const me = c.id === mySlot;
      drawText(ctx, `${String(i + 1).padStart(2)} ${me ? "YOU" : "CPU" + c.id}`, 10, 14 + i * 10 - 2, me ? PAL[3] : PAL[2]);
      drawText(ctx, c.finished ? formatTime(c.finishFrame) : "---", 70, 14 + i * 10 - 2, PAL[3]);
      if (menuIdx === 0) drawText(ctx, `${POINTS[i] ?? 0}P`, 134, 14 + i * 10 - 2, PAL[2]);
    });
  }
  if (!online && race) {
    const me = race.cars[mySlot];
    const lap = me.lapFrames.length ? Math.min(...me.lapFrames) : 0;
    const ranked = runMode === "tt" || runMode === "daily" || runMode === "weekly" || (runAi === RANKED_AI && raceSkill >= MIN_RANKED_SKILL);
    if (!ranked) drawTextC(ctx, runAi !== RANKED_AI ? "UNRANKED (AI " + runAi + ")" : "UNRANKED: " + SKILLS[raceSkill].name, 80, 112, PAL[2]);
    else {
      drawTextC(ctx, `${newLap ? "NEW " : ""}BEST LAP ${formatTime(lap)}`, 80, 110, newLap ? PAL[3] : PAL[2]);
      const w = sent.state === "sending" ? "SENDING..." : sent.state === "offline" ? "SERVER OFFLINE" : sent.state === "done" ? `WORLD RANK  LAP ${sent.lapRank}  RACE ${sent.raceRank}` : "";
      drawTextC(ctx, w, 80, 120, PAL[3]);
      if (runMode === "daily" && ghost?.race.cars[0].finished && me.finished) {
        const d = (me.finishFrame - ghost.race.cars[0].finishFrame) / 59.7275;
        drawTextC(ctx, `VS ${ghost.name.slice(0, 8)} ${d > 0 ? "+" : "-"}${Math.abs(d).toFixed(2)}`, 80, 127, d > 0 ? PAL[2] : PAL[3]);
      }
      if (runMode === "daily" && sent.state === "done") {
        const c = champs.get("month");
        const mine = typeof c === "object" ? c.standings.find((r) => r.name === nameOf()) : undefined;
        if (mine) drawTextC(ctx, `CHAMP #${mine.rank}  ${mine.points}P${mine.streak > 1 ? "  STREAK " + mine.streak : ""}`, 80, 100, PAL[3]);
      }
    }
  }
  drawTextC(ctx, "PRESS START", 80, 134, PAL[2]);
  if (canVote()) {
    const mine = voted[activeShared!.code];
    drawTextC(ctx, voteMsg || (coarse() ? "TAP: LIKE | DISLIKE" : "L: LIKE   K: DISLIKE"), 80, 10, mine ? PAL[3] : PAL[2]);
  }
  if (!online && runMode === "tt" && activeShared && sent.state !== "sending") drawTextC(ctx, linkMsg || (coarse() ? "TAP: SHARE THIS CIRCUIT" : "C: SHARE THIS CIRCUIT"), 80, 127, linkMsg === "LINK COPIED" ? PAL[3] : PAL[2]);
  else if (!online && runMode === "tt" && sent.state === "done" && sent.challenge) drawTextC(ctx, linkMsg || (coarse() ? "TAP: CHALLENGE A FRIEND" : "C: CHALLENGE A FRIEND"), 80, 127, linkMsg === "LINK COPIED" ? PAL[3] : PAL[2]);
  else if (!online && runMode === "tt" && !pilotName && sent.state === "unranked") drawTextC(ctx, "GET A LICENCE TO RANK", 80, 127, PAL[2]);
}
let seasonScored = false;

function drawStandings() {
  if (!online && soloTop && podiumActive()) return void drawPodium(ctx, tick - podiumAt, soloTop.title, soloTop.top);
  const last = round + 1 >= SEASON.length;
  drawTextC(ctx, (last ? "FINAL STANDINGS " : `STANDINGS R${round + 1}/${SEASON.length} `) + SKILLS[raceSkill].name.slice(0, 4), 80, 4, PAL[3]);
  const rows = seasonPts.map((p, id) => ({ id, p: p ?? 0 })).sort((a, b) => b.p - a.p).slice(0, 10);
  rows.forEach((r, i) => {
    const me = r.id === mySlot;
    drawText(ctx, `${String(i + 1).padStart(2)} ${me ? "YOU" : "CPU" + r.id}`, 10, 14 + i * 10 - 2, me ? PAL[3] : PAL[2]);
    drawText(ctx, `${r.p} PTS`, 100, 14 + i * 10 - 2, PAL[3]);
  });
  drawTextC(ctx, last ? "PRESS START" : "NEXT: " + TRACKS[SEASON[round + 1]].name, 80, 134, PAL[2]);
}

// ---- macro warning: rapid jet taps at one fixed rhythm (an auto-fire key or a script). Only a message; the server's review tool sees the same thing in the recorded inputs ----
const MACRO_GAPS = 30; // consecutive rapid taps looked at
let macroRace: Race | null = null, macroPrev = false, macroLast = -1, macroGaps: number[] = [], macroUntil = 0, macroShown = -99999;
function watchMacro(r: Race, jet: boolean) {
  if (!macroWarnOn) return;
  if (macroRace !== r) (macroRace = r), (macroPrev = false), (macroLast = -1), (macroGaps = []);
  if (r.phase !== "racing") return;
  if (jet && !macroPrev) {
    if (macroLast >= 0 && r.frame - macroLast <= 30) macroGaps.push(r.frame - macroLast);
    else macroGaps = [];
    macroLast = r.frame;
    if (macroGaps.length > MACRO_GAPS) macroGaps.shift();
    if (macroGaps.length === MACRO_GAPS && tick - macroShown > 60 * 20) {
      const sorted = [...macroGaps].sort((a, b) => a - b);
      const med = sorted[MACRO_GAPS >> 1];
      if (macroGaps.filter((g) => Math.abs(g - med) <= 1).length >= MACRO_GAPS * 0.9) (macroUntil = tick + 60 * 5), (macroShown = tick);
    }
  }
  macroPrev = jet;
}
function drawMacroWarning() {
  if (tick >= macroUntil) return;
  if (Math.floor(tick / 15) % 2 === 0) {
    ctx.fillStyle = PAL[3];
    ctx.fillRect(14, 22, 132, 20);
    drawTextC(ctx, "MACRO DETECTED", 80, 25, PAL[0]);
    drawTextC(ctx, "FAIR PLAY PLEASE", 80, 33, PAL[1]);
  }
}
function stepRace() {
  const r = race!;
  const me = r.cars[mySlot];
  const inputs = Array(r.cars.length).fill(NO_INPUT);
  inputs[mySlot] = readInput();
  if (!online && runMode === "weekly" && weeklyInfo?.nojet) inputs[mySlot] = { ...inputs[mySlot], jet: false }; // a no-turbo week: the server would refuse the run anyway
  if (!online && sent.state === "idle") (inputLog.push(maskOf(inputs[mySlot])), r.phase === "racing" && sampleSource(), watchMacro(r, inputs[mySlot].jet));
  const before = r.phase;
  const bump0 = me.bump, wasWall = me.inWall, laps0 = me.lapFrames.length, fin0 = me.finished;
  r.step(inputs);
  if (ghost) {
    ghost.race.step([inputOf(ghost.masks[ghost.idx++] ?? 0)]);
    ghost.posAt.push(ghost.race.cars[0].pos);
  }
  if (!online && me.finished && sent.state === "idle") submitRun();
  if (me.inWall && !wasWall) (sfx.crash(), rumble(220, 0.8, 0.5));
  else if (me.bump > bump0 && bump0 === 0) rumble(110, 0.45, 0.4); // contact with another car
  if (me.lapFrames.length > laps0 && !me.finished) sfx.jingle();
  if (me.finished && !fin0) sfx.jingle(true);
  if (online) {
    // remote humans: ease towards last received state; their car physics are not simulated locally
    const now = performance.now();
    for (const [slot, t] of targets) {
      const car = r.cars[slot];
      const age = (now - t.at) / 1000;
      const predicted = t.pos + 5 * Math.floor(t.speed / 16) * 16 * (age * 59.7275 / 5.7158);
      car.pos += (predicted - car.pos) * 0.25;
      car.speed = t.speed;
      car.lat += (t.lat - car.lat) * 0.25;
      car.jetOn = t.jet;
    }
    if ((r.phase === "racing" || r.phase === "finished") && tick - lastSend >= 3) { // keep reporting after the local race ends: the final "fin" state is what lets the server close the race
      lastSend = tick;
      net?.send({ t: "state", f: r.raceFrame, pos: me.pos, speed: me.speed, lat: me.lat, jet: me.jetOn, fin: me.finished ? me.finishFrame || 1 : 0 });
    }
  }
  if (r.phase === "countdown") {
    const left = r.goFrame - r.frame;
    if (left % 60 === 0 && left > 0 && left < 200) sfx.beep(440);
  } else if (before === "countdown") sfx.beep(880, 0.4);
  const cam: Car = me;
  const names = online ? new Map(players.map((p, i) => [i, p.name] as [number, string])) : undefined;
  setAccents();
  renderRace(ctx, { race: r, me: cam, mirror: mirrorOn, names, ghost: ghost?.race.cars[0], ghostGap: r.phase === "racing" ? ghostGap(me) : undefined }, tick);
  if (!online) drawMacroWarning();
  if (online) drawNetStatus(SCREEN_W - 26, 3, true);
  if (online && net && !net.open) { // dropped: the race carries on, we are knocking again
    ctx.fillStyle = PAL[3];
    ctx.fillRect(34, 56, 92, 11);
    drawTextC(ctx, "RECONNECTING...", 80, 59, PAL[0]);
  }
  if (r.phase === "countdown") (renderIntroBanner(ctx, r), renderCountdown(ctx, r));
  else renderIntroBanner(ctx, r); // slides away as the race starts
  if (me.finished) drawTextC(ctx, "FINISH!", 80, 52, PAL[3], 3);
  if (!online && r.phase === "finished") setTimeout(() => {
    screen = "result";
    soloTop = null;
    if (menuIdx === 0 && runMode === "season" && !testDef && race) startSeasonRacePodium();
  }, 1500);
  if (online && r.phase === "finished") {
    // wait for server result
  }
}

// fixed 59.7275 Hz timestep
let acc = 0;
let last = performance.now();
function loop(now: number) {
  // a connection that went quiet without closing (phone asleep, Wi-Fi gone) is dropped after 7 s so the reconnection starts
  if (online && net && net.open && !net.closing && net.silentMs > 7000 && (screen === "race" || RECONNECT_SCREENS.includes(screen))) net.drop();
  pollGamepad(screen === "race" && !!race, now);
  pollLobby(now);
  acc += Math.min(100, now - last);
  last = now;
  const dt = 1000 / 59.7275;
  let n = 0;
  while (acc >= dt && n < 4) {
    frame();
    acc -= dt;
    n++;
  }
  if (n > 0) applyPalette(ctx, SCREEN_W, SCREEN_H); // recolour only a freshly drawn frame (the recolouring is not repeatable)
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
void STATE_HZ;
if (testDef && !surfaceTest) (runMode = "quick"), startSolo(0); // opened from the editor: straight onto the circuit
else if (testVClass) {
  // vehicle test link: wait for the circuits and sprites, then straight onto the chosen circuit
  void loadTracks().then(() => {
    const tr = Number(testParams.get("track"));
    machine = testParams.get("machine") === "1" ? 1 : 0;
    runMode = "quick";
    startSolo(testDef || !TRACKS[tr] ? 0 : tr);
  });
}
void checkPilot();
if (reviewMode) {
  screen = "review";
  document.body.classList.add("stream");
  addEventListener("keydown", reviewKey);
  void startReview();
}
if (streamMode) {
  document.body.classList.add("stream");
  if (pilotKey || streamToken) void goOnline();
  else netErr = "NEED A PILOT";
}
// the one-click link of the sign-in email (/?le=EMAIL&lc=CODE): this device joins the licence by itself
const urlLe = new URLSearchParams(location.search).get("le") ?? "";
const urlLc = new URLSearchParams(location.search).get("lc") ?? "";
const urlEi = (new URLSearchParams(location.search).get("ei") ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
async function magicSignIn() {
  try {
    history.replaceState(null, "", "/");
  } catch {
    /* ignore */
  }
  if (pilotName && pilotKey) return void (urlEi ? loadTeamInvite(urlEi) : undefined); // this device already has a licence: only the invitation matters
  const key = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
  try {
    const d = await postJson("/api/pilot/login/verify", { key, email: cleanMail(urlLe), code: urlLc });
    if (d.ok) {
      pilotKey = key;
      writeStore("f1key", key);
      setPilot(d.name);
      pilotEmail = d.email ?? "";
      if (urlEi) writeStore("f1invite", urlEi);
      afterSignIn();
      return;
    }
  } catch {
    /* offline */
  }
  if (urlEi) return void loadTeamInvite(urlEi); // the invitation screen offers the sign-in
  startClaim();
  nameMsg = "LINK EXPIRED";
  if (emailOn) setNameMode("mail");
}
if (urlLe && urlLc) void magicSignIn();
else if (urlShared) void loadShared(urlShared);
else if (urlTeam) void loadTeamInvite(urlTeam);
if (urlChallenge) void loadChallenge(urlChallenge); // asks the server whether this browser's licence is known (and opens the name screen when it is not)
